import {
    CheckCircleOutlined,
    CloseCircleOutlined,
    DeleteOutlined,
    EditOutlined,
    IdcardOutlined,
    InfoCircleOutlined,
    ReloadOutlined
} from '@ant-design/icons';
import {
    Button,
    Card,
    Descriptions,
    Dropdown,
    message,
    Modal,
    Table,
    Tag
} from 'antd';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import PortBulkEditPanel from '../components/port-bulk-edit-panel/PortBulkEditPanel';
import PortCreatePanel from '../components/port-create-panel';
import PortEditPanel from '../components/port-edit-panel';
import PortInfoPanel from '../components/port-info-panel';
import ProxyStatsPanel from '../components/proxy-stats-panel/ProxyStatsPanel';
import SyncStatsModal from '../components/SyncStatsModal';
import CustomPagination from '../components/custom-pagination/CustomPagination';
import CheckResultBanner from '../components/check-result-banner/CheckResultBanner';
import { useBulkUpdate } from '../context/BulkUpdateContext';
import { apiService } from '../services/api';
import { formatApiErrorDetail } from '../utils/formatApiErrorDetail';
import { buildForSaleStats } from '../utils/for-sale-stats';
import { computeModemStatsFromConnections, mergeModemStatsForPanel } from '../utils/modem-stats-from-ports';
import {
    buildModemRebootStateMap,
    formatRebootCompletionBanner,
    isRebootCooldownActive,
    isSupportedModemRebootModel,
    isUnavailableModemStatus,
    resolveModemRebootModelName,
    translateModemRebootReason,
} from '../utils/modemRebootUtils';
import logger from '../utils/logger';
import { createSpaLinkClickHandler } from '../utils/spaLinkUtils';
import '../styles/network-ports.css';
import '../components/proxy-stats-panel/proxy-stats-panel.css';
import '../components/proxy-sandbox-panel/proxy-sandbox-panel.css';
import '../components/port-info-panel/port-info-panel.css';

/** Нормализация osfp значения в число | null */
const normalizeOsfpValue = (raw) => {
    if (raw === null || raw === undefined || raw === '') return null;
    if (typeof raw === 'object' && raw !== null) return raw.id || null;
    if (typeof raw === 'string') { const n = parseInt(raw, 10); return isNaN(n) ? null : n; }
    return typeof raw === 'number' ? raw : null;
};

const escapeRegex = (value) => String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const NP_IDENTITY_EMPTY_FILTER = '__EMPTY__';
const NP_BANDS_EMPTY_FILTER = '__EMPTY_BANDS__';
const LTE_BAND_ORDER = ['B1', 'B3', 'B7', 'B20'];

const normalizeAllowedLteBands = (raw) => {
    if (!Array.isArray(raw)) return [];
    const set = new Set(
        raw
            .map((band) => String(band || '').trim().toUpperCase())
            .filter(Boolean)
    );
    const known = LTE_BAND_ORDER.filter((band) => set.has(band));
    const unknown = [...set].filter((band) => !LTE_BAND_ORDER.includes(band)).sort();
    return [...known, ...unknown];
};

const formatAllowedLteBandsKey = (raw) => {
    const bands = normalizeAllowedLteBands(raw);
    return bands.length ? bands.join('+') : NP_BANDS_EMPTY_FILTER;
};

const getPortProxyCounts = (port) => {
    const total = Number(port?.modem_total_proxies ?? port?.total_used ?? 0);
    const available = Number(port?.modem_available_proxies ?? 0);
    const unavailable = Math.max(total - available, 0);
    return { total, available, unavailable };
};

const extractModemsStatusList = (modemsStatusResponse) => {
    if (Array.isArray(modemsStatusResponse?.data?.data?.modems)) {
        return modemsStatusResponse.data.data.modems;
    }
    if (Array.isArray(modemsStatusResponse?.data?.modems)) {
        return modemsStatusResponse.data.modems;
    }
    if (Array.isArray(modemsStatusResponse?.modems)) {
        return modemsStatusResponse.modems;
    }
    return [];
};

const buildLoadStatsIndex = (statsData) => {
    const byConnPort = new Map();
    const byPort = new Map();
    (Array.isArray(statsData) ? statsData : []).forEach((stat) => {
        if (stat?.port_id == null) return;
        const pid = String(stat.port_id);
        if (stat.connection_id != null) {
            byConnPort.set(`${stat.connection_id}:${pid}`, stat);
        }
        if (!byPort.has(pid)) {
            byPort.set(pid, stat);
        }
    });
    return { byConnPort, byPort };
};

const lookupLoadStat = (port, index) => {
    const candidates = [
        port?.id != null && port?.connection_id != null
            ? `${port.connection_id}:${port.id}`
            : null,
        port?.kraken_id != null && port?.connection_id != null
            ? `${port.connection_id}:${port.kraken_id}`
            : null,
    ];
    for (const key of candidates) {
        if (key && index.byConnPort.has(key)) {
            return index.byConnPort.get(key);
        }
    }
    const ids = [port?.id, port?.kraken_id, port?.server_id];
    for (const id of ids) {
        if (id == null) continue;
        const hit = index.byPort.get(String(id));
        if (hit) return hit;
    }
    return null;
};

const buildModemStatusIndex = (modemsStatusData) => {
    const byId = new Map();
    const byNameConn = new Map();
    (Array.isArray(modemsStatusData) ? modemsStatusData : []).forEach((modem) => {
        if (modem?.modem_id != null) {
            byId.set(String(modem.modem_id), modem);
        }
        if (modem?.modem_name && modem?.connection_name) {
            byNameConn.set(`${modem.connection_name}::${modem.modem_name}`, modem);
        }
    });
    return { byId, byNameConn };
};

const lookupModemStatus = (port, index) => {
    const ids = [port?.server_id, port?.kraken_id, port?.kraken_modem_id];
    for (const id of ids) {
        if (id == null) continue;
        const hit = index.byId.get(String(id));
        if (hit) return hit;
    }
    if (port?.name && port?.connection_name) {
        return index.byNameConn.get(`${port.connection_name}::${port.name}`) || null;
    }
    return null;
};

const NP_BACKGROUND_REFRESH_MS = 20000;
const NP_STICKY_RADIO_FIELDS = ['signal', 'conn_inet', 'conn_dev', 'conn_net', 'rsrp', 'rsrq', 'sinr', 'rssi'];

const portRadioStickyKey = (port) => {
    if (port?.server_id != null) return `sid:${port.server_id}`;
    if (port?.connection_id != null && (port?.kraken_modem_id != null || port?.kraken_id != null)) {
        return `${port.connection_id}:${port.kraken_modem_id ?? port.kraken_id}`;
    }
    return port?.id != null ? `id:${port.id}` : null;
};

const isMissingRadioValue = (value) => value === null || value === undefined || value === '';

const mergeStickyRadioFields = (nextPorts, prevPorts) => {
    if (!Array.isArray(nextPorts) || nextPorts.length === 0) return nextPorts;
    if (!Array.isArray(prevPorts) || prevPorts.length === 0) return nextPorts;
    const prevByKey = new Map();
    prevPorts.forEach((port) => {
        const key = portRadioStickyKey(port);
        if (key) prevByKey.set(key, port);
    });
    return nextPorts.map((port) => {
        const prev = prevByKey.get(portRadioStickyKey(port));
        if (!prev) return port;
        let changed = false;
        const merged = { ...port };
        NP_STICKY_RADIO_FIELDS.forEach((field) => {
            if (isMissingRadioValue(port[field]) && !isMissingRadioValue(prev[field])) {
                merged[field] = prev[field];
                changed = true;
            }
        });
        return changed ? merged : port;
    });
};

const isPortModemUnavailable = (port) => {
    if (!port || port.modem_offline === true) {
        return false;
    }
    const status = port.modem_status || 'unknown';
    return status === 'not_working' || status === 'unavailable';
};

const isPortModemWorking = (port) => {
    if (!port || port.modem_offline === true) {
        return false;
    }
    return port.modem_status === 'working';
};

const isPortWithUnavailableProxies = (port) => {
    if (!port || port.modem_offline === true) {
        return false;
    }
    return getPortProxyCounts(port).unavailable > 0;
};

const isPortWithAvailableProxies = (port) => {
    if (!port || port.modem_offline === true) {
        return false;
    }
    return getPortProxyCounts(port).available > 0;
};

const isEmptyPortIdentityField = (value) => {
    const trimmed = String(value ?? '').trim();
    return trimmed === '' || trimmed === '—' || trimmed === '-';
};

const buildIdentityFieldFilterStats = (ports, field) => {
    const valueCounts = {};
    let emptyCount = 0;
    ports.forEach((port) => {
        const raw = port?.[field];
        if (isEmptyPortIdentityField(raw)) {
            emptyCount += 1;
            return;
        }
        const key = String(raw).trim();
        valueCounts[key] = (valueCounts[key] || 0) + 1;
    });
    const values = Object.entries(valueCounts)
        .map(([value, count]) => ({ value, label: value, count }))
        .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label, 'ru'));
    return { emptyCount, values };
};

const matchIdentityFieldFilter = (field, filterValue, record) => {
    const raw = record?.[field];
    if (filterValue === NP_IDENTITY_EMPTY_FILTER) {
        return isEmptyPortIdentityField(raw);
    }
    return String(raw ?? '').trim() === filterValue;
};

const isUnknownOperator = (value) => {
    const normalized = String(value || '').trim().toLowerCase();
    return normalized === '' || ['unknown', 'none', 'null', 'n/a'].includes(normalized);
};

const normalizeOperatorTemplates = (rawTemplates) => {
    if (!Array.isArray(rawTemplates)) return [];
    return rawTemplates
        .filter((item) => item && typeof item === 'object')
        .map((item) => ({
            name: String(item.name || '').trim(),
            patterns: Array.isArray(item.patterns)
                ? item.patterns.map((pattern) => String(pattern || '').trim()).filter(Boolean)
                : [],
        }))
        .filter((item) => item.name);
};

const resolveOperatorByTemplates = (rawOperator, templates) => {
    const value = String(rawOperator || '').trim();
    if (isUnknownOperator(value)) return 'UNKNOWN';
    const preparedTemplates = normalizeOperatorTemplates(templates);
    if (preparedTemplates.length === 0) return value;

    for (const template of preparedTemplates) {
        for (const pattern of template.patterns) {
            try {
                const regex = new RegExp(`^${escapeRegex(pattern).replace(/\\\*/g, '.*')}$`, 'i');
                if (regex.test(value)) return template.name;
            } catch {
                continue;
            }
        }
    }
    return value;
};

const PORT_OVERRIDE_TTL_MS = 30000;
const REBOOT_TASK_POLL_INTERVAL_MS = 3000;
// Web API reboot (~10с) + ожидание web UI (~90с); bulk идёт параллельно — дедлайн не умножаем на N.
const REBOOT_TASK_MAX_WAIT_MS = 120000;
const REBOOT_TASK_PER_MODEM_BUDGET_MS = 120000;

const waitForMs = (ms) => new Promise((resolve) => {
    window.setTimeout(resolve, ms);
});

const NpFilterIcon = ({ filtered }) => (
    <svg
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        style={{ color: filtered ? '#5283f4' : '#7a7a7a' }}
    >
        <path
            d="M9.00005 14C8.89187 14 8.7866 13.9649 8.70005 13.9L6.70005 12.4C6.63795 12.3534 6.58755 12.293 6.55284 12.2236C6.51812 12.1542 6.50005 12.0776 6.50005 12V9.19L2.99205 5.2435C2.74291 4.96244 2.58024 4.61541 2.52359 4.24412C2.46694 3.87283 2.51872 3.49308 2.67271 3.15051C2.8267 2.80794 3.07635 2.51713 3.39164 2.31303C3.70694 2.10894 4.07446 2.00024 4.45005 2H11.5501C11.9256 2.00044 12.293 2.10932 12.6082 2.31355C12.9234 2.51779 13.1729 2.80869 13.3267 3.15129C13.4805 3.4939 13.5321 3.87364 13.4753 4.24487C13.4185 4.61611 13.2558 4.96305 13.0066 5.244L9.50005 9.19V13.5C9.50005 13.6326 9.44737 13.7598 9.3536 13.8536C9.25984 13.9473 9.13266 14 9.00005 14ZM7.50005 11.75L8.50005 12.5V9C8.50015 8.87758 8.54516 8.75945 8.62655 8.668L12.2606 4.5795C12.3817 4.44254 12.4608 4.27352 12.4883 4.09273C12.5157 3.91193 12.4905 3.72705 12.4154 3.56028C12.3404 3.39351 12.2189 3.25193 12.0654 3.15254C11.9118 3.05316 11.7329 3.00019 11.5501 3H4.45005C4.26728 3.00028 4.08846 3.05327 3.93505 3.15263C3.78164 3.25199 3.66015 3.3935 3.58516 3.56018C3.51016 3.72685 3.48483 3.91163 3.51221 4.09234C3.5396 4.27305 3.61854 4.44203 3.73955 4.579L7.37405 8.668C7.45526 8.75952 7.50009 8.87764 7.50005 9V11.75Z"
            fill="currentColor"
            stroke="currentColor"
            strokeWidth="0.6"
        />
    </svg>
);

const NpBugIcon = () => (
    <svg className="np-bug-icon__svg" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g clipPath="url(#np-bug-clip)">
            <path d="M11 3.5C11.2652 3.5 11.5196 3.39464 11.7071 3.20711C11.8946 3.01957 12 2.76522 12 2.5V0.5C12 0.367392 11.9473 0.240215 11.8536 0.146447C11.7598 0.0526784 11.6326 0 11.5 0C11.3674 0 11.2402 0.0526784 11.1464 0.146447C11.0527 0.240215 11 0.367392 11 0.5V2.5H9C8.991 2.5 8.984 2.5045 8.975 2.505C8.73585 1.9125 8.32509 1.40499 7.79545 1.04761C7.2658 0.690226 6.64144 0.499281 6.0025 0.499281C5.36356 0.499281 4.7392 0.690226 4.20955 1.04761C3.67991 1.40499 3.26915 1.9125 3.03 2.505C3.0195 2.5055 3.0105 2.5 3 2.5H1V0.5C1 0.367392 0.947322 0.240215 0.853553 0.146447C0.759785 0.0526784 0.632608 0 0.5 0C0.367392 0 0.240215 0.0526784 0.146447 0.146447C0.0526784 0.240215 0 0.367392 0 0.5L0 2.5C0 2.76522 0.105357 3.01957 0.292893 3.20711C0.48043 3.39464 0.734784 3.5 1 3.5H2.7425L2.236 5.5H0.5C0.367392 5.5 0.240215 5.55268 0.146447 5.64645C0.0526784 5.74021 0 5.86739 0 6C0 6.13261 0.0526784 6.25979 0.146447 6.35355C0.240215 6.44732 0.367392 6.5 0.5 6.5H1.9825L1.91 6.7865L1.9 6.85C1.87771 7.02111 1.86602 7.19344 1.865 7.366C1.86551 7.74969 1.91987 8.13142 2.0265 8.5H1C0.734784 8.5 0.48043 8.60536 0.292893 8.79289C0.105357 8.98043 0 9.23478 0 9.5L0 11.5C0 11.6326 0.0526784 11.7598 0.146447 11.8536C0.240215 11.9473 0.367392 12 0.5 12C0.632608 12 0.759785 11.9473 0.853553 11.8536C0.947322 11.7598 1 11.6326 1 11.5V9.5H2.465C2.83155 10.1101 3.34976 10.6149 3.96921 10.9654C4.58866 11.3159 5.28827 11.5001 6 11.5001C6.71173 11.5001 7.41134 11.3159 8.03079 10.9654C8.65024 10.6149 9.16845 10.1101 9.535 9.5H11V11.5C11 11.6326 11.0527 11.7598 11.1464 11.8536C11.2402 11.9473 11.3674 12 11.5 12C11.6326 12 11.7598 11.9473 11.8536 11.8536C11.9473 11.7598 12 11.6326 12 11.5V9.5C12 9.23478 11.8946 8.98043 11.7071 8.79289C11.5196 8.60536 11.2652 8.5 11 8.5H9.9735C10.0807 8.13066 10.1354 7.74808 10.136 7.3635C10.1345 7.19176 10.1225 7.02027 10.1 6.85L10.0125 6.5H11.5C11.6326 6.5 11.7598 6.44732 11.8536 6.35355C11.9473 6.25979 12 6.13261 12 6C12 5.86739 11.9473 5.74021 11.8536 5.64645C11.7598 5.55268 11.6326 5.5 11.5 5.5H9.761L9.2565 3.5H11ZM6 10.5C5.16848 10.4991 4.37129 10.1683 3.78336 9.58029C3.19543 8.99227 2.86479 8.19502 2.864 7.3635C2.86469 7.24198 2.87253 7.1206 2.8875 7L3.9025 2.985C4.05483 2.55255 4.33709 2.17775 4.71065 1.91191C5.0842 1.64606 5.53079 1.50217 5.98928 1.49992C6.44777 1.49768 6.89575 1.63719 7.27189 1.89937C7.64803 2.16154 7.93394 2.53356 8.0905 2.9645L9.1125 7C9.12743 7.12027 9.13527 7.24131 9.136 7.3625C9.13547 8.19419 8.80495 8.9917 8.21699 9.57993C7.62904 10.1682 6.83169 10.4991 6 10.5Z" fill="currentColor"/>
        </g>
        <defs>
            <clipPath id="np-bug-clip">
                <rect width="12" height="12" fill="white"/>
            </clipPath>
        </defs>
    </svg>
);

/** Проверка IMEI — barcode + галочка (как в Sim Manager) */
const NpImeiCheckIcon = () => (
    <svg viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <path d="M1 1.5C1 1.22386 1.22386 1 1.5 1H2.5V2H2V10H2.5V11H1.5C1.22386 11 1 10.7761 1 10.5V1.5Z" fill="currentColor"/>
        <path d="M10.5 1C10.7761 1 11 1.22386 11 1.5V10.5C11 10.7761 10.7761 11 10.5 11H9.5V10H10V2H9.5V1H10.5Z" fill="currentColor"/>
        <rect x="3.25" y="2.5" width="1" height="7" rx="0.25" fill="currentColor"/>
        <rect x="5" y="2.5" width="0.75" height="7" rx="0.2" fill="currentColor"/>
        <rect x="6.5" y="2.5" width="1.25" height="4" rx="0.25" fill="currentColor"/>
        <path d="M7.25 8.1L8.15 9L10.35 6.8L9.65 6.1L8.15 7.6L7.95 7.4L7.25 8.1Z" fill="currentColor"/>
    </svg>
);

const NpFilterDropdown = ({
    title,
    filters,
    selectedKeys,
    setSelectedKeys,
    confirm,
    clearFilters,
    emptyOption = null,
}) => {
    const [search, setSearch] = useState('');
    const items = filters || [];
    const filtered = search
        ? items.filter((f) => String(f.text).toLowerCase().includes(search.toLowerCase()))
        : items;

    const handleToggle = (val) => {
        const next = selectedKeys.includes(val)
            ? selectedKeys.filter((k) => k !== val)
            : [...selectedKeys, val];
        setSelectedKeys(next);
    };

    const handleReset = () => {
        setSelectedKeys([]);
        clearFilters && clearFilters();
        confirm({ closeDropdown: true });
    };

    const handleApply = () => {
        confirm({ closeDropdown: true });
    };

    return (
        <div className="np-filter-dropdown">
            <div className="np-filter-dropdown__header">Фильтр: {title}</div>
            {emptyOption && (
                <div className="np-filter-dropdown__empty-option">
                    <label className="np-filter-dropdown__item">
                        <input
                            type="checkbox"
                            className="np-filter-dropdown__checkbox"
                            checked={selectedKeys.includes(emptyOption.value)}
                            onChange={() => handleToggle(emptyOption.value)}
                        />
                        <span className="np-filter-dropdown__label">{emptyOption.text}</span>
                    </label>
                </div>
            )}
            <div className="np-filter-dropdown__search">
                <input
                    className="np-filter-dropdown__search-input"
                    placeholder="Поиск..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    autoFocus
                />
            </div>
            <div className="np-filter-dropdown__list">
                {filtered.length === 0 && (
                    <div className="np-filter-dropdown__empty">Ничего не найдено</div>
                )}
                {filtered.map((f) => (
                    <label key={String(f.value)} className="np-filter-dropdown__item">
                        <input
                            type="checkbox"
                            className="np-filter-dropdown__checkbox"
                            checked={selectedKeys.includes(f.value)}
                            onChange={() => handleToggle(f.value)}
                        />
                        <span className="np-filter-dropdown__label">{f.text}</span>
                    </label>
                ))}
            </div>
            <div className="np-filter-dropdown__actions">
                <button
                    type="button"
                    className="np-filter-dropdown__btn np-filter-dropdown__btn--reset"
                    onClick={handleReset}
                >
                    Сбросить
                </button>
                <button
                    type="button"
                    className="np-filter-dropdown__btn np-filter-dropdown__btn--apply"
                    onClick={handleApply}
                >
                    Применить
                </button>
            </div>
        </div>
    );
};

const NpFilterOffIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M3 4H15.5L9 13V19L7 17V13L3 7.58V4Z" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M17 8L22 13" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
        <path d="M22 8L17 13" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
    </svg>
);

const NetworkPorts = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const modemServerIdFromUrl = useMemo(() => {
        const v = searchParams.get('modem_server_id');
        return v != null && String(v).trim() !== '' ? String(v).trim() : '';
    }, [searchParams]);
    const modemDisplayFromUrl = useMemo(() => {
        const v = searchParams.get('modem');
        return v != null && String(v).trim() !== '' ? String(v).trim() : '';
    }, [searchParams]);
    const serverNameFromUrl = useMemo(() => {
        const v = searchParams.get('server');
        return v != null && String(v).trim() !== '' ? String(v).trim() : '';
    }, [searchParams]);
    const {
        bulkUpdateInProgress,
        completedBulkProgress,
        clearCompletedBulkProgress,
        registerBulkUpdateTask,
        startBulkUpdate,
        stopBulkUpdate,
        showBulkLoadingMessage,
    } = useBulkUpdate();
    const [ports, setPorts] = useState([]);
    const [operatorTemplates, setOperatorTemplates] = useState([]);
    const [krakenConnections, setKrakenConnections] = useState([]);
    const [krakenConnectionsForStats, setKrakenConnectionsForStats] = useState([]);
    const operatorTemplatesRef = useRef([]);
    const lastNpStatsRef = useRef(null);
    /** Все порты (включая offline) — для deep link из Sim Manager */
    const allPortsForDeepLinkRef = useRef([]);
    const lastPortsRadioRef = useRef([]);
    
    // Безопасный геттер для ports, который всегда возвращает массив
    const safePorts = useMemo(() => Array.isArray(ports) ? ports : [], [ports]);

    const hasCalculatedNpStats = useCallback((stats) => {
        if (!stats || typeof stats !== 'object' || Array.isArray(stats)) {
            return false;
        }

        return [
            stats.total_modems,
            stats.available_modems,
            stats.unavailable_modems,
            stats.total_proxies,
            stats.available_proxies,
        ].some((value) => Number(value || 0) > 0);
    }, []);

    const npStats = useMemo(() => {
        const statsPorts = safePorts.filter((p) => p.modem_offline !== true);
        const modemStatsForPanel = mergeModemStatsForPanel(krakenConnectionsForStats, statsPorts);
        const total = modemStatsForPanel?.total_modems ?? statsPorts.length;
        const active = statsPorts.filter((p) => p.active).length;
        const inactive = total - active;
        const working = modemStatsForPanel?.available_modems
            ?? statsPorts.filter((p) => p.modem_status === 'working').length;
        const notWorking = modemStatsForPanel?.unavailable_modems
            ?? statsPorts.filter((p) => p.modem_status === 'not_working' || p.modem_status === 'unavailable').length;
        const unknown = modemStatsForPanel?.unknown_modems
            ?? statsPorts.filter((p) => !p.modem_status || p.modem_status === 'unknown').length;
        const totalProxies = statsPorts.reduce((sum, p) => sum + Number(p.modem_total_proxies ?? p.total_used ?? 0), 0);
        const availableProxies = statsPorts.reduce((sum, p) => sum + Number(p.modem_available_proxies ?? 0), 0);

        const forSaleStats = buildForSaleStats({
            ports: statsPorts,
            connections: krakenConnectionsForStats,
            operatorTemplates,
            resolveOperatorByTemplates,
            isPortEligible: () => true,
        });

        const nextStats = {
            total_modems: total,
            active_modems: active,
            inactive_modems: inactive,
            available_modems: working,
            unavailable_modems: notWorking,
            unknown_modems: unknown,
            total_proxies: totalProxies,
            available_proxies: availableProxies,
            unavailable_proxies: Math.max(totalProxies - availableProxies, 0),
            for_sale_operators: forSaleStats.for_sale_operators,
            for_sale_operators_by_city: forSaleStats.for_sale_operators_by_city,
            for_sale_total: forSaleStats.for_sale_total,
        };

        if (hasCalculatedNpStats(nextStats)) {
            lastNpStatsRef.current = nextStats;
            return nextStats;
        }

        return lastNpStatsRef.current || nextStats;
    }, [safePorts, operatorTemplates, krakenConnectionsForStats, hasCalculatedNpStats]);

    const [loading, setLoading] = useState(false);
    const [, setLoadingProgress] = useState(0);
    const [, setLoadingMessage] = useState('');

    const [syncing, setSyncing] = useState(false);
    const [manualRefreshing, setManualRefreshing] = useState(false);
    const [exportingPorts, setExportingPorts] = useState(false);
    const [checkingModemIds, setCheckingModemIds] = useState(() => new Set());
    const [rebootingModemIds, setRebootingModemIds] = useState(() => new Set());
    const [refreshingIdentityModemIds, setRefreshingIdentityModemIds] = useState(() => new Set());
    const [modemRebootStateById, setModemRebootStateById] = useState({});
    const [npStatsVisible, setNpStatsVisible] = useState(false);
    const [npFilterPanelVisible, setNpFilterPanelVisible] = useState(false);
    const [npLocalFilters, setNpLocalFilters] = useState({});
    const [npOpenDropdown, setNpOpenDropdown] = useState(null);
    const [npDropdownSearch, setNpDropdownSearch] = useState({});
    const npDropdownRefs = useRef({});
    const npSearchInputRefs = useRef({});
    const backgroundRefreshInFlightRef = useRef(false);
    const fetchRequestSeqRef = useRef(0);
    const latestAppliedFetchRef = useRef(0);
    const initialLoadStartedRef = useRef(false);
    const hasAppliedPortsRef = useRef(false);
    const krakenConnectionsCacheRef = useRef(null);
    const krakenConnectionsInFlightRef = useRef(null);
    const pendingPortOverridesRef = useRef(new Map());
    const portsImportFileInputRef = useRef(null);

    const buildPortKey = useCallback((port) => {
        if (!port || typeof port !== 'object') return null;
        if (port.server_id !== undefined && port.server_id !== null) {
            return `server:${String(port.server_id)}`;
        }
        if (port.id !== undefined && port.id !== null && port.connection_id !== undefined && port.connection_id !== null) {
            return `port:${String(port.id)}:connection:${String(port.connection_id)}`;
        }
        if (port.id !== undefined && port.id !== null) {
            return `port:${String(port.id)}`;
        }
        return null;
    }, []);

    const getModemCheckId = useCallback((port) => {
        if (!port || typeof port !== 'object') return null;
        const modemId = port.server_id ?? port.id;
        if (modemId === undefined || modemId === null) return null;
        return String(modemId);
    }, []);

    const setModemRebootingState = useCallback((modemIds, isRebooting) => {
        setRebootingModemIds((prev) => {
            const next = new Set(prev);
            modemIds.forEach((modemId) => {
                if (!modemId) return;
                if (isRebooting) {
                    next.add(String(modemId));
                } else {
                    next.delete(String(modemId));
                }
            });
            return next;
        });
    }, []);

    const modemRebootIdsKey = useMemo(() => {
        const modemIds = Array.from(
            new Set(
                safePorts
                    .map((row) => Number(getModemCheckId(row)))
                    .filter((value) => Number.isFinite(value) && value > 0)
            )
        ).sort((a, b) => a - b);
        return modemIds.join(',');
    }, [getModemCheckId, safePorts]);

    const fetchModemRebootStatuses = useCallback(async (portRows = safePorts) => {
        const modemIds = Array.from(
            new Set(
                (Array.isArray(portRows) ? portRows : [])
                    .map((row) => Number(getModemCheckId(row)))
                    .filter((value) => Number.isFinite(value) && value > 0)
            )
        );
        if (modemIds.length === 0) {
            setModemRebootStateById({});
            return;
        }
        try {
            const response = await apiService.getModemRebootStatus({ modem_ids: modemIds });
            setModemRebootStateById(buildModemRebootStateMap(response?.data?.data || response?.data || {}));
        } catch (error) {
            logger.warn('Не удалось загрузить состояния reboot модемов:', error);
        }
    }, [getModemCheckId, safePorts]);

    const lastModemRebootFetchRef = useRef({ key: '', at: 0 });
    useEffect(() => {
        if (!modemRebootIdsKey) {
            setModemRebootStateById({});
            return undefined;
        }
        const now = Date.now();
        const prev = lastModemRebootFetchRef.current;
        // Не дёргать /reboot-status на каждый refresh ports: тот же набор id — не чаще 20с.
        if (prev.key === modemRebootIdsKey && now - prev.at < 20000) {
            return undefined;
        }
        lastModemRebootFetchRef.current = { key: modemRebootIdsKey, at: now };
        void fetchModemRebootStatuses(safePorts);
        return undefined;
    }, [fetchModemRebootStatuses, modemRebootIdsKey, safePorts]);

    const getModemRebootState = useCallback((port) => {
        const modemId = getModemCheckId(port);
        return modemId ? (modemRebootStateById[String(modemId)] || null) : null;
    }, [getModemCheckId, modemRebootStateById]);

    const getRebootDisabledReason = useCallback((port, options = {}) => {
        const { strict = false } = options;
        const modemId = getModemCheckId(port);
        if (!modemId) {
            return 'Невозможно определить модем.';
        }
        if (port?.active === false || port?.modem_offline === true) {
            return 'Модем отключён в Kraken.';
        }
        const modelName = resolveModemRebootModelName(port);
        if (!isSupportedModemRebootModel(modelName)) {
            return 'Модель модема не поддерживает перезагрузку.';
        }
        if (rebootingModemIds.has(String(modemId))) {
            return 'Перезагрузка уже запущена.';
        }
        const rebootState = getModemRebootState(port);
        if (rebootState?.last_result === 'in_progress') {
            return 'Перезагрузка уже выполняется.';
        }
        if (!strict) {
            return null;
        }
        if (!isUnavailableModemStatus(port?.modem_status)) {
            return 'Перезагрузка доступна только для недоступных модемов.';
        }
        if (isRebootCooldownActive(rebootState)) {
            return 'Для модема активен cooldown 1 час.';
        }
        return null;
    }, [getModemCheckId, getModemRebootState, rebootingModemIds]);

    const buildRebootCompletionSummary = useCallback((modemRows, modemIds, statusPayload, queuedPayload = {}, timedOut = false) => {
        const normalizedIds = (Array.isArray(modemIds) ? modemIds : [])
            .map((value) => String(value))
            .filter(Boolean);
        const modemNamesById = new Map();

        (Array.isArray(modemRows) ? modemRows : []).forEach((row) => {
            const modemId = getModemCheckId(row);
            if (!modemId) return;
            modemNamesById.set(String(modemId), row?.name || `#${modemId}`);
        });

        const accepted = Array.isArray(queuedPayload?.accepted) ? queuedPayload.accepted : [];
        accepted.forEach((item) => {
            const modemId = item?.modem_id != null ? String(item.modem_id) : '';
            if (!modemId || modemNamesById.has(modemId)) return;
            modemNamesById.set(modemId, item?.modem_name || `#${modemId}`);
        });

        const statesMap = buildModemRebootStateMap(statusPayload || {});
        const taskResults = Array.isArray(statusPayload?.task?.results) ? statusPayload.task.results : [];
        const taskResultsById = new Map();

        taskResults.forEach((item) => {
            const modemId = item?.modem_id != null ? String(item.modem_id) : '';
            if (!modemId) return;
            taskResultsById.set(modemId, item);
        });

        const results = normalizedIds.map((modemId) => {
            const taskResult = taskResultsById.get(modemId);
            const state = statesMap[modemId] || null;
            const status = taskResult?.status || state?.last_result || 'unknown';
            const error = taskResult?.error || state?.last_error || null;
            return {
                modemId,
                modemName: modemNamesById.get(modemId) || `#${modemId}`,
                status,
                error,
            };
        });

        const skipped = Array.isArray(queuedPayload?.skipped) ? queuedPayload.skipped : [];
        const successCount = results.filter((item) => item.status === 'success').length;
        const warningCount = results.filter((item) => item.status === 'warning').length;
        const errorCount = results.filter((item) => item.status === 'error').length;
        const inProgressCount = results.filter((item) => item.status === 'in_progress').length;

        return {
            timedOut,
            isSingle: results.length === 1,
            results,
            skipped,
            total: results.length,
            successCount,
            warningCount,
            errorCount,
            inProgressCount,
            skippedCount: skipped.length,
        };
    }, [getModemCheckId]);

    const waitForRebootCompletion = useCallback(async ({
        modemRows,
        modemIds,
        taskId,
        queuedPayload,
        onProgress,
    }) => {
        const normalizedModemIds = (Array.isArray(modemIds) ? modemIds : [])
            .map((value) => Number(value))
            .filter((value) => Number.isFinite(value) && value > 0);
        const acceptedCount = Array.isArray(queuedPayload?.accepted) && queuedPayload.accepted.length
            ? queuedPayload.accepted.length
            : normalizedModemIds.length;
        const maxWaitMs = acceptedCount > 1
            ? REBOOT_TASK_MAX_WAIT_MS
            : Math.max(REBOOT_TASK_MAX_WAIT_MS, REBOOT_TASK_PER_MODEM_BUDGET_MS);
        const startedAt = Date.now();
        let lastPayload = null;

        const emitProgress = (payload, timedOut = false) => {
            const partialSummary = buildRebootCompletionSummary(
                modemRows,
                normalizedModemIds,
                payload,
                queuedPayload,
                timedOut,
            );
            onProgress?.(partialSummary, timedOut ? 'completed' : 'progress');
            return partialSummary;
        };

        onProgress?.(
            buildRebootCompletionSummary(modemRows, normalizedModemIds, null, queuedPayload, false),
            'progress',
        );

        while (Date.now() - startedAt < maxWaitMs) {
            const response = await apiService.getModemRebootStatus({
                modem_ids: normalizedModemIds,
                task_id: taskId,
            });
            const payload = response?.data?.data || response?.data || {};
            lastPayload = payload;

            const stateMap = buildModemRebootStateMap(payload);
            setModemRebootStateById((prev) => ({
                ...prev,
                ...stateMap,
            }));

            const task = payload?.task || null;
            const processed = Number(task?.processed || 0);
            const total = Number(task?.total || acceptedCount) || acceptedCount;

            const relevantStates = normalizedModemIds
                .map((modemId) => stateMap[modemId] || null)
                .filter(Boolean);
            const taskCompleted = task?.status === 'completed';
            const statesCompleted = !task && relevantStates.length > 0
                && relevantStates.every((item) => item?.last_result && item.last_result !== 'in_progress');

            emitProgress(payload, false);

            if (taskCompleted || statesCompleted) {
                return buildRebootCompletionSummary(modemRows, normalizedModemIds, payload, queuedPayload, false);
            }

            if (processed > 0 || total > 1) {
                onProgress?.(
                    {
                        ...buildRebootCompletionSummary(modemRows, normalizedModemIds, payload, queuedPayload, false),
                        processed,
                        total,
                    },
                    'progress',
                );
            }

            await waitForMs(REBOOT_TASK_POLL_INTERVAL_MS);
        }

        return buildRebootCompletionSummary(modemRows, normalizedModemIds, lastPayload, queuedPayload, true);
    }, [buildRebootCompletionSummary]);

    const setModemCheckingState = useCallback((modemIds, isChecking) => {
        setCheckingModemIds((prev) => {
            const next = new Set(prev);
            modemIds.forEach((modemId) => {
                if (!modemId) return;
                if (isChecking) {
                    next.add(modemId);
                } else {
                    next.delete(modemId);
                }
            });
            return next;
        });
    }, []);

    const queuePortOverride = useCallback((port, patch = {}) => {
        const key = buildPortKey(port);
        if (!key) return;

        const sanitizedPatch = { ...patch };
        delete sanitizedPatch.id;
        delete sanitizedPatch.server_id;
        delete sanitizedPatch.connection_id;

        if (Object.keys(sanitizedPatch).length === 0) return;

        pendingPortOverridesRef.current.set(key, {
            patch: sanitizedPatch,
            expiresAt: Date.now() + PORT_OVERRIDE_TTL_MS,
        });
    }, [buildPortKey]);

    const doesPortSatisfyOverride = useCallback((port, patch = {}) => {
        if (!patch || typeof patch !== 'object') return true;
        return Object.entries(patch).every(([field, expectedValue]) => {
            const actualValue = port?.[field];

            if (field === 'osfp') {
                return normalizeOsfpValue(actualValue) === normalizeOsfpValue(expectedValue);
            }

            if (typeof expectedValue === 'boolean') {
                return Boolean(actualValue) === expectedValue;
            }

            if (expectedValue === null || expectedValue === undefined) {
                return actualValue === expectedValue;
            }

            return String(actualValue) === String(expectedValue);
        });
    }, []);

    const resolvePortWithPendingOverride = useCallback((port) => {
        const key = buildPortKey(port);
        if (!key) return port;

        const overrideEntry = pendingPortOverridesRef.current.get(key);
        if (!overrideEntry) return port;

        if (Date.now() > overrideEntry.expiresAt) {
            pendingPortOverridesRef.current.delete(key);
            return port;
        }

        if (doesPortSatisfyOverride(port, overrideEntry.patch)) {
            pendingPortOverridesRef.current.delete(key);
            return port;
        }

        return {
            ...port,
            ...overrideEntry.patch,
        };
    }, [buildPortKey, doesPortSatisfyOverride]);

    useEffect(() => {
        if (npFilterPanelVisible) {
            setNpLocalFilters({ ...tableFilters });
            setNpOpenDropdown(null);
            setNpDropdownSearch({});
        }
    }, [npFilterPanelVisible]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!npOpenDropdown) return;
        const handleClickOutside = (e) => {
            const ref = npDropdownRefs.current[npOpenDropdown];
            if (ref && !ref.contains(e.target)) setNpOpenDropdown(null);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [npOpenDropdown]);

    useEffect(() => {
        if (!npFilterPanelVisible) return;
        const handleKey = (e) => {
            if (e.key === 'Escape') setNpFilterPanelVisible(false);
        };
        document.addEventListener('keydown', handleKey);
        return () => document.removeEventListener('keydown', handleKey);
    }, [npFilterPanelVisible]);

    useEffect(() => {
        if (!npFilterPanelVisible) return;
        if (typeof window === 'undefined' || typeof document === 'undefined') return;
        if (window.innerWidth > 768) return;

        const scrollY = window.scrollY || window.pageYOffset || 0;
        const previousBodyStyles = {
            position: document.body.style.position,
            top: document.body.style.top,
            left: document.body.style.left,
            right: document.body.style.right,
            width: document.body.style.width,
            overflow: document.body.style.overflow,
        };

        document.body.style.position = 'fixed';
        document.body.style.top = `-${scrollY}px`;
        document.body.style.left = '0';
        document.body.style.right = '0';
        document.body.style.width = '100%';
        document.body.style.overflow = 'hidden';

        return () => {
            document.body.style.position = previousBodyStyles.position;
            document.body.style.top = previousBodyStyles.top;
            document.body.style.left = previousBodyStyles.left;
            document.body.style.right = previousBodyStyles.right;
            document.body.style.width = previousBodyStyles.width;
            document.body.style.overflow = previousBodyStyles.overflow;
            window.scrollTo(0, scrollY);
        };
    }, [npFilterPanelVisible]);

    const npToggleFilterChip = useCallback((field, value) => {
        setNpLocalFilters(prev => {
            const current = prev[field] || [];
            const next = current.includes(value)
                ? current.filter(v => v !== value)
                : [...current, value];
            if (next.length === 0) { const copy = { ...prev }; delete copy[field]; return copy; }
            return { ...prev, [field]: next };
        });
    }, []);

    const npDropdownToggle = useCallback((field, value) => {
        setNpLocalFilters(prev => {
            const current = prev[field] || [];
            const next = current.includes(value)
                ? current.filter(v => v !== value)
                : [...current, value];
            if (next.length === 0) { const copy = { ...prev }; delete copy[field]; return copy; }
            return { ...prev, [field]: next };
        });
    }, []);

    const npDropdownClear = useCallback((field) => {
        setNpLocalFilters(prev => { const copy = { ...prev }; delete copy[field]; return copy; });
    }, []);

    const handleNpFilterApply = useCallback(() => {
        setTableFilters(npLocalFilters);
        setNpCurrentPage(1);
        setNpFilterPanelVisible(false);
    }, [npLocalFilters]);

    const handleNpFilterReset = useCallback(() => {
        setNpLocalFilters({});
        setTableFilters({});
        setNpCurrentPage(1);
        setNpFilterPanelVisible(false);
    }, []);

    const npFilterStatusStats = useMemo(() => {
        let working = 0, notWorking = 0, offline = 0, unknown = 0;
        safePorts.forEach(p => {
            const isOffline = p.modem_offline === true;
            const ms = p.modem_status || 'unknown';
            if (isOffline) offline++;
            else if (ms === 'working') working++;
            else if (ms === 'not_working') notWorking++;
            else unknown++;
        });
        return [
            { value: 'Рабочий', filterValue: 'working', count: working, color: 'green' },
            { value: 'Недоступен', filterValue: 'not_working', count: notWorking, color: 'red' },
            { value: 'Выключен', filterValue: 'offline', count: offline, color: 'gray' },
            { value: 'Неизвестно', filterValue: 'unknown', count: unknown, color: 'gray' },
        ];
    }, [safePorts]);

    const npFilterCityStats = useMemo(() => {
        const map = {};
        safePorts.forEach(p => { const c = p.city || 'Не определен'; map[c] = (map[c] || 0) + 1; });
        return Object.entries(map).map(([value, count]) => ({ value, count })).sort((a, b) => b.count - a.count);
    }, [safePorts]);

    const npFilterOperatorStats = useMemo(() => {
        const map = {};
        safePorts.forEach((p) => {
            const canonicalOperator = resolveOperatorByTemplates(p.operator, operatorTemplates);
            const displayOperator = canonicalOperator === 'UNKNOWN' ? 'Неизвестно' : canonicalOperator;
            map[displayOperator] = (map[displayOperator] || 0) + 1;
        });
        return Object.entries(map)
            .map(([value, count]) => ({ value, count }))
            .sort((a, b) => b.count - a.count);
    }, [safePorts, operatorTemplates]);

    const npFilterRotationStats = useMemo(() => {
        const ROTATION_LABELS = { time: 'Время', link: 'Ссылка', time_link: 'Время + ссылка' };
        const map = {};
        safePorts.forEach(p => {
            const rt = p.reconnect_type || 'time_link';
            map[rt] = (map[rt] || 0) + 1;
        });
        return Object.entries(map).map(([value, count]) => ({
            value,
            label: ROTATION_LABELS[value] || value,
            count,
        })).sort((a, b) => b.count - a.count);
    }, [safePorts]);

    const npFilterOsfpStats = useMemo(() => {
        const map = {};
        safePorts.forEach(p => {
            const label = p.is_osfp ? (p.osfp_name || 'Android (Linux:2.6.x)') : 'Выключен';
            map[label] = (map[label] || 0) + 1;
        });
        return Object.entries(map).map(([value, count]) => ({ value, label: value, count })).sort((a, b) => b.count - a.count);
    }, [safePorts]);

    const npFilterImeiStats = useMemo(
        () => buildIdentityFieldFilterStats(safePorts, 'imei'),
        [safePorts],
    );

    const npFilterMacStats = useMemo(
        () => buildIdentityFieldFilterStats(safePorts, 'mac_address'),
        [safePorts],
    );
    
    // Состояние для мобильной версии
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Функция для извлечения третьего октета из IP адреса
    const getModemNumberFromIP = useCallback((ip) => {
        if (!ip) return null;
        const parts = ip.split('.');
        if (parts.length === 4) {
            return parts[2]; // Третий октет (индекс 2)
        }
        return null;
    }, []);

    const getModemNumberFromDisplay = useCallback((value) => {
        const match = String(value || '').trim().match(/^№\s*(\d+)/);
        return match ? match[1] : null;
    }, []);

    const getPortModemNumber = useCallback((port) => {
        if (!port || typeof port !== 'object') return null;
        // Учётный № только из modem_internal_ip; без fallback на ip_address.
        return getModemNumberFromIP(port.modem_internal_ip);
    }, [getModemNumberFromIP]);

    const findPortForModemDeepLink = useCallback((ports, serverIdFromUrl, serverName) => {
        const sid = String(serverIdFromUrl || '').trim();
        if (!sid || !Array.isArray(ports) || ports.length === 0) {
            return null;
        }

        const matchesServer = (port) => {
            if (!serverName) {
                return true;
            }
            const connectionName = port.connection_name || port.server_name || '';
            return connectionName === serverName;
        };

        const byServerId = ports.find(
            (port) => matchesServer(port) && String(port.server_id) === sid,
        );
        if (byServerId) {
            return byServerId;
        }

        const byKrakenModemId = ports.find(
            (port) => matchesServer(port) && String(port.kraken_modem_id) === sid,
        );
        if (byKrakenModemId) {
            return byKrakenModemId;
        }

        return ports.find(
            (port) => matchesServer(port) && String(getPortModemNumber(port)) === sid,
        ) || null;
    }, [getPortModemNumber]);

    const getPortModemDisplayName = useCallback((port) => {
        const ipNumber = getPortModemNumber(port);
        return (ipNumber ? `№ ${ipNumber} ${port?.name || ''}` : (port?.name || 'unknown')).trim();
    }, [getPortModemNumber]);

    const isPortMatchingModemFilter = useCallback((value, record) => {
        const filterValue = String(value || '').trim();
        if (!filterValue) return true;

        const displayName = getPortModemDisplayName(record);
        if (displayName === filterValue) return true;

        const filterModemNumber = getModemNumberFromDisplay(filterValue);
        const recordModemNumber = getPortModemNumber(record);
        return Boolean(filterModemNumber && recordModemNumber && filterModemNumber === String(recordModemNumber));
    }, [getModemNumberFromDisplay, getPortModemDisplayName, getPortModemNumber]);
    
    // Состояния для фильтрации и сортировки
    const [filteredPorts, setFilteredPorts] = useState([]);
    const [serverNameFilter] = useState([]);
    const [portNameFilter] = useState([]);
    const [modemTypeFilter] = useState([]);
    const initFilters = useMemo(() => {
        const f = {};
        const status = searchParams.get('status');
        if (status) f.active = status.split(',');
        const server = searchParams.get('server');
        if (server && String(server).trim()) {
            f.connection_name = [String(server).trim()];
        }
        const md = searchParams.get('modem');
        if (md && String(md).trim()) {
            f.name = [String(md).trim()];
        }
        return f;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const [tableFilters, setTableFilters] = useState(initFilters);
    const activeFilterCount = useMemo(() =>
        Object.values(tableFilters).filter((v) => Array.isArray(v) && v.length > 0).length,
    [tableFilters]);

    const npQuickStats = useMemo(() => {
        const totalPorts = safePorts.length;
        const availablePorts = safePorts.filter((p) => p.active === true).length;
        const unavailablePorts = Math.max(totalPorts - availablePorts, 0);

        const modemAvailable = safePorts.filter(isPortModemWorking).length;
        const modemUnavailable = safePorts.filter(isPortModemUnavailable).length;

        const totalProxies = safePorts.reduce((sum, p) => sum + getPortProxyCounts(p).total, 0);
        const availableProxies = safePorts.reduce((sum, p) => sum + getPortProxyCounts(p).available, 0);
        const unavailableProxies = Math.max(totalProxies - availableProxies, 0);
        const portsWithAvailableProxies = safePorts.filter(isPortWithAvailableProxies).length;
        const portsWithUnavailableProxies = safePorts.filter(isPortWithUnavailableProxies).length;

        const cityMap = {};
        safePorts.forEach((p) => {
            const city = String(
                p.city ||
                p.connection_city ||
                p.server_city ||
                p.geo ||
                'Не определен'
            ).trim() || 'Не определен';
            cityMap[city] = (cityMap[city] || 0) + 1;
        });
        const cities = Object.entries(cityMap)
            .map(([name, count]) => ({ name, count }))
            .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));

        return {
            totalPorts,
            availablePorts,
            unavailablePorts,
            totalProxies,
            availableProxies,
            unavailableProxies,
            portsWithAvailableProxies,
            portsWithUnavailableProxies,
            modemAvailable,
            modemUnavailable,
            cities,
        };
    }, [safePorts]);

    const handleQuickFilter = useCallback((field, value) => {
        setTableFilters((prev) => {
            const next = { ...prev };
            if (value === null || value === undefined || value === '') {
                delete next[field];
                return next;
            }

            const current = Array.isArray(prev[field]) ? prev[field] : [];
            if (current.length === 1 && current[0] === value) {
                delete next[field];
                return next;
            }

            next[field] = [value];
            if (field === '__quick_modem_status') {
                delete next.__quick_proxy_state;
            } else if (field === '__quick_proxy_state') {
                delete next.__quick_modem_status;
            }
            return next;
        });
        setNpCurrentPage(1);
    }, []);
    const handleClearAllFilters = useCallback(() => {
        setTableFilters({});
        setNpCurrentPage(1);
    }, []);

    const handleCheckDuplicateImei = useCallback(() => {
        const isActive = Array.isArray(tableFilters.__imei_dup_check)
            && tableFilters.__imei_dup_check.length > 0;

        if (isActive) {
            setTableFilters((prev) => {
                const next = { ...prev };
                delete next.__imei_dup_check;
                return next;
            });
            setNpCurrentPage(1);
            message.info('Фильтр дублей IMEI сброшен');
            return;
        }

        const counts = {};
        safePorts.forEach((port) => {
            if (isEmptyPortIdentityField(port.imei)) {
                return;
            }
            const norm = String(port.imei).trim().toLowerCase();
            counts[norm] = (counts[norm] || 0) + 1;
        });

        const dupNorms = Object.entries(counts)
            .filter(([, count]) => count >= 2)
            .map(([norm]) => norm);

        if (dupNorms.length === 0) {
            message.success('Дублирующихся IMEI не найдено');
            return;
        }

        const dupSet = new Set(dupNorms);
        const modemCount = safePorts.filter((port) => {
            if (isEmptyPortIdentityField(port.imei)) {
                return false;
            }
            return dupSet.has(String(port.imei).trim().toLowerCase());
        }).length;

        setTableFilters((prev) => ({
            ...prev,
            __imei_dup_check: dupNorms,
        }));
        setNpCurrentPage(1);
        message.warning(
            `Дубли IMEI: ${dupNorms.length} значений, ${modemCount} модемов`
        );
    }, [safePorts, tableFilters.__imei_dup_check]);

    useEffect(() => {
        if (searchParams.has('status')) {
            setSearchParams({}, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const [createModalVisible, setCreateModalVisible] = useState(false);
    const [createSubmitting, setCreateSubmitting] = useState(false);
    const [editSubmitting, setEditSubmitting] = useState(false);
    const [editModalVisible, setEditModalVisible] = useState(false);
    const [detailsModalVisible, setDetailsModalVisible] = useState(false);
    const [bulkEditModalVisible, setBulkEditModalVisible] = useState(false);
    const [selectedPort, setSelectedPort] = useState(null);
    const [infoPortKey, setInfoPortKey] = useState(null);
    const lastInfoPortRef = useRef(null);
    useEffect(() => {
        if (!infoPortKey) return undefined;
        const handleKey = (e) => {
            if (e.key === 'Escape') setInfoPortKey(null);
        };
        document.addEventListener('keydown', handleKey);
        return () => document.removeEventListener('keydown', handleKey);
    }, [infoPortKey]);

    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [selectedRows, setSelectedRows] = useState([]);
    const lastSelectedPortKeyRef = useRef(null);
    const npUrlModemAppliedRef = useRef({ sid: '', disp: '' });
    const [modemTypes, setModemTypes] = useState([]);
    const [serverUsers, setServerUsers] = useState([]);
    const [tcpFingerprints, setTcpFingerprints] = useState([]);
    const [loadingTcpFingerprints, setLoadingTcpFingerprints] = useState(false);
    const [syncStats, setSyncStats] = useState(null);
    const [syncStatsVisible, setSyncStatsVisible] = useState(false);
    const [bulkUpdateStats, setBulkUpdateStats] = useState(null);
    const [bulkUpdateStatsVisible, setBulkUpdateStatsVisible] = useState(false);
    const [loadingConnections, setLoadingConnections] = useState(false);
    const [networkInterfaces, setNetworkInterfaces] = useState([]);
    const [loadingInterfaces, setLoadingInterfaces] = useState(false);
    const [tcpFingerprintsForCreate, setTcpFingerprintsForCreate] = useState([]);
    const [loadingTcpFingerprintsForCreate, setLoadingTcpFingerprintsForCreate] = useState(false);
    const [, setInitialFormValues] = useState(null);
    const [npCurrentPage, setNpCurrentPage] = useState(1);
    const [npPerPage, setNpPerPage] = useState(50);
    const [npFilteredTotal, setNpFilteredTotal] = useState(0);
    const [proxyCheckSummary, setProxyCheckSummary] = useState(null);
    const npPerPageOptions = [10, 20, 50, 100];
    const npPagination = useMemo(() => ({
        current: npCurrentPage,
        pageSize: npPerPage,
        showSizeChanger: false,
        className: 'np-pagination-hidden',
    }), [npCurrentPage, npPerPage]);

    const injectDeepLinkPortIntoVisibleList = useCallback((port) => {
        if (!port || port.server_id == null) {
            return;
        }
        // Выключенные в Kraken / offline не показываем и не подмешиваем в таблицу.
        if (port.modem_offline === true) {
            return;
        }
        const sid = String(port.server_id);
        const alreadyVisible = safePorts.some((p) => String(p.server_id) === sid);
        if (alreadyVisible) {
            return;
        }
        setPorts((prev) => {
            if (prev.some((p) => String(p.server_id) === sid)) {
                return prev;
            }
            return [...prev, port];
        });
        setFilteredPorts((prev) => {
            if (prev.some((p) => String(p.server_id) === sid)) {
                return prev;
            }
            return [...prev, port];
        });
    }, [safePorts]);

    const findPortByModemDisplayDeepLink = useCallback((ports, displayName, serverName) => {
        const display = String(displayName || '').trim();
        if (!display || !Array.isArray(ports) || ports.length === 0) {
            return null;
        }
        return ports.find((port) => {
            if (serverName) {
                const connectionName = port.connection_name || port.server_name || '';
                if (connectionName !== serverName) {
                    return false;
                }
            }
            return isPortMatchingModemFilter(display, port);
        }) || null;
    }, [isPortMatchingModemFilter]);

    useEffect(() => {
        if (!modemServerIdFromUrl && !modemDisplayFromUrl && !serverNameFromUrl) {
            npUrlModemAppliedRef.current = { sid: '', disp: '' };
            return;
        }
        const deepLinkPorts = allPortsForDeepLinkRef.current.length > 0
            ? allPortsForDeepLinkRef.current
            : safePorts;
        if (modemServerIdFromUrl) {
            if (!deepLinkPorts.length) {
                return;
            }
            const port = findPortForModemDeepLink(
                deepLinkPorts,
                modemServerIdFromUrl,
                serverNameFromUrl,
            );
            if (port) {
                // Всегда подмешиваем: после refetch offline снова выпадает из safePorts.
                injectDeepLinkPortIntoVisibleList(port);
                const appliedKey = `${modemServerIdFromUrl}:${serverNameFromUrl}`;
                if (npUrlModemAppliedRef.current.sid === appliedKey) {
                    return;
                }
                const displayName = getPortModemDisplayName(port);
                const connectionName = port.connection_name || port.server_name || '';
                setTableFilters((prev) => ({
                    ...prev,
                    name: [displayName],
                    ...(connectionName ? { connection_name: [connectionName] } : {}),
                }));
                setNpCurrentPage(1);
                npUrlModemAppliedRef.current = { sid: appliedKey, disp: '' };
                return;
            }
            if (modemDisplayFromUrl) {
                const byDisplay = findPortByModemDisplayDeepLink(
                    deepLinkPorts,
                    modemDisplayFromUrl,
                    serverNameFromUrl,
                );
                const appliedKey = `modem:${modemDisplayFromUrl}:${serverNameFromUrl}`;
                if (npUrlModemAppliedRef.current.sid !== appliedKey) {
                    if (byDisplay) {
                        injectDeepLinkPortIntoVisibleList(byDisplay);
                    }
                    setTableFilters((prev) => ({
                        ...prev,
                        name: [modemDisplayFromUrl],
                        ...(serverNameFromUrl ? { connection_name: [serverNameFromUrl] } : {}),
                    }));
                    setNpCurrentPage(1);
                    npUrlModemAppliedRef.current = { sid: appliedKey, disp: '' };
                }
                return;
            }
            const notFoundKey = `!${modemServerIdFromUrl}:${serverNameFromUrl}`;
            if (npUrlModemAppliedRef.current.sid !== notFoundKey) {
                message.warning(
                    serverNameFromUrl
                        ? `Модем (ID ${modemServerIdFromUrl}) на сервере «${serverNameFromUrl}» не найден`
                        : `Модем (ID ${modemServerIdFromUrl}) не найден среди портов`,
                );
                npUrlModemAppliedRef.current.sid = notFoundKey;
            }
            return;
        }
        if (modemDisplayFromUrl || serverNameFromUrl) {
            const appliedKey = `${modemDisplayFromUrl}:${serverNameFromUrl}`;
            if (npUrlModemAppliedRef.current.disp === appliedKey) {
                return;
            }
            if (modemDisplayFromUrl && deepLinkPorts.length > 0) {
                const byDisplay = findPortByModemDisplayDeepLink(
                    deepLinkPorts,
                    modemDisplayFromUrl,
                    serverNameFromUrl,
                );
                if (byDisplay) {
                    injectDeepLinkPortIntoVisibleList(byDisplay);
                }
            }
            setTableFilters((prev) => ({
                ...prev,
                ...(modemDisplayFromUrl ? { name: [modemDisplayFromUrl] } : {}),
                ...(serverNameFromUrl ? { connection_name: [serverNameFromUrl] } : {}),
            }));
            setNpCurrentPage(1);
            npUrlModemAppliedRef.current = { sid: '', disp: appliedKey };
        }
    }, [
        modemServerIdFromUrl,
        modemDisplayFromUrl,
        serverNameFromUrl,
        safePorts,
        getPortModemDisplayName,
        findPortForModemDeepLink,
        findPortByModemDisplayDeepLink,
        injectDeepLinkPortIntoVisibleList,
    ]);

    // Загрузка данных портов
    
    // Загрузка данных портов с прогрессом (skipLoadingOverlay — не показывать прогресс, например после массового обновления)
    const fetchPorts = useCallback(async (
        skipLoadingOverlay = false,
        applyOptimisticOverrides = true,
        forceRefresh = false,
    ) => {
        const currentFetchRequestId = ++fetchRequestSeqRef.current;
        try {
            logger.info('🚀 NetworkPorts: Начало загрузки данных портов');
            if (!skipLoadingOverlay) {
                setLoading(true);
                setLoadingProgress(0);
                setLoadingMessage('Инициализация...');
                setLoadingProgress(40);
                setLoadingMessage('Загрузка данных портов...');
            }
            
            logger.info('📡 NetworkPorts: Загрузка списка портов...');
            const portsResponse = await apiService.getAllKrakenModems({ refresh: forceRefresh });
            const loadStatsResponse = { data: [] };
            const modemsStatusResponse = { success: true, data: { modems: [] } };
            
            if (!skipLoadingOverlay) {
                setLoadingProgress(70);
                setLoadingMessage('Обработка данных...');
            }
            
            const portsData = portsResponse?.data?.ports || portsResponse?.ports;
            const statsData = Array.isArray(loadStatsResponse?.data)
                ? loadStatsResponse.data
                : (Array.isArray(loadStatsResponse?.data?.stats)
                    ? loadStatsResponse.data.stats
                    : []);
            const modemsStatusData = extractModemsStatusList(modemsStatusResponse);
            const loadStatsIndex = buildLoadStatsIndex(statsData);
            const modemStatusIndex = buildModemStatusIndex(modemsStatusData);
            
            if (latestAppliedFetchRef.current > currentFetchRequestId) {
                logger.debug('NetworkPorts: пропускаем устаревший ответ fetchPorts', {
                    currentFetchRequestId,
                    latestAppliedRequestId: latestAppliedFetchRef.current,
                });
                return;
            }

            latestAppliedFetchRef.current = currentFetchRequestId;

            if (portsResponse && portsData && Array.isArray(portsData)) {
                logger.info(`📋 NetworkPorts: Обработка ${portsData.length} портов`);

                const portsWithStatsAll = portsData.map((rawPort) => {
                    const port = applyOptimisticOverrides
                        ? resolvePortWithPendingOverride(rawPort)
                        : rawPort;
                    const stats = lookupLoadStat(port, loadStatsIndex);
                    const modemStatus = lookupModemStatus(port, modemStatusIndex);
                    
                    const loadStats = stats || {
                        current_connections: 0,
                        max_connections: port.max_tcp_connections || 1000,
                        connection_usage_percent: port.overall_load_percentage || 0,
                        estimated_clients: port.total_used || 0,
                        load_status: 'low',
                        load_speed_percentage: 0,
                        load_tcp_percentage: 0,
                        total_allocated_speed: 0,
                        total_tcp_connections: 0
                    };
                    
                    // ИСПРАВЛЕНО: Приоритет - сначала данные из port (из бэкенда), потом из loadStats
                    // Это гарантирует, что данные из /api/kraken/ports/all используются правильно
                    const loadSpeedPercentage = port.load_speed_percentage !== undefined && port.load_speed_percentage !== null
                        ? port.load_speed_percentage
                        : (loadStats.load_speed_percentage !== undefined && loadStats.load_speed_percentage !== null
                            ? loadStats.load_speed_percentage
                            : (port.total_guaranteed_speed && port.capacity_speed_mbps 
                                ? (port.total_guaranteed_speed / port.capacity_speed_mbps * 100) 
                                : 0));
                    // ИСПРАВЛЕНО: Приоритет - сначала данные из port (из бэкенда), потом из loadStats
                    // Важно: проверяем на undefined/null, но не на 0, так как 0 - это валидное значение
                    const loadTcpPercentage = (port.load_tcp_percentage !== undefined && port.load_tcp_percentage !== null)
                        ? port.load_tcp_percentage
                        : ((loadStats.load_tcp_percentage !== undefined && loadStats.load_tcp_percentage !== null)
                            ? loadStats.load_tcp_percentage
                            : 0);
                    const totalGuaranteedSpeed = port.total_guaranteed_speed !== undefined && port.total_guaranteed_speed !== null
                        ? port.total_guaranteed_speed
                        : (port.total_allocated_speed !== undefined && port.total_allocated_speed !== null
                            ? port.total_allocated_speed
                            : (loadStats.total_allocated_speed !== undefined && loadStats.total_allocated_speed !== null
                                ? loadStats.total_allocated_speed
                                : 0));
                    // ИСПРАВЛЕНО: Приоритет - сначала данные из port (из бэкенда), потом из loadStats
                    const estimatedTcpConnections = (port.estimated_tcp_connections !== undefined && port.estimated_tcp_connections !== null)
                        ? port.estimated_tcp_connections
                        : ((loadStats.total_tcp_connections !== undefined && loadStats.total_tcp_connections !== null)
                            ? loadStats.total_tcp_connections
                            : ((loadStats.current_connections !== undefined && loadStats.current_connections !== null)
                                ? loadStats.current_connections
                                : 0));
                    
                    return {
                        ...port,
                        load_stats: loadStats,
                        modem_status: port.modem_status || modemStatus?.status || 'unknown',
                        modem_status_details: port.modem_status_details || modemStatus?.details || {},
                        modem_available_proxies: port.modem_available_proxies ?? modemStatus?.available_proxies ?? 0,
                        modem_total_proxies: port.modem_total_proxies ?? modemStatus?.total_proxies ?? 0,
                        // ИСПРАВЛЕНО: Используем правильные поля для количества прокси и общей загрузки
                        total_used: port.total_used || loadStats.estimated_clients || 0,
                        overall_load_percentage: port.overall_load_percentage || port.utilization || loadStats.connection_usage_percent || 0,
                        // ✅ ИСПРАВЛЕНО: Копируем данные о нагрузке по скорости и TCP из port (приоритет) или loadStats
                        // Важно: используем вычисленные значения, которые уже имеют правильный приоритет
                        load_speed_percentage: loadSpeedPercentage,
                        load_tcp_percentage: loadTcpPercentage,
                        total_guaranteed_speed: totalGuaranteedSpeed,
                        total_allocated_speed: totalGuaranteedSpeed,
                        estimated_tcp_connections: estimatedTcpConnections,
                        capacity_speed_mbps: port.capacity_speed_mbps || 20,
                        // ✅ ДОБАВЛЕНО: Явно копируем данные из port, если они есть (для гарантии)
                        // Это гарантирует, что данные из бэкенда не потеряются
                        ...(port.load_speed_percentage !== undefined && port.load_speed_percentage !== null ? { load_speed_percentage: port.load_speed_percentage } : {}),
                        ...(port.load_tcp_percentage !== undefined && port.load_tcp_percentage !== null ? { load_tcp_percentage: port.load_tcp_percentage } : {}),
                        ...(port.estimated_tcp_connections !== undefined && port.estimated_tcp_connections !== null ? { estimated_tcp_connections: port.estimated_tcp_connections } : {}),
                        ...(port.total_guaranteed_speed !== undefined && port.total_guaranteed_speed !== null ? { total_guaranteed_speed: port.total_guaranteed_speed } : {})
                    };
                });

                const stickyBase = lastPortsRadioRef.current;
                const mergedAll = mergeStickyRadioFields(portsWithStatsAll, stickyBase);
                allPortsForDeepLinkRef.current = mergedAll;
                lastPortsRadioRef.current = mergedAll;

                const portsWithStats = mergedAll.filter((port) => {
                    const connectionIsDisabled =
                        port?.connection_active === false ||
                        port?.connection_enabled === false ||
                        String(port?.connection_status || '').toLowerCase() === 'disconnected';
                    const modemIsOffline = port?.modem_offline === true;
                    return !connectionIsDisabled && !modemIsOffline;
                });
                
                hasAppliedPortsRef.current = true;
                setPorts(portsWithStats);
                setFilteredPorts(portsWithStats);
            } else {
                allPortsForDeepLinkRef.current = [];
                setPorts([]);
                setFilteredPorts([]);
            }
            
            if (!skipLoadingOverlay) {
                setLoadingProgress(100);
                setLoadingMessage('');
            }
            
        } catch (error) {
            // Улучшенная обработка ошибок с проверкой типа
            let errorMessage = 'Неизвестная ошибка';
            let errorDetails = null;
            
            if (error instanceof Error) {
                errorMessage = error.message || error.toString();
                errorDetails = {
                    message: error.message,
                    stack: error.stack,
                    response: error.response ? {
                        status: error.response.status,
                        statusText: error.response.statusText,
                        data: error.response.data
                    } : null,
                    request: error.request ? 'Request made but no response received' : null
                };
            } else if (typeof error === 'string') {
                errorMessage = error;
            } else if (error && typeof error === 'object') {
                errorMessage = error.message || error.detail || JSON.stringify(error);
                errorDetails = error;
            } else {
                errorMessage = String(error);
            }
            
            logger.error('Error fetching network ports:', errorMessage);
            if (errorDetails) {
                logger.error('Error details:', errorDetails);
            }
            
            if (!skipLoadingOverlay) {
                setLoadingProgress(0);
                setLoadingMessage('');
            }
            
            // Определяем тип ошибки и показываем соответствующее сообщение
            if (error?.response?.status === 500) {
                message.error('Ошибка сервера при загрузке портов. Попробуйте обновить страницу.');
            } else if (error?.response?.status === 401) {
                message.error('Ошибка авторизации. Войдите в систему заново.');
            } else if (error?.response?.status === 403) {
                message.error('Доступ запрещен. Проверьте права доступа.');
            } else if (error?.response?.status === 404) {
                message.error('Ресурс не найден. Проверьте подключение к серверу.');
            } else if (error?.code === 'ECONNABORTED' || error?.message?.includes('timeout')) {
                message.error('Превышено время ожидания ответа сервера. Попробуйте позже.');
            } else if (error?.response?.data?.detail) {
                message.error(error.response.data.detail);
            } else if (errorMessage && errorMessage !== 'Неизвестная ошибка') {
                message.error(`Ошибка загрузки сетевых портов: ${errorMessage}`);
            } else {
                message.error('Ошибка загрузки сетевых портов');
            }
        } finally {
            if (!skipLoadingOverlay) {
                setLoading(false);
            }
        }
    }, [resolvePortWithPendingOverride]);

    const refreshPortsImmediately = useCallback(async () => {
        // skipLoadingOverlay, keepOptimistic, forceRefresh — иначе ports/all без refresh
        // отдаёт кэш и затирает только что применённый band.
        await fetchPorts(true, true, true);
    }, [fetchPorts]);

    /** После set-bands: сразу патчим колонку Band, затем refresh снимка с Midori/БД. */
    const handleBandPolicyApplied = useCallback(async (payload) => {
        const rows = Array.isArray(payload?.results)
            ? payload.results
            : (payload && typeof payload === 'object' ? [payload] : []);
        const byModemId = new Map();
        rows.forEach((row) => {
            if (!row || row.status !== 'success') return;
            const modemId = Number(row.modem_id);
            if (!Number.isFinite(modemId) || modemId <= 0) return;
            const bands = normalizeAllowedLteBands(
                row.bands_enabled || row.allowed_lte_bands || row.bands
            );
            if (!bands.length) return;
            byModemId.set(modemId, bands);
        });
        if (byModemId.size) {
            setPorts((prev) => prev.map((port) => {
                const modemId = Number(port.server_id ?? port.id);
                const bands = byModemId.get(modemId);
                if (!bands) return port;
                return { ...port, allowed_lte_bands: bands };
            }));
            setSelectedRows((prev) => prev.map((port) => {
                const modemId = Number(port.server_id ?? port.id);
                const bands = byModemId.get(modemId);
                if (!bands) return port;
                return { ...port, allowed_lte_bands: bands };
            }));
            setSelectedPort((prev) => {
                if (!prev) return prev;
                const modemId = Number(prev.server_id ?? prev.id);
                const bands = byModemId.get(modemId);
                return bands ? { ...prev, allowed_lte_bands: bands } : prev;
            });
        }
        await refreshPortsImmediately();
    }, [refreshPortsImmediately]);

    useEffect(() => {
        if (!completedBulkProgress) return;
        const progress = completedBulkProgress;
        const completed = progress.completed || 0;
        const errors = progress.errors || 0;
        setBulkUpdateStats({
            summary: progress.summary || {
                total_updated: completed - errors,
                total_errors: errors
            },
            details: progress.details || [],
            error_details: progress.error_details || []
        });
        setBulkUpdateStatsVisible(true);
        setBulkEditModalVisible(false);
        setSelectedRowKeys([]);
        setSelectedRows([]);
        clearCompletedBulkProgress();
        void refreshPortsImmediately();
    }, [completedBulkProgress, clearCompletedBulkProgress, refreshPortsImmediately]);

    const handleManualRefresh = useCallback(async () => {
        setManualRefreshing(true);
        try {
            pendingPortOverridesRef.current.clear();
            await fetchPorts(false, false, true);
        } finally {
            setManualRefreshing(false);
        }
    }, [fetchPorts]);


    const extractConnectionsFromResponse = useCallback((response) => {
        if (!response || !response.data) return [];
        if (Array.isArray(response.data)) return response.data;
        if (Array.isArray(response.data.connections)) return response.data.connections;
        if (Array.isArray(response.data.data)) return response.data.data;
        if (Array.isArray(response.data?.data?.connections)) return response.data.data.connections;
        return [];
    }, []);

    const getActiveKrakenConnectionsCached = useCallback(async ({ force = false, showLoader = false } = {}) => {
        if (!force && Array.isArray(krakenConnectionsCacheRef.current)) {
            return krakenConnectionsCacheRef.current;
        }

        if (krakenConnectionsInFlightRef.current) {
            return krakenConnectionsInFlightRef.current;
        }

        if (showLoader) {
            setLoadingConnections(true);
        }

        const requestPromise = (async () => {
            const response = await apiService.getActiveKrakenConnections({ light: 1 });
            const connections = extractConnectionsFromResponse(response);
            const activeConnections = connections.filter(conn => conn.is_active);
            krakenConnectionsCacheRef.current = activeConnections;
            setKrakenConnections(activeConnections);
            return activeConnections;
        })();

        krakenConnectionsInFlightRef.current = requestPromise;

        try {
            return await requestPromise;
        } finally {
            krakenConnectionsInFlightRef.current = null;
            if (showLoader) {
                setLoadingConnections(false);
            }
        }
    }, [extractConnectionsFromResponse]);

    // Загрузка типов модемов из Kraken API
    const fetchModemTypes = useCallback(async () => {
        try {
            const connections = await getActiveKrakenConnectionsCached();
            if (Array.isArray(connections) && connections.length > 0) {
                // Берем первое активное подключение для получения типов модемов
                const activeConnection = connections[0];
                if (activeConnection) {
                    try {
                        const typesResponse = await apiService.getKrakenModemTypes(activeConnection.id);
                        
                        logger.info('Kraken modem types response:', {
                            response: typesResponse,
                            hasData: !!(typesResponse && typesResponse.data),
                            dataKeys: typesResponse?.data ? Object.keys(typesResponse.data) : [],
                            isArray: Array.isArray(typesResponse?.data)
                        });

                        if (typesResponse && typesResponse.data) {
                            // Проверяем разные возможные структуры ответа
                            let modemTypesData = null;
                            
                            if (typesResponse.data.modem_types) {
                                // Структура: { data: { modem_types: [...] } }
                                modemTypesData = typesResponse.data.modem_types;
                                logger.info('Using modem_types structure, count:', modemTypesData?.length);
                            } else if (Array.isArray(typesResponse.data)) {
                                // Структура: { data: [...] }
                                modemTypesData = typesResponse.data;
                                logger.info('Using direct array structure, count:', modemTypesData?.length);
                            } else if (typesResponse.data.types) {
                                // Структура: { data: { types: [...] } }
                                modemTypesData = typesResponse.data.types;
                                logger.info('Using types structure, count:', modemTypesData?.length);
                            }

                            if (modemTypesData && Array.isArray(modemTypesData)) {
                                // Убеждаемся, что каждый элемент имеет правильную структуру
                                const processedTypes = modemTypesData.map((type, index) => {
                                    return {
                                        id: type.id,
                                        name: type.name || `Type ${type.id}`,
                                        code: type.code || `type_${type.id}`
                                    };
                                });
                                
                                logger.info('Processed modem types:', processedTypes.length, 'types');
                                setModemTypes(processedTypes);
                            } else {
                                logger.warn('No valid modem types data found, using fallback');
                                // Попробуем использовать fallback типы модемов (расширены современными моделями и высокими ID)
                                setModemTypes([
                                    { id: 1, name: 'Huawei E3372h-153', code: 'Huawei_h_150' },
                                    { id: 2, name: 'ZTE MF833R', code: 'Zte_mf_833_r' },
                                    { id: 3, name: 'ZTE MF920RU', code: 'Zte_mf_920_ru' },
                                    { id: 4, name: 'Huawei E3372h-320', code: 'Huawei_h_320' },
                                    { id: 5, name: 'ZTE MF823D', code: 'Zte_mf_823_d' },
                                    { id: 6, name: 'ZTE MF79U', code: 'Zte_mf_79u' },
                                    { id: 7, name: 'Huawei E3372h-510', code: 'Huawei_h_510' },
                                    { id: 8, name: 'ZTE MF79U-1', code: 'Zte_mf_79u_1' },
                                    { id: 9, name: 'ZTE MF79U-2', code: 'Zte_mf_79u_2' },
                                    { id: 10, name: 'ZTE MF79U-3', code: 'Zte_mf_79u_3' },
                                    { id: 11, name: 'Huawei E3372h-607', code: 'Huawei_h_607' },
                                    { id: 12, name: 'ZTE MF79U-4', code: 'Zte_mf_79u_4' },
                                    { id: 13, name: 'Huawei E3372h-608', code: 'Huawei_h_608' },
                                    { id: 14, name: 'ZTE MF79U-5', code: 'Zte_mf_79u_5' },
                                    { id: 15, name: 'ZTE MF79U-6', code: 'Zte_mf_79u_6' },
                                    { id: 16, name: 'ZTE MF79U-7', code: 'Zte_mf_79u_7' },
                                    { id: 17, name: 'ZTE MF79U-8', code: 'Zte_mf_79u_8' },
                                    { id: 18, name: 'Huawei E3372h-609', code: 'Huawei_h_609' },
                                    { id: 19, name: 'ZTE MF79U-9', code: 'Zte_mf_79u_9' },
                                    { id: 20, name: 'ZTE MF79U-10', code: 'Zte_mf_79u_10' },
                                    { id: 21, name: 'Huawei E3372h-610', code: 'Huawei_h_610' },
                                    { id: 22, name: 'ZTE MF79U-11', code: 'Zte_mf_79u_11' },
                                    { id: 23, name: 'ZTE MF79U-12', code: 'Zte_mf_79u_12' },
                                    { id: 24, name: 'Huawei E3372h-611', code: 'Huawei_h_611' },
                                    { id: 25, name: 'ZTE MF79U-13', code: 'Zte_mf_79u_13' },
                                    { id: 26, name: 'ZTE MF79U-14', code: 'Zte_mf_79u_14' },
                                    { id: 27, name: 'Huawei E3372h-612', code: 'Huawei_h_612' },
                                    { id: 28, name: 'ZTE MF79U-15', code: 'Zte_mf_79u_15' },
                                    { id: 29, name: 'ZTE MF79U-16', code: 'Zte_mf_79u_16' },
                                    { id: 30, name: 'Huawei E3372h-613', code: 'Huawei_h_613' }
                                ]);
                            }
                        } else {
                            logger.warn('No response data from Kraken API, using fallback');
                            setModemTypes([
                                { id: 1, name: 'Huawei E3372h-153', code: 'Huawei_h_150' },
                                { id: 2, name: 'ZTE MF833R', code: 'Zte_mf_833_r' },
                                { id: 3, name: 'ZTE MF920RU', code: 'Zte_mf_920_ru' },
                                { id: 4, name: 'Huawei E3372h-320', code: 'Huawei_h_320' },
                                { id: 5, name: 'ZTE MF823D', code: 'Zte_mf_823_d' },
                                { id: 6, name: 'ZTE MF79U', code: 'Zte_mf_79u' },
                                { id: 7, name: 'Huawei E3372h-510', code: 'Huawei_h_510' },
                                { id: 8, name: 'ZTE MF79U-1', code: 'Zte_mf_79u_1' },
                                { id: 9, name: 'ZTE MF79U-2', code: 'Zte_mf_79u_2' },
                                { id: 10, name: 'ZTE MF79U-3', code: 'Zte_mf_79u_3' },
                                { id: 11, name: 'Huawei E3372h-607', code: 'Huawei_h_607' },
                                { id: 12, name: 'ZTE MF79U-4', code: 'Zte_mf_79u_4' },
                                { id: 13, name: 'Huawei E3372h-608', code: 'Huawei_h_608' },
                                { id: 14, name: 'ZTE MF79U-5', code: 'Zte_mf_79u_5' },
                                { id: 15, name: 'ZTE MF79U-6', code: 'Zte_mf_79u_6' },
                                { id: 16, name: 'ZTE MF79U-7', code: 'Zte_mf_79u_7' },
                                { id: 17, name: 'ZTE MF79U-8', code: 'Zte_mf_79u_8' },
                                { id: 18, name: 'Huawei E3372h-609', code: 'Huawei_h_609' },
                                { id: 19, name: 'ZTE MF79U-9', code: 'Zte_mf_79u_9' },
                                { id: 20, name: 'ZTE MF79U-10', code: 'Zte_mf_79u_10' },
                                { id: 21, name: 'Huawei E3372h-610', code: 'Huawei_h_610' },
                                { id: 22, name: 'ZTE MF79U-11', code: 'Zte_mf_79u_11' },
                                { id: 23, name: 'ZTE MF79U-12', code: 'Zte_mf_79u_12' },
                                { id: 24, name: 'Huawei E3372h-611', code: 'Huawei_h_611' },
                                { id: 25, name: 'ZTE MF79U-13', code: 'Zte_mf_79u_13' },
                                { id: 26, name: 'ZTE MF79U-14', code: 'Zte_mf_79u_14' },
                                { id: 27, name: 'Huawei E3372h-612', code: 'Huawei_h_612' },
                                { id: 28, name: 'ZTE MF79U-15', code: 'Zte_mf_79u_15' },
                                { id: 29, name: 'ZTE MF79U-16', code: 'Zte_mf_79u_16' },
                                { id: 30, name: 'Huawei E3372h-613', code: 'Huawei_h_613' }
                            ]);
                        }
                    } catch (error) {
                        logger.error('Error fetching modem types from API:', {
                            error: error,
                            connectionId: activeConnection.id,
                            connectionName: activeConnection.name
                        });
                        setModemTypes([
                            { id: 1, name: 'Huawei E3372h-153', code: 'Huawei_h_150' },
                            { id: 2, name: 'ZTE MF833R', code: 'Zte_mf_833_r' },
                            { id: 3, name: 'ZTE MF920RU', code: 'Zte_mf_920_ru' },
                            { id: 4, name: 'Huawei E3372h-320', code: 'Huawei_h_320' },
                            { id: 5, name: 'ZTE MF823D', code: 'Zte_mf_823_d' },
                            { id: 6, name: 'ZTE MF79U', code: 'Zte_mf_79u' },
                            { id: 7, name: 'Huawei E3372h-510', code: 'Huawei_h_510' },
                            { id: 8, name: 'ZTE MF79U-1', code: 'Zte_mf_79u_1' },
                            { id: 9, name: 'ZTE MF79U-2', code: 'Zte_mf_79u_2' },
                            { id: 10, name: 'ZTE MF79U-3', code: 'Zte_mf_79u_3' },
                            { id: 11, name: 'Huawei E3372h-607', code: 'Huawei_h_607' },
                            { id: 12, name: 'ZTE MF79U-4', code: 'Zte_mf_79u_4' },
                            { id: 13, name: 'Huawei E3372h-608', code: 'Huawei_h_608' },
                            { id: 14, name: 'ZTE MF79U-5', code: 'Zte_mf_79u_5' },
                            { id: 15, name: 'ZTE MF79U-6', code: 'Zte_mf_79u_6' },
                            { id: 16, name: 'ZTE MF79U-7', code: 'Zte_mf_79u_7' },
                            { id: 17, name: 'ZTE MF79U-8', code: 'Zte_mf_79u_8' },
                            { id: 18, name: 'Huawei E3372h-609', code: 'Huawei_h_609' },
                            { id: 19, name: 'ZTE MF79U-9', code: 'Zte_mf_79u_9' },
                            { id: 20, name: 'ZTE MF79U-10', code: 'Zte_mf_79u_10' },
                            { id: 21, name: 'Huawei E3372h-610', code: 'Huawei_h_610' },
                            { id: 22, name: 'ZTE MF79U-11', code: 'Zte_mf_79u_11' },
                            { id: 23, name: 'ZTE MF79U-12', code: 'Zte_mf_79u_12' },
                            { id: 24, name: 'Huawei E3372h-611', code: 'Huawei_h_611' },
                            { id: 25, name: 'ZTE MF79U-13', code: 'Zte_mf_79u_13' },
                            { id: 26, name: 'ZTE MF79U-14', code: 'Zte_mf_79u_14' },
                            { id: 27, name: 'Huawei E3372h-612', code: 'Huawei_h_612' },
                            { id: 28, name: 'ZTE MF79U-15', code: 'Zte_mf_79u_15' },
                            { id: 29, name: 'ZTE MF79U-16', code: 'Zte_mf_79u_16' },
                            { id: 30, name: 'Huawei E3372h-613', code: 'Huawei_h_613' }
                        ]);
                }
            } else {
                logger.warn('No active Kraken connection found, using fallback modem types');
                setModemTypes([
                    { id: 1, name: 'Huawei E3372h-153', code: 'Huawei_h_150' },
                    { id: 2, name: 'ZTE MF833R', code: 'Zte_mf_833_r' },
                    { id: 3, name: 'ZTE MF920RU', code: 'Zte_mf_920_ru' },
                    { id: 4, name: 'Huawei E3372h-320', code: 'Huawei_h_320' },
                    { id: 5, name: 'ZTE MF823D', code: 'Zte_mf_823_d' },
                    { id: 6, name: 'ZTE MF79U', code: 'Zte_mf_79u' },
                    { id: 7, name: 'Huawei E3372h-510', code: 'Huawei_h_510' },
                    { id: 8, name: 'ZTE MF79U-1', code: 'Zte_mf_79u_1' },
                    { id: 9, name: 'ZTE MF79U-2', code: 'Zte_mf_79u_2' },
                    { id: 10, name: 'ZTE MF79U-3', code: 'Zte_mf_79u_3' }
                ]);
            }
            } else {
                logger.warn('No response data, using fallback modem types');
                setModemTypes([
                    { id: 1, name: 'Huawei E3372h-153', code: 'Huawei_h_150' },
                    { id: 2, name: 'ZTE MF833R', code: 'Zte_mf_833_r' },
                    { id: 3, name: 'ZTE MF920RU', code: 'Zte_mf_920_ru' },
                    { id: 4, name: 'Huawei E3372h-320', code: 'Huawei_h_320' },
                    { id: 5, name: 'ZTE MF823D', code: 'Zte_mf_823_d' },
                    { id: 6, name: 'ZTE MF79U', code: 'Zte_mf_79u' },
                    { id: 7, name: 'Huawei E3372h-510', code: 'Huawei_h_510' },
                    { id: 8, name: 'ZTE MF79U-1', code: 'Zte_mf_79u_1' },
                    { id: 9, name: 'ZTE MF79U-2', code: 'Zte_mf_79u_2' },
                    { id: 10, name: 'ZTE MF79U-3', code: 'Zte_mf_79u_3' }
                ]);
            }
        } catch (error) {
            logger.error('Error in fetchModemTypes function:', error);
            // Используем fallback типы модемов при ошибке
            setModemTypes([
                { id: 1, name: 'Huawei E3372h-153', code: 'Huawei_h_150' },
                { id: 4, name: 'Huawei E3372h-320', code: 'Huawei_h_320' },
                { id: 6, name: 'Huawei E8372h-320', code: 'Huawei_8372_h_320' },
                { id: 7, name: 'Huawei E8372h-153', code: 'Huawei_8372_h_153' },
                { id: 8, name: 'Huawei E5373s-157', code: 'Huawei_5373_s_157' },
                { id: 20, name: 'ZTE MF79U', code: 'Zte_mf_79_u' },
                { id: 5, name: 'ZTE MF823D', code: 'Zte_mf_823_d' },
                { id: 2, name: 'ZTE MF833R', code: 'Zte_mf_833_r' },
                { id: 3, name: 'ZTE MF920RU', code: 'Zte_mf_920_ru' }
            ]);
        }
    }, [getActiveKrakenConnectionsCached]);

    // Загрузка подключений Кракен
    const fetchKrakenConnections = useCallback(async (options = {}) => {
        try {
            return await getActiveKrakenConnectionsCached({ showLoader: true, ...options });
        } catch (error) {
            logger.error('Error fetching Kraken connections:', error);
            message.error('Ошибка загрузки подключений Кракен');
            return [];
        }
    }, [getActiveKrakenConnectionsCached]);

    // Загрузка сетевых интерфейсов для выбранного подключения
    const fetchNetworkInterfaces = async (connectionId) => {
        if (!connectionId) {
            setNetworkInterfaces([]);
            return;
        }
        try {
            setLoadingInterfaces(true);
            const response = await apiService.getKrakenNetworkInterfaces(connectionId);
            
            let interfaces = [];
            // Обрабатываем разные варианты структуры ответа
            if (response) {
                if (response.data) {
                    if (Array.isArray(response.data)) {
                        interfaces = response.data;
                    } else if (response.data.network_interfaces && Array.isArray(response.data.network_interfaces)) {
                        interfaces = response.data.network_interfaces;
                    } else if (response.data.data && Array.isArray(response.data.data)) {
                        interfaces = response.data.data;
                    }
                } else if (response.network_interfaces && Array.isArray(response.network_interfaces)) {
                    interfaces = response.network_interfaces;
                } else if (Array.isArray(response)) {
                    interfaces = response;
                }
            }
            
            setNetworkInterfaces(interfaces);
        } catch (error) {
            logger.error('Error fetching network interfaces:', error);
            message.error('Ошибка загрузки сетевых интерфейсов');
            setNetworkInterfaces([]);
        } finally {
            setLoadingInterfaces(false);
        }
    };

    // Загрузка TCP отпечатков для выбранного подключения
    const fetchTcpFingerprintsForConnection = async (connectionId) => {
        if (!connectionId) {
            setTcpFingerprintsForCreate([]);
            return;
        }
        try {
            setLoadingTcpFingerprintsForCreate(true);
            const response = await apiService.getKrakenOsfpList(connectionId);
            
            let fingerprints = [];
            if (response && response.data) {
                if (response.data.osfp_list && Array.isArray(response.data.osfp_list)) {
                    fingerprints = response.data.osfp_list;
                } else if (Array.isArray(response.data)) {
                    fingerprints = response.data;
                } else if (response.data.fingerprints && Array.isArray(response.data.fingerprints)) {
                    fingerprints = response.data.fingerprints;
                }
            } else if (response && Array.isArray(response)) {
                fingerprints = response;
            }
            
            // Обрабатываем отпечатки для UI
            const processedFingerprints = fingerprints.map((fingerprint) => {
                const displayName = fingerprint.display_name || 
                                   fingerprint.name || 
                                   fingerprint.label || 
                                   (fingerprint.os && fingerprint.browser ? `${fingerprint.os} (${fingerprint.browser})` : null) ||
                                   `TCP Fingerprint ${fingerprint.id || ''}`;
                
                return {
                    id: fingerprint.id,
                    name: displayName,
                    modem: fingerprint.modem || fingerprint.os || 'Unknown',
                    sig: fingerprint.sig || '',
                    os: fingerprint.os || '',
                    browser: fingerprint.browser || ''
                };
            });
            
            setTcpFingerprintsForCreate(processedFingerprints);
        } catch (error) {
            logger.error('Error fetching TCP fingerprints:', error);
            setTcpFingerprintsForCreate([]);
        } finally {
            setLoadingTcpFingerprintsForCreate(false);
        }
    };

    // Загрузка пользователей сервера
    const fetchServerUsers = useCallback(async () => {
        try {
            const connectionsData = await getActiveKrakenConnectionsCached();
            
            if (connectionsData && connectionsData.length > 0) {
                // Берем первое активное подключение для получения пользователей
                const activeConnection = connectionsData[0];
                if (activeConnection) {
                    try {
                        const usersResponse = await apiService.getKrakenUsers(activeConnection.id);

                        if (usersResponse && usersResponse.data) {
                            // Проверяем разные возможные структуры ответа
                            let usersData = null;
                            
                            if (usersResponse.data.data) {
                                // Структура: { data: { data: [...] } }
                                usersData = usersResponse.data.data;
                            } else if (Array.isArray(usersResponse.data)) {
                                // Структура: { data: [...] }
                                usersData = usersResponse.data;
                            } else if (usersResponse.data.users) {
                                // Структура: { data: { users: [...] } }
                                usersData = usersResponse.data.users;
                            }

                            if (usersData && Array.isArray(usersData)) {
                                // Обрабатываем пользователей для UI
                                   const processedUsers = usersData.map((user, index) => {
                                       return {
                                           id: user.id,
                                           name: user.username || user.login || user.name || `User ${user.id}`,
                                           login: user.username || user.login || `user_${user.id}`,
                                           bitrate_in: user.bitrate_in || 0,
                                           bitrate_out: user.bitrate_out || 0
                                       };
                                   });
                                
                                setServerUsers(processedUsers);
                                } else {
                                setServerUsers([]);
                            }
                        } else {
                            setServerUsers([]);
                        }
                    } catch (usersError) {
                        logger.error('Error fetching server users from API:', usersError);
                        // Используем fallback пользователей для тестирования
                        setServerUsers([
                            { id: 1, name: 'admin', login: 'admin', bitrate_in: 0, bitrate_out: 0 },
                            { id: 2, name: 'user1', login: 'user1', bitrate_in: 1000, bitrate_out: 1000 },
                            { id: 3, name: 'user2', login: 'user2', bitrate_in: 2000, bitrate_out: 2000 }
                        ]);
                    }
                } else {
                    setServerUsers([]);
                }
            } else {
                setServerUsers([]);
            }
        } catch (error) {
            logger.error('Error fetching server users:', error);
            setServerUsers([]);
        }
    }, [getActiveKrakenConnectionsCached]);

    // Загрузка TCP отпечатков
    const fetchTcpFingerprints = useCallback(async () => {
        try {
            logger.info('🔄 NetworkPorts: Начало загрузки TCP отпечатков');
            const connectionsData = await getActiveKrakenConnectionsCached();
            
            logger.info(`📊 NetworkPorts: Найдено подключений: ${connectionsData?.length || 0}`);
            
            if (connectionsData && connectionsData.length > 0) {
                const activeConnection = connectionsData[0];
                logger.info(`✅ NetworkPorts: Используем активное подключение: ${activeConnection.id}`);
                
                if (activeConnection) {
                    try {
                        logger.info(`🔍 NetworkPorts: Запрос TCP отпечатков для подключения ${activeConnection.id}`);
                        const fingerprintsResponse = await apiService.getKrakenOsfpList(activeConnection.id);
                        logger.info('📦 NetworkPorts: Ответ API для TCP отпечатков:', fingerprintsResponse);

                        if (fingerprintsResponse && fingerprintsResponse.data) {
                            // Проверяем разные возможные структуры ответа
                            let fingerprintsData = null;
                            
                            if (fingerprintsResponse.data.osfp_list && Array.isArray(fingerprintsResponse.data.osfp_list)) {
                                // Структура: { data: { osfp_list: [...] } }
                                fingerprintsData = fingerprintsResponse.data.osfp_list;
                                logger.info(`✅ NetworkPorts: Найдено TCP отпечатков в osfp_list: ${fingerprintsData.length}`);
                            } else if (Array.isArray(fingerprintsResponse.data)) {
                                // Структура: { data: [...] }
                                fingerprintsData = fingerprintsResponse.data;
                                logger.info(`✅ NetworkPorts: Найдено TCP отпечатков в data: ${fingerprintsData.length}`);
                            } else if (fingerprintsResponse.data.fingerprints && Array.isArray(fingerprintsResponse.data.fingerprints)) {
                                // Структура: { data: { fingerprints: [...] } }
                                fingerprintsData = fingerprintsResponse.data.fingerprints;
                                logger.info(`✅ NetworkPorts: Найдено TCP отпечатков в fingerprints: ${fingerprintsData.length}`);
                            } else {
                                logger.warn('⚠️ NetworkPorts: Не удалось извлечь данные TCP отпечатков из ответа:', fingerprintsResponse.data);
                            }

                            if (fingerprintsData && Array.isArray(fingerprintsData) && fingerprintsData.length > 0) {
                                // Обрабатываем TCP отпечатки для UI
                                const processedFingerprints = fingerprintsData.map((fingerprint, index) => {
                                    // API возвращает display_name, name, os, browser, modem
                                    const displayName = fingerprint.display_name || 
                                                       fingerprint.name || 
                                                       fingerprint.label || 
                                                       (fingerprint.os && fingerprint.browser ? `${fingerprint.os} (${fingerprint.browser})` : null) ||
                                                       `TCP Fingerprint ${fingerprint.id || index + 1}`;
                                    
                                    return {
                                        id: fingerprint.id,
                                        name: displayName,
                                        modem: fingerprint.modem || fingerprint.os || 'Unknown',
                                        sig: fingerprint.sig || '',
                                        os: fingerprint.os || '',
                                        browser: fingerprint.browser || ''
                                    };
                                });
                                
                                logger.info(`✅ NetworkPorts: Обработано TCP отпечатков: ${processedFingerprints.length}`);
                                setTcpFingerprints(processedFingerprints);
                            } else {
                                logger.warn('⚠️ NetworkPorts: TCP отпечатки не найдены или пустой массив');
                                setTcpFingerprints([]);
                            }
                        } else {
                            logger.warn('⚠️ NetworkPorts: Ответ API не содержит данных');
                            setTcpFingerprints([]);
                        }
                    } catch (fingerprintsError) {
                        logger.error('❌ NetworkPorts: Ошибка получения TCP отпечатков из API:', fingerprintsError);
                        // Используем fallback TCP отпечатки для тестирования
                        logger.info('🔄 NetworkPorts: Используем fallback TCP отпечатки');
                        setTcpFingerprints([
                            { id: 1, name: 'Windows 10', modem: 'Windows' },
                            { id: 2, name: 'Windows 11', modem: 'Windows' },
                            { id: 3, name: 'macOS Monterey', modem: 'macOS' },
                            { id: 4, name: 'Ubuntu 20.04', modem: 'Linux' },
                            { id: 5, name: 'Android 12', modem: 'Android' }
                        ]);
                    }
                } else {
                    logger.warn('⚠️ NetworkPorts: Подключение для загрузки TCP отпечатков не найдено');
                    setTcpFingerprints([]);
                }
            } else {
                logger.warn('⚠️ NetworkPorts: Нет доступных подключений для загрузки TCP отпечатков');
                setTcpFingerprints([]);
            }
        } catch (error) {
            logger.error('❌ NetworkPorts: Ошибка загрузки TCP отпечатков:', error);
            setTcpFingerprints([]);
        } finally {
            setLoadingTcpFingerprints(false);
        }   
    }, [getActiveKrakenConnectionsCached]);

    const loadOperatorTemplates = useCallback(async () => {
        try {
            const response = await apiService.getOperatorTemplates();
            const templates = response?.data?.data?.templates || [];
            const normalized = normalizeOperatorTemplates(templates);
            operatorTemplatesRef.current = normalized;
            setOperatorTemplates(normalized);
        } catch (error) {
            logger.warn('Не удалось загрузить шаблоны операторов для статистики портов', error);
        }
    }, []);

    useEffect(() => {
        if (initialLoadStartedRef.current) {
            return;
        }
        initialLoadStartedRef.current = true;
        logger.info('🔄 NetworkPorts: первичная загрузка');

        const loadInitialData = async () => {
            const portsPromise = fetchPorts().catch((error) => {
                logger.error('fetchPorts() failed:', error);
            });
            void loadOperatorTemplates().catch((error) => {
                logger.error('loadOperatorTemplates() failed:', error);
            });
            void getActiveKrakenConnectionsCached().catch((error) => {
                logger.error('getActiveKrakenConnectionsCached() failed:', error);
            });

            await portsPromise;

            try {
                const connectionsResponse = await apiService.getKrakenConnections();
                const connectionsForStats = connectionsResponse?.data?.connections
                    || connectionsResponse?.data?.data?.connections
                    || [];
                setKrakenConnectionsForStats(Array.isArray(connectionsForStats) ? connectionsForStats : []);
            } catch (error) {
                logger.error('getKrakenConnections() for stats failed:', error);
            }

            void fetchModemTypes().catch((error) => {
                logger.error('fetchModemTypes() failed:', error);
            });
            void fetchServerUsers().catch((error) => {
                logger.error('fetchServerUsers() failed:', error);
            });
            void fetchTcpFingerprints().catch((error) => {
                logger.error('fetchTcpFingerprints() failed:', error);
            });
        };

        void loadInitialData();
    }, [
        fetchPorts,
        loadOperatorTemplates,
        getActiveKrakenConnectionsCached,
        fetchModemTypes,
        fetchServerUsers,
        fetchTcpFingerprints,
    ]);

    useEffect(() => {
        if (location.pathname !== '/network-ports') return;
        if (!hasAppliedPortsRef.current) return;
        void loadOperatorTemplates();
        void getActiveKrakenConnectionsCached();
    }, [location.pathname, loadOperatorTemplates, getActiveKrakenConnectionsCached]);

    // Умный polling:
    // - частый опрос на активной странице
    // - редкий опрос в фоне
    // - пауза при открытых модалках/массовом обновлении
    useEffect(() => {
        if (location.pathname !== '/network-ports') return undefined;

        let timeoutId = null;
        let isCancelled = false;

        const isAnyModalActive = (
            createModalVisible ||
            editModalVisible ||
            bulkEditModalVisible ||
            detailsModalVisible
        );

        const shouldPauseRefresh = () => (
            isAnyModalActive ||
            loading ||
            syncing ||
            bulkUpdateInProgress
        );

        const getNextIntervalMs = () => {
            if (shouldPauseRefresh()) return 5000;
            if (document.visibilityState !== 'visible') return 60000;
            return NP_BACKGROUND_REFRESH_MS;
        };

        const refreshDynamicPortsData = async () => {
            if (shouldPauseRefresh()) return;
            if (backgroundRefreshInFlightRef.current) return;

            backgroundRefreshInFlightRef.current = true;
            try {
                await fetchPorts(true);
            } catch (error) {
                logger.debug('NetworkPorts: ошибка фонового автообновления', error);
            } finally {
                backgroundRefreshInFlightRef.current = false;
            }
        };

        const scheduleNextTick = () => {
            if (isCancelled) return;
            timeoutId = setTimeout(async () => {
                await refreshDynamicPortsData();
                scheduleNextTick();
            }, getNextIntervalMs());
        };

        const handleVisibilityChange = () => {
            if (isCancelled || document.visibilityState !== 'visible' || shouldPauseRefresh()) return;
            if (backgroundRefreshInFlightRef.current) return;
            refreshDynamicPortsData();
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        scheduleNextTick();

        return () => {
            isCancelled = true;
            if (timeoutId) clearTimeout(timeoutId);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [
        location.pathname,
        fetchPorts,
        createModalVisible,
        editModalVisible,
        bulkEditModalVisible,
        detailsModalVisible,
        loading,
        syncing,
        bulkUpdateInProgress,
    ]);

    // Загрузка подключений Кракен при открытии панели создания порта
    useEffect(() => {
        if (createModalVisible) {
            fetchKrakenConnections();
        }
    }, [createModalVisible, fetchKrakenConnections]);

    // Загрузка TCP отпечатков при открытии модальных окон редактирования
    useEffect(() => {
        if (!editModalVisible && !bulkEditModalVisible) return;

        const connectionId = selectedPort?.connection_id;
        if (connectionId) {
            setLoadingTcpFingerprints(true);
            apiService.getKrakenOsfpList(connectionId)
                .then(response => {
                    let fingerprints = [];
                    if (response?.data?.osfp_list && Array.isArray(response.data.osfp_list)) {
                        fingerprints = response.data.osfp_list;
                    } else if (Array.isArray(response?.data)) {
                        fingerprints = response.data;
                    } else if (response?.data?.fingerprints && Array.isArray(response.data.fingerprints)) {
                        fingerprints = response.data.fingerprints;
                    }
                    setTcpFingerprints(fingerprints.map(fp => ({
                        id: fp.id,
                        name: fp.display_name || fp.name || fp.label || (fp.os && fp.browser ? `${fp.os} (${fp.browser})` : `TCP Fingerprint ${fp.id || ''}`),
                        modem: fp.modem || fp.os || 'Unknown',
                        sig: fp.sig || '',
                        os: fp.os || '',
                        browser: fp.browser || '',
                    })));
                })
                .catch(error => {
                    logger.error('Ошибка загрузки TCP отпечатков:', error);
                    setTcpFingerprints([]);
                })
                .finally(() => setLoadingTcpFingerprints(false));
        } else {
            fetchTcpFingerprints().catch(error => {
                logger.error('Ошибка загрузки TCP отпечатков (fallback):', error);
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editModalVisible, bulkEditModalVisible]);

    // Синхронизация портов с Kraken
    const handleSyncPorts = useCallback(async () => {
        try {
            setSyncing(true);
            const response = await apiService.syncKrakenModems();
            const syncResult = response.data;
            
            // НОВОЕ: Сохраняем статистику для отображения в модальном окне
            setSyncStats(syncResult);
            
            // Показываем краткое сообщение
            const summary = syncResult.summary || {};
            const added = summary.total_modems_added || 0;
            const updated = summary.total_modems_updated || 0;
            const removed = summary.total_modems_removed || 0;
            message.success(`Синхронизация портов завершена: добавлено ${added}, обновлено ${updated}, удалено ${removed}`);
            
            // НОВОЕ: Открываем модальное окно с детальной статистикой
            setSyncStatsVisible(true);
            try {
                await refreshPortsImmediately();
            } catch (refreshError) {
                logger.error('Error refreshing ports after sync:', refreshError);
                message.warning('Синхронизация выполнена, но не удалось обновить таблицу. Нажмите «Обновить».');
            }
        } catch (error) {
            logger.error('Error syncing ports:', error);
            message.error(formatApiErrorDetail(error) || 'Ошибка синхронизации портов');
        } finally {
            setSyncing(false);
        }
    }, [refreshPortsImmediately]);

    // Создание нового порта
    const handleCreatePort = async (values) => {
        try {
            
            // Обрабатываем поле users - теперь это уже массив
            const processedValues = { ...values };
            if (!processedValues.users || !Array.isArray(processedValues.users)) {
                    processedValues.users = [];
            }
            
            // Убеждаемся, что connection_id передается
            if (!processedValues.connection_id) {
                message.error('Необходимо выбрать подключение Кракен');
                return;
            }
            
            // Убеждаемся, что net_mode установлен (по умолчанию 'lte')
            if (!processedValues.net_mode) {
                processedValues.net_mode = 'lte';
            }
            
            // Убеждаемся, что reconnect_type установлен (по умолчанию 'time_link')
            if (!processedValues.reconnect_type) {
                processedValues.reconnect_type = 'time_link';
            }
            
            // Обрабатываем osfp - если is_osfp false, то osfp должен быть null
            if (!processedValues.is_osfp) {
                processedValues.osfp = null;
            } else if (processedValues.osfp === undefined || processedValues.osfp === '') {
                // Если is_osfp true, но osfp не указан, оставляем null
                processedValues.osfp = null;
            }

            // Обязательные поля Kraken API: reconnect_event, timeout, params
            if (!Array.isArray(processedValues.reconnect_event) || processedValues.reconnect_event.length === 0) {
                processedValues.reconnect_event = ['timeout'];
            }
            const timeoutVal = processedValues.timeout;
            processedValues.timeout = typeof timeoutVal === 'number' && !Number.isNaN(timeoutVal)
                ? timeoutVal
                : parseInt(timeoutVal, 10) || 30;
            let paramsVal = processedValues.params;
            if (typeof paramsVal === 'string') {
                try {
                    processedValues.params = JSON.parse(paramsVal || '{}');
                } catch {
                    processedValues.params = {};
                }
            } else if (typeof paramsVal !== 'object' || paramsVal === null) {
                processedValues.params = {};
            }

            const portDataForKraken = { ...processedValues };
            delete portDataForKraken.connection_id;
            // Также удаляем поля производительности, которые не нужны в Kraken API
            delete portDataForKraken.capacity_speed_mbps;
            delete portDataForKraken.max_tcp_connections;
            delete portDataForKraken.tcp_per_client_estimate;
            delete portDataForKraken.safety_buffer;
            
            
            // Отправляем данные с connection_id для backend
            await apiService.createKrakenPort(processedValues);
            message.success('Сетевой порт успешно создан');
            setCreateModalVisible(false);
            await refreshPortsImmediately();
        } catch (error) {
            message.error(error.response?.data?.detail || 'Ошибка создания сетевого порта');
        }
    };

    // Обёртка для PortCreatePanel — вызывает handleCreatePort с данными из панели
    const handlePortCreateSubmit = async (values) => {
        setCreateSubmitting(true);
        try {
            await handleCreatePort(values);
        } finally {
            setCreateSubmitting(false);
        }
    };

    // Обёртка для PortEditPanel — вызывает handleUpdatePort с дельтой из панели
    const handlePortEditSubmit = async (delta) => {
        setEditSubmitting(true);
        try {
            await handleUpdatePort(delta);
        } finally {
            setEditSubmitting(false);
        }
    };

    // Обновление порта (принимает предвычисленную дельту из PortEditPanel)
    const handleUpdatePort = async (delta) => {
        try {
            const processedValues = { ...delta };

            // Нормализация users
            if ('users' in processedValues && (!processedValues.users || !Array.isArray(processedValues.users))) {
                processedValues.users = [];
            }

            logger.info('🔍 ========== ОБНОВЛЕНИЕ ПОРТА ==========');
            logger.info('Обновление порта:', {
                portId: selectedPort?.id,
                portName: selectedPort?.name,
                delta: processedValues,
            });

            // Проверяем, что дельта не пустая (за вычетом is_osfp/osfp которые всегда есть)
            const meaningfulKeys = Object.keys(processedValues).filter(k => k !== 'is_osfp' && k !== 'osfp');
            if (meaningfulKeys.length === 0 &&
                processedValues.is_osfp === Boolean(selectedPort?.is_osfp ?? false) &&
                processedValues.osfp === normalizeOsfpValue(selectedPort?.osfp)) {
                message.info('Нет изменений для сохранения');
                return;
            }

            // Идентификация модема
            if (!selectedPort?.connection_id && !selectedPort?.server_id) {
                message.error('Ошибка: невозможно определить модем для обновления. Обновите страницу.');
                return;
            }
            if (selectedPort?.connection_id) processedValues.connection_id = selectedPort.connection_id;
            if (selectedPort?.server_id) processedValues.server_id = selectedPort.server_id;

            if ('is_osfp' in processedValues) {
                processedValues.is_osfp = Boolean(processedValues.is_osfp);
            }

            const response = await apiService.updateKrakenPort(selectedPort.id, processedValues);
            logger.info('Ответ сервера:', response);

            // Обновляем локальное состояние сразу и максимально надежно:
            // сравниваем ключи как строки, чтобы не потерять матч из-за number/string.
            const serverUpdated = response?.data?.updated_port || {};
            const optimisticPatch = {
                ...processedValues,
                ...serverUpdated,
            };
            queuePortOverride(selectedPort, optimisticPatch);
            const selectedServerId = selectedPort?.server_id != null ? String(selectedPort.server_id) : null;
            const selectedPortId = selectedPort?.id != null ? String(selectedPort.id) : null;
            const selectedConnectionId = selectedPort?.connection_id != null ? String(selectedPort.connection_id) : null;

            const applyPortUpdate = (port) => {
                const portServerId = port?.server_id != null ? String(port.server_id) : null;
                const portId = port?.id != null ? String(port.id) : null;
                const portConnectionId = port?.connection_id != null ? String(port.connection_id) : null;

                const matchByServer = selectedServerId && portServerId === selectedServerId;
                const matchByIdAndConnection = (
                    selectedPortId &&
                    selectedConnectionId &&
                    portId === selectedPortId &&
                    portConnectionId === selectedConnectionId
                );
                const matchByIdOnly = selectedPortId && portId === selectedPortId;

                if (!matchByServer && !matchByIdAndConnection && !matchByIdOnly) {
                    return port;
                }

                return {
                    ...port,
                    ...serverUpdated,
                    ...processedValues,
                    id: serverUpdated.id ?? port.id,
                    server_id: port.server_id,
                    connection_id: port.connection_id,
                };
            };

            setPorts(prevPorts => prevPorts.map(applyPortUpdate));
            setFilteredPorts(prevFiltered => prevFiltered.map(applyPortUpdate));

            message.success('Сетевой порт успешно обновлен');
            setEditModalVisible(false);
            setSelectedPort(null);
            setInitialFormValues(null);

            // Мгновенно подтягиваем актуальные данные, без ручного обновления страницы
            await refreshPortsImmediately();
        } catch (error) {
            logger.error('❌ Error in handleUpdatePort:', error);
            message.error(error.response?.data?.detail || 'Ошибка обновления сетевого порта');
        }
    };

    const handleDeletePort = async (portId) => {
        try {
            await apiService.deleteKrakenPort(portId);
            setPorts(prevPorts => prevPorts.filter((port) => String(port.id) !== String(portId)));
            setFilteredPorts(prevFiltered => prevFiltered.filter((port) => String(port.id) !== String(portId)));
            message.success('Сетевой порт успешно удален');
            await refreshPortsImmediately();
        } catch (error) {
            message.error(error.response?.data?.detail || 'Ошибка удаления сетевого порта');
        }
    };

    // Массовое редактирование портов
    const handleBulkUpdatePorts = async (values) => {
        const totalPorts = selectedRows.length;
        let taskId = null;

        try {
            // ✅ КРИТИЧЕСКАЯ ПРОВЕРКА: Предупреждение о дублирующихся kraken_id
            const krakenIds = selectedRows.map(port => port.kraken_id || port.kraken_modem_id);
            const krakenIdCounts = {};
            krakenIds.forEach(id => {
                krakenIdCounts[id] = (krakenIdCounts[id] || 0) + 1;
            });
            
            const duplicates = Object.entries(krakenIdCounts)
                .filter(([_, count]) => count > 1)
                .map(([id]) => id);
            
            if (duplicates.length > 0) {
                message.warning(`⚠️ Предупреждение: Обнаружены дублирующиеся kraken_id: ${duplicates.join(', ')}`);
                logger.warn(`WARNING: Дублирующиеся kraken_id при массовом редактировании: ${duplicates.join(', ')}`);
            }
            
            setBulkEditModalVisible(false);
            startBulkUpdate({ total: totalPorts, phase: 'preparing' });

            const updateData = {};
            
            // Добавляем только те поля, которые были изменены
            // Основные настройки модема
            if (values.type !== undefined && values.type !== null) {
                updateData.type = values.type;
            }
            if (values.net_mode !== undefined && values.net_mode !== null) {
                updateData.net_mode = values.net_mode;
            }

            // Поля производительности
            if (values.capacity_speed_mbps !== undefined) {
                updateData.capacity_speed_mbps = values.capacity_speed_mbps;
            }
            if (values.max_tcp_connections !== undefined) {
                updateData.max_tcp_connections = values.max_tcp_connections;
            }
            if (values.safety_buffer !== undefined) {
                updateData.safety_buffer = values.safety_buffer;
            }
            
            // Поля производительности (дополнительные)
            if (values.tcp_per_client_estimate !== undefined) {
                updateData.tcp_per_client_estimate = values.tcp_per_client_estimate;
            }
            
            // Поля мониторинга
            if (values.monitoring_interval !== undefined) {
                updateData.monitoring_interval = values.monitoring_interval;
            }
            if (values.reconnect_type !== undefined) {
                updateData.reconnect_type = values.reconnect_type;
            }
            if (values.reconnect_interval !== undefined) {
                updateData.reconnect_interval = values.reconnect_interval;
            }
            if (values.reconnect_min !== undefined) {
                updateData.reconnect_min = values.reconnect_min;
            }
            if (values.mtu !== undefined) {
                updateData.mtu = values.mtu;
            }
            if (values.is_osfp !== undefined) {
                updateData.is_osfp = values.is_osfp;
            }
            if (values.osfp !== undefined) {
                updateData.osfp = values.osfp;
            }
            if (values.reconnect_event !== undefined && Array.isArray(values.reconnect_event)) {
                updateData.reconnect_event = values.reconnect_event;
            }

            if (Object.keys(updateData).length === 0) {
                stopBulkUpdate();
                message.warning({
                    content: 'Нет изменений для применения: измените поле или переключатель настроек модема.',
                    key: 'bulk-modem-update',
                    duration: 4,
                });
                return;
            }

            showBulkLoadingMessage(
                totalPorts > 0
                    ? `Отправка настроек на сервер (${totalPorts} модемов)…`
                    : 'Отправка настроек на сервер…',
            );

            // Передаём connection_id только при одном подключении; при нескольких — бэкенд определит по server_id
            const connectionIdsForRequest = new Set(selectedRows.map(port => port.connection_id));
            if (connectionIdsForRequest.size === 1) {
                updateData.connection_id = selectedRows[0].connection_id;
                logger.info(`✅ Добавлено connection_id=${updateData.connection_id} в запрос для правильной идентификации модемов`);
            } else {
                logger.info(`Массовое обновление модемов из ${connectionIdsForRequest.size} подключений — connection_id не передаём, бэкенд определит по server_id`);
            }

            const localPreviewPatch = { ...updateData };
            delete localPreviewPatch.connection_id;
            if (Object.keys(localPreviewPatch).length > 0) {
                selectedRows.forEach((port) => queuePortOverride(port, localPreviewPatch));

                const selectedPortKeys = new Set(selectedRows.map((port) => buildPortKey(port)).filter(Boolean));
                const applyBulkPreview = (port) => {
                    const key = buildPortKey(port);
                    if (!key || !selectedPortKeys.has(key)) return port;
                    return {
                        ...port,
                        ...localPreviewPatch,
                    };
                };

                setPorts(prevPorts => prevPorts.map(applyBulkPreview));
                setFilteredPorts(prevFiltered => prevFiltered.map(applyBulkPreview));
            }

            // ✅ ИСПРАВЛЕНО: ВСЕГДА используем server_id (уникальный ID из kraken_servers)
            // server_id гарантированно уникален и не дублируется между серверами
            // Это решает проблему с одинаковыми kraken_id на разных серверах Kraken
            const portIds = selectedRows.map(port => {
                // КРИТИЧЕСКИ ВАЖНО: Используем server_id (id из таблицы kraken_servers)
                // который является первичным ключом и уникален для каждого модема
                const serverId = port.server_id || port.id;
                
                if (!port.server_id) {
                    logger.error(`❌ КРИТИЧЕСКАЯ ОШИБКА: У порта ${port.name || port.id} отсутствует server_id! Используем fallback на id=${port.id}`);
                }
                
                logger.info(`✅ Порт ${port.name || port.id}: используем server_id=${serverId} (kraken_connection_id=${port.connection_id}, kraken_id=${port.kraken_id})`);
                return serverId;
            });
            logger.info(`📤 Отправка массового обновления для ${portIds.length} портов с уникальными server_id: ${JSON.stringify(portIds)}`);
            
            const updatePromise = apiService.bulkUpdateKrakenPorts(portIds, updateData);

            const result = await updatePromise;
            taskId = result.data?.task_id;

            logger.info(`Результат массового обновления: ${JSON.stringify(result)}`);

            if (taskId) {
                registerBulkUpdateTask(taskId);
                showBulkLoadingMessage(
                    totalPorts > 0
                        ? `Передача настроек в Kraken: 0 из ${totalPorts}…`
                        : 'Передача настроек в Kraken…',
                );
                return;
            }
            
            // Если task_id не получен (старый формат ответа), завершаем сразу
            stopBulkUpdate();
            message.success({
                content: result.data?.message || `Настройки портов обновлены для ${selectedRows.length} портов`,
                key: 'bulk-modem-update',
                duration: 5,
            });
            
            setBulkEditModalVisible(false);
            setSelectedRowKeys([]);
            setSelectedRows([]);
            await refreshPortsImmediately();
        } catch (error) {
            stopBulkUpdate();
            logger.error('Ошибка массового обновления портов:', error);
            message.error({
                content: error.response?.data?.detail || 'Ошибка массового обновления портов',
                key: 'bulk-modem-update',
                duration: 5,
            });
        }
    };

    // Массовое удаление портов
    const handleBulkDeletePorts = async () => {
        try {
            // Используем оптимизированный bulk endpoint для массового удаления
            const portIds = selectedRows.map(port => port.id);
            logger.info(`Отправка массового удаления для ${portIds.length} портов`);
            
            const result = await apiService.bulkDeleteKrakenPorts(portIds);
            
            logger.info(`Результат массового удаления: ${JSON.stringify(result)}`);
            
            if (result.data?.errors > 0) {
                message.warning(result.data?.message || `Удалено ${result.data?.deleted} портов, ошибок: ${result.data?.errors}`);
            } else {
                message.success(result.data?.message || `${selectedRows.length} портов успешно удалены`);
            }

            const selectedIds = new Set(selectedRows.map((port) => String(port.id)));
            setPorts(prevPorts => prevPorts.filter((port) => !selectedIds.has(String(port.id))));
            setFilteredPorts(prevFiltered => prevFiltered.filter((port) => !selectedIds.has(String(port.id))));
            setSelectedRowKeys([]);
            setSelectedRows([]);
            await refreshPortsImmediately();
        } catch (error) {
            logger.error('Ошибка массового удаления портов:', error);
            message.error(error.response?.data?.detail || 'Ошибка массового удаления портов');
        }
    };

    const performModemProxyQuickCheck = useCallback(async (portRecord) => {
        const modemId = getModemCheckId(portRecord);
        if (!modemId) {
            throw new Error('Невозможно определить ID модема для проверки прокси.');
        }

        const response = await apiService.quickCheckModemProxies(modemId);
        const payload = response?.data?.data || response?.data || {};

        return {
            modemId,
            modemName: portRecord?.name || `#${modemId}`,
            totalChecked: Number(payload.total_checked || 0),
            available: Number(payload.available || 0),
            unavailable: Number(payload.unavailable || 0),
            errors: Number(payload.errors || 0),
        };
    }, [getModemCheckId]);

    const handleCheckModemUnavailableProxies = useCallback(async (portRecord) => {
        const modemId = getModemCheckId(portRecord);
        if (!modemId) {
            setProxyCheckSummary({
                tone: 'error',
                text: 'Ошибка: невозможно определить модем для проверки прокси.',
            });
            return;
        }

        const modemName = portRecord?.name || `#${modemId}`;
        setModemCheckingState([modemId], true);

        try {
            const result = await performModemProxyQuickCheck(portRecord);
            if (result.totalChecked === 0) {
                setProxyCheckSummary({
                    tone: 'success',
                    text: `Проверка прокси (${modemName}): модемов 1, проверено 0, доступно 0, недоступно 0, ошибок 0`,
                });
            } else {
                setProxyCheckSummary({
                    tone: result.errors > 0 ? 'error' : 'success',
                    text: `Проверка прокси (${modemName}): модемов 1, проверено ${result.totalChecked}, доступно ${result.available}, недоступно ${result.unavailable}, ошибок ${result.errors}`,
                });
            }
            await fetchPorts(true);
        } catch (err) {
            setProxyCheckSummary({
                tone: 'error',
                text: `Ошибка проверки прокси (${modemName}): ${err?.response?.data?.detail || err.message}`,
            });
        } finally {
            setModemCheckingState([modemId], false);
        }
    }, [fetchPorts, getModemCheckId, performModemProxyQuickCheck, setModemCheckingState]);

    const handleBulkCheckUnavailableProxies = useCallback(async () => {
        const uniqueModemsMap = new Map();
        selectedRows.forEach((row) => {
            const modemId = getModemCheckId(row);
            if (!modemId || uniqueModemsMap.has(modemId)) return;
            uniqueModemsMap.set(modemId, row);
        });

        const modemsForCheck = Array.from(uniqueModemsMap.values());
        if (modemsForCheck.length === 0) {
            message.warning('Выберите хотя бы один модем для проверки прокси.');
            return;
        }

        const modemIds = modemsForCheck.map((row) => getModemCheckId(row)).filter(Boolean);
        setModemCheckingState(modemIds, true);

        const totals = {
            checkedModems: modemsForCheck.length,
            checkedProxies: 0,
            available: 0,
            unavailable: 0,
            errors: 0,
            failedModems: 0,
        };

        try {
            for (const modemRecord of modemsForCheck) {
                try {
                    const result = await performModemProxyQuickCheck(modemRecord);
                    totals.checkedProxies += result.totalChecked;
                    totals.available += result.available;
                    totals.unavailable += result.unavailable;
                    totals.errors += result.errors;
                } catch (error) {
                    totals.failedModems += 1;
                    totals.errors += 1;
                    logger.error('Ошибка проверки прокси для модема:', {
                        modem: modemRecord?.name,
                        server_id: modemRecord?.server_id,
                        error: error?.response?.data?.detail || error?.message,
                    });
                }
            }

            if (totals.checkedProxies === 0 && totals.failedModems === totals.checkedModems) {
                setProxyCheckSummary({
                    tone: 'error',
                    text: 'Не удалось выполнить проверку прокси для выбранных модемов.',
                });
            } else {
                const totalErrors = totals.errors + totals.failedModems;
                setProxyCheckSummary({
                    tone: totalErrors > 0 ? 'error' : 'success',
                    text: `Проверка прокси (выбранных модемов): модемов ${totals.checkedModems}, проверено ${totals.checkedProxies}, доступно ${totals.available}, недоступно ${totals.unavailable}, ошибок ${totalErrors}`,
                });
            }
            await fetchPorts(true);
        } finally {
            setModemCheckingState(modemIds, false);
        }
    }, [fetchPorts, getModemCheckId, performModemProxyQuickCheck, selectedRows, setModemCheckingState]);

    const handleRebootModem = useCallback((portRecord) => {
        const modemId = getModemCheckId(portRecord);
        const modemName = portRecord?.name || `#${modemId || 'unknown'}`;
        const disabledReason = getRebootDisabledReason(portRecord, { strict: true });
        if (!modemId) {
            message.error('Ошибка: невозможно определить модем для перезагрузки.');
            return;
        }
        if (disabledReason) {
            message.warning(disabledReason);
            return;
        }

        Modal.confirm({
            title: 'Перезагрузка модема',
            content: `Перезагрузить модем "${modemName}"? Команда будет отправлена на хост подключения в фоне.`,
            okText: 'Перезагрузить',
            cancelText: 'Отмена',
            onOk: async () => {
                setModemRebootingState([modemId], true);
                try {
                    const response = await apiService.rebootModem(Number(modemId));
                    const queuedPayload = response?.data?.data || {};
                    const accepted = queuedPayload?.accepted || [];
                    const rebootTaskId = queuedPayload?.task_id;
                    setProxyCheckSummary({
                        tone: 'success',
                        text: `Перезагрузка модема (${modemName}) запущена${rebootTaskId ? `, task ${rebootTaskId}` : ''}. Принято модемов: ${accepted.length || 1}. Команда отправляется...`,
                        busy: true,
                    });
                    const completionSummary = await waitForRebootCompletion({
                        modemRows: [portRecord],
                        modemIds: [modemId],
                        taskId: rebootTaskId,
                        queuedPayload,
                        onProgress: (partialSummary, phase) => {
                            setProxyCheckSummary({
                                ...formatRebootCompletionBanner(partialSummary, { phase }),
                                busy: phase === 'progress',
                            });
                        },
                    });
                    setProxyCheckSummary({
                        ...formatRebootCompletionBanner(completionSummary, { phase: 'completed' }),
                        busy: false,
                    });
                } catch (error) {
                    const detail = translateModemRebootReason(error?.response?.data?.detail) || error.message;
                    setProxyCheckSummary({
                        tone: 'error',
                        text: `Ошибка перезагрузки модема (${modemName}): ${detail}`,
                    });
                } finally {
                    await fetchPorts(true);
                    await fetchModemRebootStatuses([portRecord]);
                    setModemRebootingState([modemId], false);
                }
            },
        });
    }, [fetchModemRebootStatuses, fetchPorts, getModemCheckId, getRebootDisabledReason, setModemRebootingState, waitForRebootCompletion]);

    const handleBulkRebootModems = useCallback(async () => {
        const uniqueModemsMap = new Map();
        selectedRows.forEach((row) => {
            const modemId = getModemCheckId(row);
            if (!modemId || uniqueModemsMap.has(modemId)) return;
            uniqueModemsMap.set(modemId, row);
        });
        const modemsForReboot = Array.from(uniqueModemsMap.values());
        if (modemsForReboot.length === 0) {
            message.warning('Выберите хотя бы один модем для перезагрузки.');
            return;
        }

        const eligibleModems = modemsForReboot.filter((row) => !getRebootDisabledReason(row));
        if (eligibleModems.length === 0) {
            message.warning('Среди выбранных модемов нет подходящих для перезагрузки.');
            return;
        }

        const modemIds = eligibleModems.map((row) => Number(getModemCheckId(row))).filter(Boolean);
        Modal.confirm({
            title: 'Массовая перезагрузка модемов',
            content: `Запустить перезагрузку ${eligibleModems.length} выбранных модемов? Неподходящие модемы будут пропущены.`,
            okText: 'Перезагрузить',
            cancelText: 'Отмена',
            onOk: async () => {
                setModemRebootingState(modemIds, true);
                try {
                    const response = await apiService.rebootModemsBulk(modemIds);
                    const payload = response?.data?.data || {};
                    const acceptedCount = Array.isArray(payload.accepted) ? payload.accepted.length : 0;
                    const skippedCount = Array.isArray(payload.skipped) ? payload.skipped.length : 0;
                    setProxyCheckSummary({
                        tone: skippedCount > 0 ? 'error' : 'success',
                        text: `Перезагрузка модемов: принято ${acceptedCount}, пропущено ${skippedCount}${payload.task_id ? `, task ${payload.task_id}` : ''}. Команды отправляются параллельно...`,
                        busy: true,
                    });
                    const completionSummary = await waitForRebootCompletion({
                        modemRows: eligibleModems,
                        modemIds,
                        taskId: payload.task_id,
                        queuedPayload: payload,
                        onProgress: (partialSummary, phase) => {
                            setProxyCheckSummary({
                                ...formatRebootCompletionBanner(partialSummary, { phase }),
                                busy: phase === 'progress',
                            });
                        },
                    });
                    setProxyCheckSummary({
                        ...formatRebootCompletionBanner(completionSummary, { phase: 'completed' }),
                        busy: false,
                    });
                } catch (error) {
                    const detail = translateModemRebootReason(error?.response?.data?.detail) || error.message;
                    setProxyCheckSummary({
                        tone: 'error',
                        text: `Ошибка массовой перезагрузки модемов: ${detail}`,
                    });
                } finally {
                    await fetchPorts(true);
                    await fetchModemRebootStatuses(eligibleModems);
                    setModemRebootingState(modemIds, false);
                }
            },
        });
    }, [fetchModemRebootStatuses, fetchPorts, getModemCheckId, getRebootDisabledReason, selectedRows, setModemRebootingState, waitForRebootCompletion]);

    const setModemIdentityRefreshingState = useCallback((modemIds, isRefreshing) => {
        setRefreshingIdentityModemIds((prev) => {
            const next = new Set(prev);
            modemIds.forEach((modemId) => {
                const key = String(modemId);
                if (isRefreshing) {
                    next.add(key);
                } else {
                    next.delete(key);
                }
            });
            return next;
        });
    }, []);

    const patchPortsIdentity = useCallback((modemId, identityPatch) => {
        const normalizedId = String(modemId);
        const applyPatch = (list) => list.map((port) => {
            const portModemId = getModemCheckId(port);
            if (!portModemId || String(portModemId) !== normalizedId) {
                return port;
            }
            return {
                ...port,
                imei: identityPatch.imei ?? port.imei,
                mac_address: identityPatch.mac_address ?? port.mac_address,
            };
        });
        setPorts((prev) => applyPatch(prev));
        setFilteredPorts((prev) => applyPatch(prev));
    }, [getModemCheckId]);

    const handleRefreshModemIdentity = useCallback((portRecord) => {
        const modemId = getModemCheckId(portRecord);
        const modemName = portRecord?.name || `#${modemId || 'unknown'}`;
        if (!modemId) {
            message.error('Ошибка: невозможно определить модем для обновления IMEI/MAC.');
            return;
        }

        Modal.confirm({
            title: 'Обновить IMEI и MAC',
            content: `Получить IMEI и MAC с модема "${modemName}"?`,
            okText: 'Обновить',
            cancelText: 'Отмена',
            onOk: async () => {
                setModemIdentityRefreshingState([modemId], true);
                try {
                    const response = await apiService.refreshModemIdentity(Number(modemId));
                    const payload = response?.data?.data || {};
                    patchPortsIdentity(modemId, {
                        imei: payload.imei || '',
                        mac_address: payload.mac_address || '',
                    });
                    message.success(`IMEI/MAC обновлены для модема "${modemName}"`);
                } catch (error) {
                    const detail = error?.response?.data?.detail || error.message;
                    message.error(`Ошибка обновления IMEI/MAC (${modemName}): ${detail}`);
                } finally {
                    setModemIdentityRefreshingState([modemId], false);
                }
            },
        });
    }, [getModemCheckId, patchPortsIdentity, setModemIdentityRefreshingState]);

    const handleBulkRefreshModemIdentity = useCallback(() => {
        const uniqueModemsMap = new Map();
        selectedRows.forEach((row) => {
            const modemId = getModemCheckId(row);
            if (!modemId || uniqueModemsMap.has(modemId)) return;
            uniqueModemsMap.set(modemId, row);
        });
        const modemsForRefresh = Array.from(uniqueModemsMap.values());
        if (modemsForRefresh.length === 0) {
            message.warning('Выберите хотя бы один модем для обновления IMEI/MAC.');
            return;
        }

        const modemIds = modemsForRefresh.map((row) => Number(getModemCheckId(row))).filter(Boolean);
        Modal.confirm({
            title: 'Массовое обновление IMEI и MAC',
            content: `Получить IMEI и MAC с ${modemsForRefresh.length} выбранных модемов?`,
            okText: 'Обновить',
            cancelText: 'Отмена',
            onOk: async () => {
                setModemIdentityRefreshingState(modemIds, true);
                try {
                    const response = await apiService.refreshModemsIdentityBulk(modemIds);
                    const payload = response?.data?.data || {};
                    const results = Array.isArray(payload.results) ? payload.results : [];
                    results.forEach((item) => {
                        if (item?.status === 'success') {
                            patchPortsIdentity(item.modem_id, {
                                imei: item.imei || '',
                                mac_address: item.mac_address || '',
                            });
                        }
                    });
                    const successCount = payload.success_count ?? results.filter((item) => item.status === 'success').length;
                    const totalCount = results.length || modemIds.length;
                    if (successCount > 0) {
                        message.success(`Обновлено ${successCount} из ${totalCount} модемов`);
                    } else {
                        message.error('Не удалось обновить IMEI/MAC для выбранных модемов');
                    }
                } catch (error) {
                    const detail = error?.response?.data?.detail || error.message;
                    message.error(`Ошибка массового обновления IMEI/MAC: ${detail}`);
                } finally {
                    setModemIdentityRefreshingState(modemIds, false);
                }
            },
        });
    }, [getModemCheckId, patchPortsIdentity, selectedRows, setModemIdentityRefreshingState]);

    // Получение уникальных значений для фильтров
    const getUniqueServerNames = () => {
        const fromPorts = safePorts.map(
            (port) => port.connection_name || port.server_name || 'Не определен',
        );
        const fromConnections = (krakenConnectionsForStats || [])
            .filter((conn) => conn && conn.is_active !== false)
            .map((conn) => String(conn.name || '').trim())
            .filter(Boolean);
        const serverNames = [...fromPorts, ...fromConnections].filter(
            (name, index, arr) => arr.indexOf(name) === index,
        );
        return serverNames.sort((a, b) => a.localeCompare(b, 'ru'));
    };

    const getUniqueModemNamesWithNumber = () => {
        const modemNames = safePorts.map(port => {
            const ipNumber = getPortModemNumber(port);
            const displayName = ipNumber ? `№ ${ipNumber} ${port.name || ''}` : (port.name || 'unknown');
            return displayName;
        })
            .filter((name, index, arr) => arr.indexOf(name) === index && name);
        return modemNames.sort();
    };


    const getUniqueCities = () => {
        const cities = safePorts.map(port => port.city || 'Не определен')
            .filter((city, index, arr) => arr.indexOf(city) === index);
        return cities.sort();
    };

    const getUniqueOperators = () => {
        const operators = safePorts
            .map((port) => {
                const canonical = resolveOperatorByTemplates(port.operator, operatorTemplates);
                return canonical === 'UNKNOWN' ? 'Неизвестно' : canonical;
            })
            .filter((operator, index, arr) => arr.indexOf(operator) === index);
        return operators.sort((a, b) => String(a).localeCompare(String(b), 'ru'));
    };

    const getUniqueAllowedLteBands = () => {
        const keys = safePorts
            .map((port) => formatAllowedLteBandsKey(port.allowed_lte_bands))
            .filter((key, index, arr) => arr.indexOf(key) === index);
        return keys.sort((a, b) => {
            if (a === NP_BANDS_EMPTY_FILTER) return 1;
            if (b === NP_BANDS_EMPTY_FILTER) return -1;
            return a.localeCompare(b, 'ru');
        });
    };

    /** Уникальные значения capacity со всех портов (не только текущей страницы). */
    const getPortPerformanceMbps = (port) => {
        const raw = Number(port?.capacity_speed_mbps);
        return Number.isFinite(raw) && raw > 0 ? raw : 20;
    };

    const getUniquePerformanceSpeeds = () => {
        const speeds = safePorts
            .map((port) => getPortPerformanceMbps(port))
            .filter((speed, index, arr) => arr.indexOf(speed) === index);
        return speeds.sort((a, b) => a - b);
    };

    // Получение типа модема по ID
    const getModemTypeName = useCallback((typeId) => {
        // Если тип уже строка, возвращаем как есть
        if (typeof typeId === 'string') {
            return typeId;
        }
        
        // Ищем тип модема в загруженных данных из API
        const modemType = modemTypes.find(type => type.id === typeId || type.id === parseInt(typeId));
        if (modemType && modemType.name) {
            return modemType.name;
        }
        
        // Fallback типы модемов (расширены современными моделями и высокими ID)
        const fallbackTypes = {
            1: 'Huawei E3372h-153',
            2: 'ZTE MF833R',
            3: 'ZTE MF920RU',
            4: 'Huawei E3372h-320',
            5: 'ZTE MF823D',
            6: 'ZTE MF79U',
            7: 'Huawei E3372h-510',
            8: 'ZTE MF79U-1',
            9: 'ZTE MF79U-2',
            10: 'ZTE MF79U-3',
            11: 'Huawei E3372h-607',
            12: 'ZTE MF79U-4',
            13: 'Huawei E3372h-608',
            14: 'ZTE MF79U-5',
            15: 'ZTE MF79U-6',
            16: 'ZTE MF79U-7',
            17: 'ZTE MF79U-8',
            18: 'Huawei E3372h-609',
            19: 'ZTE MF79U-9',
            20: 'ZTE MF79U-10',  // ← Добавлен для ID 20
            21: 'Huawei E3372h-610',
            22: 'ZTE MF79U-11',
            23: 'ZTE MF79U-12',
            24: 'Huawei E3372h-611',
            25: 'ZTE MF79U-13',
            26: 'ZTE MF79U-14',
            27: 'Huawei E3372h-612',
            28: 'ZTE MF79U-15',
            29: 'ZTE MF79U-16',
            30: 'Huawei E3372h-613'
        };
        
        const fallbackName = fallbackTypes[typeId];
        if (fallbackName) {
            return fallbackName;
        }
        
        // Если fallback не найден, пытаемся сгенерировать разумное название
        // Генерируем название на основе ID
        if (typeId > 30) {
            // Для очень высоких ID используем общий паттерн
            return `Modem Type ${typeId}`;
        } else if (typeId >= 16) {
            // Для ID 16-30 предполагаем, что это вариации ZTE MF79U
            return `ZTE MF79U-${typeId - 6}`;
        } else {
            // Для остальных случаев
            return `Type ${typeId}`;
        }
    }, [modemTypes]);

    // Применение фильтров к данным
    const applyFilters = useCallback(() => {
        let filtered = [...safePorts];

        // Фильтр по названию сервера
        if (serverNameFilter.length > 0) {
            filtered = filtered.filter(port => {
                const serverName = port.connection_name || port.server_name || 'Не определен';
                return serverNameFilter.includes(serverName);
            });
        }

        // Фильтр по названию порта
        if (portNameFilter.length > 0) {
            filtered = filtered.filter(port => portNameFilter.includes(port.name));
        }

        // Фильтр по типу модема
        if (modemTypeFilter.length > 0) {
            filtered = filtered.filter(port => {
                const modemTypeName = getModemTypeName(port.type);
                return modemTypeFilter.includes(modemTypeName);
            });
        }

        setFilteredPorts(filtered);
    }, [safePorts, serverNameFilter, portNameFilter, modemTypeFilter, getModemTypeName]);

    // Применяем фильтры при изменении данных или фильтров
    useEffect(() => {
        applyFilters();
    }, [applyFilters]);

    // Инициализация отфильтрованных данных
    useEffect(() => {
        if (safePorts.length > 0 && filteredPorts.length === 0) {
            setFilteredPorts([...safePorts]);
        }
    }, [safePorts, filteredPorts.length]);


    // Получение цвета для статуса
    const getStatusColor = (active) => active ? 'green' : 'red';
    
    const RECONNECT_LABELS = { time: 'Время', link: 'Ссылка', time_link: 'Время + Ссылка' };

    const getReconnectTypeColor = (type) => {
        switch(type) {
            case 'time': return 'purple';
            case 'link': return 'green';
            case 'time_link': return 'blue';
            default: return 'default';
        }
    };

    const getReconnectTypeLabel = (type) => RECONNECT_LABELS[type] || type;

    const getPortKrakenModemNumberForSimManager = (record) => {
        if (!record || typeof record !== 'object') {
            return '';
        }
        const accountingNumber = getPortModemNumber(record);
        if (accountingNumber != null && String(accountingNumber).trim() !== '') {
            return String(accountingNumber).trim();
        }
        if (record.kraken_modem_id != null && String(record.kraken_modem_id).trim() !== '') {
            return String(record.kraken_modem_id).trim();
        }
        if (record.kraken_id != null && String(record.kraken_id).trim() !== '') {
            return String(record.kraken_id).trim();
        }
        return '';
    };

    const getPortServerIdForSimManager = (record) => {
        if (!record || typeof record !== 'object') {
            return '';
        }
        if (record.server_id != null && String(record.server_id).trim() !== '') {
            return String(record.server_id).trim();
        }
        return '';
    };

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 80,
            sorter: (a, b) => a.id - b.id,
            render: (id) => <span className="np-cell--muted">{String(id).padStart(3, '0')}</span>
        },
        {
            title: 'Сервер',
            dataIndex: 'connection_name',
            key: 'connection_name',
            width: 144,
            sorter: (a, b) => {
                const aName = a.connection_name || a.server_name || 'Не определен';
                const bName = b.connection_name || b.server_name || 'Не определен';
                return aName.localeCompare(bName);
            },
            filters: getUniqueServerNames().map(name => ({
                text: name,
                value: name
            })),
            filterIcon: (filtered) => <NpFilterIcon filtered={filtered} />,
            filterDropdown: (props) => <NpFilterDropdown title="Сервер" {...props} />,
            filteredValue: tableFilters.connection_name || null,
            onFilter: (value, record) => {
                const serverName = record.connection_name || record.server_name || 'Не определен';
                return serverName === value;
            },
            render: (text, record) => text || record.server_name || 'Не определен'
        },
        {
            title: 'ГЕО',
            dataIndex: 'city',
            key: 'city',
            width: 144,
            sorter: (a, b) => {
                const aCity = a.city || '';
                const bCity = b.city || '';
                return aCity.localeCompare(bCity);
            },
            filters: getUniqueCities().map(city => ({
                text: city,
                value: city
            })),
            filterIcon: (filtered) => <NpFilterIcon filtered={filtered} />,
            filterDropdown: (props) => <NpFilterDropdown title="ГЕО" {...props} />,
            filteredValue: tableFilters.city || null,
            onFilter: (value, record) => {
                const recordCity = record.city || 'Не определен';
                return recordCity === value;
            },
            render: (city) => {
                const SHORT_CITY = {
                    'Санкт-Петербург': 'СПб', 'санкт-петербург': 'СПб', 'Saint Petersburg': 'СПб', 'SPB': 'СПб',
                    'Москва': 'МСК', 'москва': 'МСК', 'Moscow': 'МСК', 'MSK': 'МСК',
                };
                const label = SHORT_CITY[city] || city || '—';
                return <span className="np-cell--dark">{label}</span>;
            }
        },
        {
            title: 'Модем',
            dataIndex: 'name',
            key: 'name',
            width: 144,
            render: (name, record) => {
                const ipNumber = getPortModemNumber(record);
                if (ipNumber) {
                    return (
                        <span className="np-cell--dark"><b>№ {ipNumber}</b> {name || ''}</span>
                    );
                }
                return <span className="np-cell--dark">{name || 'unknown'}</span>;
            },
            sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
            filters: getUniqueModemNamesWithNumber().map(fullName => ({
                text: fullName,
                value: fullName
            })),
            filterIcon: (filtered) => <NpFilterIcon filtered={filtered} />,
            filterDropdown: (props) => <NpFilterDropdown title="Модем" {...props} />,
            filteredValue: tableFilters.name || null,
            onFilter: (value, record) => {
                return isPortMatchingModemFilter(value, record);
            },
        },
        {
            title: 'Статус',
            dataIndex: 'active',
            key: 'active',
            width: 144,
            filters: [
                { text: 'Рабочий', value: 'working' },
                { text: 'Недоступен', value: 'not_working' },
                { text: 'Выключен', value: 'offline' },
                { text: 'Неизвестно', value: 'unknown' }
            ],
            filterIcon: (filtered) => <NpFilterIcon filtered={filtered} />,
            filterDropdown: (props) => <NpFilterDropdown title="Статус" {...props} />,
            filteredValue: tableFilters.active || null,
            onFilter: (value, record) => {
                const modemStatus = record.modem_status || 'unknown';
                const isOffline = record.modem_offline === true;
                if (value === 'offline') return isOffline;
                if (value === 'not_working') return modemStatus === 'not_working' && !isOffline;
                return modemStatus === value;
            },
            render: (_, record) => {
                const modemStatus = record.modem_status || 'unknown';
                const isOffline = record.modem_offline === true;

                let text = 'Неизвестно';
                let color = 'grey';

                if (isOffline) {
                    text = 'Выключен';
                    color = 'grey';
                } else if (modemStatus === 'working') {
                    text = 'Рабочий';
                    color = 'green';
                } else if (modemStatus === 'not_working') {
                    text = 'Недоступен';
                    color = 'red';
                }

                return <span className={`np-badge np-badge--${color}`}>{text}</span>;
            }
        },
        {
            title: 'Общая загрузка',
            key: 'overall_load',
            width: 144,
            sorter: (a, b) => (a.overall_load_percentage || 0) - (b.overall_load_percentage || 0),
            filters: [
                { text: 'Свободен (0%)', value: 'free' },
                { text: 'Минимальная (0-35%)', value: 'low' },
                { text: 'Средняя (36-70%)', value: 'medium' },
                { text: 'Максимальная (71-99%)', value: 'high' },
                { text: 'Переполнен (>99%)', value: 'overloaded' }
            ],
            filterIcon: (filtered) => <NpFilterIcon filtered={filtered} />,
            filterDropdown: (props) => <NpFilterDropdown title="Общая загрузка" {...props} />,
            filteredValue: tableFilters.overall_load || null,
            onFilter: (value, record) => {
                const load = record.overall_load_percentage || 0;
                switch (value) {
                    case 'free': return load === 0 || load < 0.1;
                    case 'low': return load >= 0.1 && load <= 35;
                    case 'medium': return load >= 36 && load <= 70;
                    case 'high': return load >= 71 && load <= 99;
                    case 'overloaded': return load > 99;
                    default: return true;
                }
            },
            render: (_, record) => {
                const load = record.overall_load_percentage !== undefined && record.overall_load_percentage !== null
                    ? record.overall_load_percentage
                    : (record.utilization !== undefined && record.utilization !== null ? record.utilization : 0);

                let badgeColor = 'green';
                let badgeText = 'Min';

                if (load === 0 || load < 0.1) {
                    badgeColor = 'grey';
                    badgeText = 'Free';
                } else if (load > 99) {
                    badgeColor = 'dark';
                    badgeText = 'Load';
                } else if (load >= 71) {
                    badgeColor = 'red';
                    badgeText = 'Max';
                } else if (load >= 36) {
                    badgeColor = 'yellow';
                    badgeText = 'Med';
                }

                return (
                    <span className="np-cell-composed">
                        <span className={`np-badge np-badge--${badgeColor}`}>{badgeText}</span>
                        <span className="np-cell-composed__text">{load.toFixed(0)}%</span>
                    </span>
                );
            }
        },
        {
            title: 'Прокси',
            key: 'proxy_count',
            width: 96,
            sorter: (a, b) => (a.total_used || 0) - (b.total_used || 0),
            render: (_, record) => {
                const count = record.total_used || 0;
                if (count <= 0) {
                    return <span className="np-cell--muted">{count}</span>;
                }
                const displayFilterKey = getPortModemDisplayName(record);
                const proxiesModemPath = `/proxies?modem=${encodeURIComponent(displayFilterKey)}&page=1`;
                const handleOpenProxiesForModem = (e) => {
                    createSpaLinkClickHandler(navigate, proxiesModemPath)(e);
                    e.stopPropagation();
                };
                return (
                    <a
                        href={proxiesModemPath}
                        className="np-modem-link-btn"
                        onClick={handleOpenProxiesForModem}
                        aria-label={`Прокси на модеме ${displayFilterKey}: ${count}`}
                        tabIndex={0}
                    >
                        <span className="np-cell--dark">{count}</span>
                    </a>
                );
            }
        },
        {
            title: 'Уровень сигнала',
            key: 'signal_info',
            width: 144,
            sorter: (a, b) => (a.signal || 0) - (b.signal || 0),
            render: (_, record) => {
                const signal = (record.signal !== null && record.signal !== undefined) ? record.signal : null;
                if (signal === null) {
                    return <span className="np-badge np-badge--grey">Нет данных</span>;
                }

                const textClass = signal > 0 ? 'np-signal__text--green' : 'np-signal__text--red';
                const bars = [];
                for (let i = 0; i < 5; i++) {
                    bars.push(
                        <span key={i} className={`np-signal__bar ${i < signal ? 'np-signal__bar--filled' : 'np-signal__bar--empty'}`} />
                    );
                }

                return (
                    <span className="np-signal">
                        <span className={`np-signal__text ${textClass}`}>{signal}/5</span>
                        <span className="np-signal__bars">{bars}</span>
                    </span>
                );
            }
        },
        {
            title: 'Band',
            key: 'allowed_lte_bands',
            width: 128,
            sorter: (a, b) => {
                const aBands = formatAllowedLteBandsKey(a.allowed_lte_bands);
                const bBands = formatAllowedLteBandsKey(b.allowed_lte_bands);
                return aBands.localeCompare(bBands, 'ru');
            },
            filters: getUniqueAllowedLteBands().map((key) => ({
                text: key === NP_BANDS_EMPTY_FILTER ? 'Нет данных' : key,
                value: key,
            })),
            filterIcon: (filtered) => <NpFilterIcon filtered={filtered} />,
            filterDropdown: (props) => <NpFilterDropdown title="Band" {...props} />,
            filteredValue: tableFilters.allowed_lte_bands || null,
            onFilter: (value, record) => formatAllowedLteBandsKey(record.allowed_lte_bands) === value,
            render: (_, record) => {
                const bands = normalizeAllowedLteBands(record.allowed_lte_bands);
                if (!bands.length) {
                    return <span className="np-badge np-badge--grey">Нет данных</span>;
                }
                // Диапазон, на котором модем работает сейчас, — с сервера Midori (active_lte_bands).
                const active = new Set(normalizeAllowedLteBands(record.active_lte_bands));
                return (
                    <span className="np-cell--dark">
                        {bands.map((band, index) => (
                            <React.Fragment key={band}>
                                {index > 0 && '+'}
                                <span className={active.has(band) ? 'np-band--active' : undefined}>{band}</span>
                            </React.Fragment>
                        ))}
                    </span>
                );
            }
        },
        {
            title: 'Интернет',
            key: 'internet_status',
            width: 112,
            filters: [
                { text: 'Подключен', value: true },
                { text: 'Отключен', value: false }
            ],
            filterIcon: (filtered) => <NpFilterIcon filtered={filtered} />,
            filterDropdown: (props) => <NpFilterDropdown title="Интернет" {...props} />,
            filteredValue: tableFilters.internet_status || null,
            onFilter: (value, record) => record.conn_inet === value,
            render: (_, record) => {
                const connInet = record.conn_inet;
                if (connInet === null || connInet === undefined) {
                    return <span className="np-badge np-badge--grey">Нет данных</span>;
                }
                const isConnected = connInet === true;
                return (
                    <span className={`np-badge np-badge--${isConnected ? 'green' : 'red'}`}>
                        {isConnected ? 'Подключен' : 'Отключен'}
                    </span>
                );
            }
        },
        {
            title: 'Оператор',
            dataIndex: 'operator',
            key: 'operator',
            width: 144,
            sorter: (a, b) => {
                const aOperator = resolveOperatorByTemplates(a.operator, operatorTemplates);
                const bOperator = resolveOperatorByTemplates(b.operator, operatorTemplates);
                return String(aOperator).localeCompare(String(bOperator), 'ru');
            },
            filters: getUniqueOperators().map(operator => ({
                text: operator,
                value: operator
            })),
            filterIcon: (filtered) => <NpFilterIcon filtered={filtered} />,
            filterDropdown: (props) => <NpFilterDropdown title="Оператор" {...props} />,
            filteredValue: tableFilters.operator || null,
            onFilter: (value, record) => {
                const recordOperatorRaw = resolveOperatorByTemplates(record.operator, operatorTemplates);
                const recordOperator = recordOperatorRaw === 'UNKNOWN' ? 'Неизвестно' : recordOperatorRaw;
                return recordOperator === value;
            },
            render: (operator, record) => {
                const canonicalOperator = resolveOperatorByTemplates(operator, operatorTemplates);
                const displayOperator = canonicalOperator === 'UNKNOWN' ? 'Неизвестно' : canonicalOperator;
                const op = displayOperator.toLowerCase();
                let color = 'grey';
                if (op.includes('мтс') || op.includes('mts')) color = 'red';
                else if (op.includes('мегафон') || op.includes('megafon')) color = 'green';
                else if (op === 'т2' || op === 't2' || op.includes('tele2')) color = 'dark';
                else if (op.includes('билайн') || op.includes('beeline')) color = 'yellow';
                const km = getPortKrakenModemNumberForSimManager(record);
                const sid = getPortServerIdForSimManager(record);
                const canSimLink = Boolean(km || sid);
                const badge = <span className={`np-badge np-badge--${color}`}>{displayOperator}</span>;
                if (!canSimLink) {
                    return badge;
                }
                const simParams = new URLSearchParams();
                if (km) {
                    simParams.set('modem_number', km);
                }
                if (sid) {
                    simParams.set('modem_server_id', sid);
                }
                const simManagerPath = `/sim-manager?${simParams.toString()}`;
                const handleOpenSimManagerFromOperator = (e) => {
                    createSpaLinkClickHandler(navigate, simManagerPath)(e);
                    e.stopPropagation();
                };
                const ariaLabel = km
                    ? `SIM-менеджер: SIM на модеме № ${km}`
                    : 'SIM-менеджер: SIM на этом модеме';
                return (
                    <a
                        href={simManagerPath}
                        className="np-operator-sim-link-btn"
                        onClick={handleOpenSimManagerFromOperator}
                        aria-label={ariaLabel}
                        tabIndex={0}
                    >
                        {badge}
                    </a>
                );
            }
        },
        {
            title: 'Производительность',
            key: 'performance',
            sorter: (a, b) => getPortPerformanceMbps(a) - getPortPerformanceMbps(b),
            filters: getUniquePerformanceSpeeds().map((speed) => ({
                text: `${speed} Мб/с`,
                value: speed,
            })),
            filterIcon: (filtered) => <NpFilterIcon filtered={filtered} />,
            filterDropdown: (props) => <NpFilterDropdown title="Производительность" {...props} />,
            filteredValue: tableFilters.performance || null,
            onFilter: (value, record) => getPortPerformanceMbps(record) === Number(value),
            render: (_, record) => {
                const speed = getPortPerformanceMbps(record);
                return <span className="np-badge np-badge--perf">{speed} Мб/с</span>;
            }
        },
        {
            title: 'Переподключение',
            key: 'reconnect',
            width: 144,
            sorter: (a, b) => (a.reconnect_interval || 0) - (b.reconnect_interval || 0),
            filters: [
                { text: 'Время', value: 'time' },
                { text: 'Ссылка', value: 'link' },
                { text: 'Время + ссылка', value: 'time_link' }
            ],
            filterIcon: (filtered) => <NpFilterIcon filtered={filtered} />,
            filterDropdown: (props) => <NpFilterDropdown title="Переподключение" {...props} />,
            filteredValue: tableFilters.reconnect || null,
            onFilter: (value, record) => {
                const reconnectType = record.reconnect_type || '';
                return reconnectType === value;
            },
            render: (_, record) => {
                const rType = record.reconnect_type || '';
                let badgeColor = 'green';
                let badgeText = rType;
                const showReconnectInterval = rType !== 'link';

                if (rType === 'time_link') {
                    badgeColor = 'yellow';
                    badgeText = 'Время + ссылка';
                } else if (rType === 'link') {
                    badgeColor = 'blue';
                    badgeText = 'Ссылка';
                } else if (rType === 'time') {
                    badgeColor = 'purple';
                    badgeText = 'Время';
                }

                return (
                    <span className="np-cell-composed">
                        <span className={`np-badge np-badge--${badgeColor}`}>{badgeText}</span>
                        {showReconnectInterval && (
                            <span className="np-cell-composed__text">{record.reconnect_interval || 0}s</span>
                        )}
                    </span>
                );
            }
        },
        {
            title: 'Отпечаток ОС',
            key: 'os_fingerprint',
            width: 144,
            filters: npFilterOsfpStats.map(item => ({ text: item.label, value: item.value })),
            filterIcon: (filtered) => <NpFilterIcon filtered={filtered} />,
            filterDropdown: (props) => <NpFilterDropdown title="Отпечаток ОС" {...props} />,
            filteredValue: tableFilters.os_fingerprint || null,
            onFilter: (value, record) => {
                const label = record.is_osfp ? (record.osfp_name || 'Android (Linux:2.6.x)') : 'Выключен';
                return label === value;
            },
            render: (_, record) => {
                const isOsfp = record.is_osfp;
                const osfpName = record.osfp_name || 'Android (Linux:2.6.x)';

                if (!isOsfp) {
                    return <span className="np-badge np-badge--red">Off</span>;
                }

                return (
                    <span className="np-cell-composed">
                        <span className="np-badge np-badge--green">On</span>
                        <span className="np-cell-composed__text" title={osfpName}>
                            {osfpName.length > 18 ? osfpName.substring(0, 18) + '...' : osfpName}
                        </span>
                    </span>
                );
            }
        },
        {
            title: 'IMEI',
            dataIndex: 'imei',
            key: 'imei',
            width: 150,
            filters: npFilterImeiStats.values.map((item) => ({ text: item.label, value: item.value })),
            filterIcon: (filtered) => <NpFilterIcon filtered={filtered} />,
            filterDropdown: (props) => (
                <NpFilterDropdown
                    title="IMEI"
                    emptyOption={{
                        text: `Без данных (${npFilterImeiStats.emptyCount})`,
                        value: NP_IDENTITY_EMPTY_FILTER,
                    }}
                    {...props}
                />
            ),
            filteredValue: tableFilters.imei || null,
            onFilter: (value, record) => matchIdentityFieldFilter('imei', value, record),
            render: (value) => (
                <span className="np-cell-composed__text" title={value || ''}>
                    {value || '—'}
                </span>
            ),
        },
        {
            title: 'MAC',
            dataIndex: 'mac_address',
            key: 'mac_address',
            width: 150,
            filters: npFilterMacStats.values.map((item) => ({ text: item.label, value: item.value })),
            filterIcon: (filtered) => <NpFilterIcon filtered={filtered} />,
            filterDropdown: (props) => (
                <NpFilterDropdown
                    title="MAC"
                    emptyOption={{
                        text: `Без данных (${npFilterMacStats.emptyCount})`,
                        value: NP_IDENTITY_EMPTY_FILTER,
                    }}
                    {...props}
                />
            ),
            filteredValue: tableFilters.mac_address || null,
            onFilter: (value, record) => matchIdentityFieldFilter('mac_address', value, record),
            render: (value) => (
                <span className="np-cell-composed__text" title={value || ''}>
                    {value || '—'}
                </span>
            ),
        },
        {
            title: '',
            key: 'actions',
            width: 40,
            fixed: 'right',
            render: (_, record) => {
                const modemCheckId = getModemCheckId(record);
                const isRowCheckInProgress = Boolean(modemCheckId && checkingModemIds.has(modemCheckId));
                const rowRebootDisabledReason = getRebootDisabledReason(record);
                const isRowRebootInProgress = Boolean(modemCheckId && rebootingModemIds.has(String(modemCheckId)));
                const isRowIdentityRefreshing = Boolean(modemCheckId && refreshingIdentityModemIds.has(String(modemCheckId)));
                const handleRowMenuAction = ({ key, domEvent }) => {
                    domEvent?.stopPropagation?.();

                    if (key === 'details') {
                        const nextKey = record.server_id
                            ? `server-${record.server_id}`
                            : (record.connection_id
                                ? `${record.id}-connection-${record.connection_id}`
                                : `${record.id}-${record.connection_name || 'default'}`);
                        setInfoPortKey(nextKey);
                        setNpStatsVisible(false);
                        setNpFilterPanelVisible(false);
                        return;
                    }

                    if (key === 'edit') {
                        if (!record.connection_id && !record.server_id) {
                            message.error('Ошибка: невозможно определить модем для редактирования. Обновите страницу.');
                            return;
                        }
                        setSelectedPort(record);
                        setEditModalVisible(true);
                        fetchModemTypes().catch(() => {});
                        return;
                    }

                    if (key === 'check') {
                        handleCheckModemUnavailableProxies(record);
                        return;
                    }

                    if (key === 'reboot') {
                        handleRebootModem(record);
                        return;
                    }

                    if (key === 'refreshIdentity') {
                        handleRefreshModemIdentity(record);
                        return;
                    }

                    if (key === 'delete') {
                        Modal.confirm({
                            title: 'Удалить порт',
                            content: `Вы уверены, что хотите удалить порт "${record.name}"? Это действие нельзя отменить.`,
                            okText: 'Удалить',
                            okType: 'danger',
                            cancelText: 'Отмена',
                            onOk: () => handleDeletePort(record.id)
                        });
                    }
                };

                const menuItems = [
                    {
                        key: 'details',
                        label: 'Подробности',
                        icon: <InfoCircleOutlined />,
                    },
                    {
                        key: 'edit',
                        label: 'Редактировать',
                        icon: <EditOutlined />,
                    },
                    {
                        key: 'check',
                        label: 'Проверить прокси',
                        icon: (
                            <span className={`np-bug-icon${isRowCheckInProgress ? ' np-bug-icon--blinking' : ''}`}>
                                <NpBugIcon />
                            </span>
                        ),
                        disabled: isRowCheckInProgress,
                    },
                    {
                        key: 'reboot',
                        label: 'Перезагрузка',
                        icon: <ReloadOutlined />,
                        disabled: Boolean(rowRebootDisabledReason) || isRowRebootInProgress,
                    },
                    {
                        key: 'refreshIdentity',
                        label: 'Обновить IMEI и MAC',
                        icon: <IdcardOutlined />,
                        disabled: isRowIdentityRefreshing,
                    },
                    {
                        type: 'divider'
                    },
                    {
                        key: 'delete',
                        label: 'Удалить',
                        icon: <DeleteOutlined />,
                        danger: true,
                    }
                ];

                return (
                    <Dropdown
                        menu={{ items: menuItems, onClick: handleRowMenuAction }}
                        trigger={['click']}
                        placement="bottomRight"
                        overlayClassName="pm-action-dropdown"
                    >
                        <button className="pm-action-dots" aria-label="Действия" tabIndex={0} onClick={(e) => e.stopPropagation()}>
                            <span className="pm-action-dots__dot" />
                            <span className="pm-action-dots__dot" />
                            <span className="pm-action-dots__dot" />
                        </button>
                    </Dropdown>
                );
            }
        },
    ];

    const getPortRowKey = (record) => {
        if (record.server_id) return `server-${record.server_id}`;
        if (record.connection_id) return `${record.id}-connection-${record.connection_id}`;
        return `${record.id}-${record.connection_name || 'default'}`;
    };

    const infoPort = useMemo(() => {
        if (!infoPortKey) {
            lastInfoPortRef.current = null;
            return null;
        }
        const fromSource = (Array.isArray(safePorts) ? safePorts : []).find(
            (port) => getPortRowKey(port) === infoPortKey
        );
        // Во время poll порт может кратко пропасть из списка — не размонтируем панель
        if (fromSource) {
            lastInfoPortRef.current = fromSource;
            return fromSource;
        }
        return lastInfoPortRef.current;
    }, [infoPortKey, safePorts]);

    const handleOpenPortInfo = useCallback((record) => {
        if (!record) return;
        const key = getPortRowKey(record);
        setInfoPortKey((prev) => (prev === key ? null : key));
        if (npStatsVisible) setNpStatsVisible(false);
        if (npFilterPanelVisible) setNpFilterPanelVisible(false);
    }, [npFilterPanelVisible, npStatsVisible]);

    const handleClosePortInfo = useCallback(() => {
        setInfoPortKey(null);
    }, []);

    const handleEditFromInfoPanel = useCallback((record) => {
        if (!record) return;
        if (!record.connection_id && !record.server_id) {
            message.error('Ошибка: невозможно определить модем для редактирования. Обновите страницу.');
            return;
        }
        setSelectedPort(record);
        setEditModalVisible(true);
        fetchModemTypes().catch(() => {});
    }, [fetchModemTypes]);

    const basePortDataSource = filteredPorts.length > 0 ? filteredPorts : safePorts;
    const portDataSource = useMemo(() => {
        const modemQuickFilter = Array.isArray(tableFilters.__quick_modem_status)
            ? tableFilters.__quick_modem_status[0]
            : null;
        const proxyQuickFilter = Array.isArray(tableFilters.__quick_proxy_state)
            ? tableFilters.__quick_proxy_state[0]
            : null;
        const imeiDupNorms = Array.isArray(tableFilters.__imei_dup_check)
            ? tableFilters.__imei_dup_check
            : null;

        let result = [...basePortDataSource];

        if (proxyQuickFilter === 'available') {
            result = result.filter(isPortWithAvailableProxies);
        } else if (proxyQuickFilter === 'unavailable') {
            result = result.filter(isPortWithUnavailableProxies);
        }

        if (imeiDupNorms && imeiDupNorms.length > 0) {
            const dupSet = new Set(imeiDupNorms.map((v) => String(v).toLowerCase()));
            result = result.filter((port) => {
                if (isEmptyPortIdentityField(port.imei)) {
                    return false;
                }
                return dupSet.has(String(port.imei).trim().toLowerCase());
            });
        }

        if (!modemQuickFilter) {
            return result;
        }

        if (modemQuickFilter === 'unavailable') {
            return result.filter(isPortModemUnavailable);
        }

        return result.filter(isPortModemWorking);
    }, [
        basePortDataSource,
        tableFilters.__quick_modem_status,
        tableFilters.__quick_proxy_state,
        tableFilters.__imei_dup_check,
    ]);

    const handleExportPortsExcel = useCallback(async () => {
        if (!portDataSource.length) {
            message.warning('Нет данных для экспорта');
            return;
        }
        if (exportingPorts) {
            return;
        }

        setExportingPorts(true);
        const hideLoading = message.loading('Формируем Excel...', 0);

        try {
            const serverIdsForExport = portDataSource
                .map((port) => port.server_id)
                .filter((serverId) => serverId !== undefined && serverId !== null);

            await apiService.exportNetworkPortsXlsx(serverIdsForExport);
            hideLoading();
            message.success(`Экспортировано ${serverIdsForExport.length} портов`);
        } catch (error) {
            hideLoading();
            message.error(`Ошибка экспорта: ${error.message || 'неизвестная ошибка'}`);
        } finally {
            setExportingPorts(false);
        }
    }, [exportingPorts, portDataSource]);

    const handlePortsImportFileChange = useCallback(async (event) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) {
            return;
        }

        try {
            const response = await apiService.importNetworkPortsXlsx(file);
            const result = response?.data?.data || response?.data || {};
            const parts = [];
            if (result.updated > 0) parts.push(`обновлено: ${result.updated}`);
            if (result.skipped > 0) parts.push(`пропущено: ${result.skipped}`);
            if (result.errors?.length > 0) parts.push(`ошибок: ${result.errors.length}`);
            message.success(result.message || `Импорт завершён — ${parts.join(', ') || 'без изменений'}`);
            await fetchPorts(true);
        } catch (error) {
            const detail = error?.response?.data?.detail || error.message || 'неизвестная ошибка';
            message.error(`Ошибка импорта: ${formatApiErrorDetail(detail)}`);
        }
    }, [fetchPorts]);

    const handlePortsExcelMenuClick = useCallback(({ key }) => {
        if (key === 'export') {
            void handleExportPortsExcel();
            return;
        }
        if (key === 'import') {
            portsImportFileInputRef.current?.click();
        }
    }, [handleExportPortsExcel]);

    const portsExcelMenuItems = useMemo(() => ([
        {
            key: 'export',
            label: exportingPorts ? 'Экспорт Excel...' : 'Экспорт Excel',
            disabled: exportingPorts,
        },
        { key: 'import', label: 'Импорт Excel' },
    ]), [exportingPorts]);

    useEffect(() => {
        setNpFilteredTotal(portDataSource.length);
    }, [portDataSource.length]);

    /** «из N записей» в пагинации: при полном списке без фильтров — servers_count из API (386), не длина таблицы (385). */
    const npPaginationTotalItems = useMemo(() => {
        const tableCount = npFilteredTotal;
        const apiTotal = computeModemStatsFromConnections(krakenConnectionsForStats)?.total_modems;
        const showingAllPortsWithoutFilters =
            tableCount > 0 &&
            tableCount === safePorts.length &&
            portDataSource.length === safePorts.length;
        if (showingAllPortsWithoutFilters && apiTotal != null && apiTotal > tableCount) {
            return apiTotal;
        }
        return tableCount;
    }, [npFilteredTotal, safePorts.length, portDataSource.length, krakenConnectionsForStats]);

    const handlePortShiftSelect = (record, selected, _selectedRows, nativeEvent) => {
        const currentKey = getPortRowKey(record);
        if (nativeEvent?.shiftKey && lastSelectedPortKeyRef.current != null) {
            const lastKey = lastSelectedPortKeyRef.current;
            const allKeys = portDataSource.map(getPortRowKey);
            const currentIdx = allKeys.indexOf(currentKey);
            const lastIdx = allKeys.indexOf(lastKey);

            if (currentIdx !== -1 && lastIdx !== -1) {
                const start = Math.min(currentIdx, lastIdx);
                const end = Math.max(currentIdx, lastIdx);
                const rangeKeys = allKeys.slice(start, end + 1);
                const rangeRecords = portDataSource.slice(start, end + 1);

                if (selected) {
                    const newKeys = [...new Set([...selectedRowKeys, ...rangeKeys])];
                    const existingMap = new Map(selectedRows.map(r => [getPortRowKey(r), r]));
                    rangeRecords.forEach(r => existingMap.set(getPortRowKey(r), r));
                    setSelectedRowKeys(newKeys);
                    setSelectedRows([...existingMap.values()]);
                } else {
                    const removeSet = new Set(rangeKeys);
                    setSelectedRowKeys(selectedRowKeys.filter(k => !removeSet.has(k)));
                    setSelectedRows(selectedRows.filter(r => !removeSet.has(getPortRowKey(r))));
                }
            }
        }
        lastSelectedPortKeyRef.current = currentKey;
    };

    if (loading && safePorts.length === 0) {
        return (
            <div className="loading-container">
                <div className="loader-container">
                    <div className="loader-ball" />
                    <div className="loader-shadow" />
                </div>
            </div>
        );
    }

    return (
        <div className="pm-content-fade-in np-page">
            {proxyCheckSummary && (
                <CheckResultBanner
                    text={proxyCheckSummary.text}
                    tone={proxyCheckSummary.tone}
                    busy={Boolean(proxyCheckSummary.busy)}
                    autoHideMs={proxyCheckSummary.busy ? 0 : 10000}
                    onClose={() => setProxyCheckSummary(null)}
                />
            )}
            {/* Header — Figma */}
            <div className="np-header">
                <h1 className="np-header__title">Сетевые порты</h1>
                <div className="np-header__quick-filters">
                    {!isMobile && (
                    <div className="np-quick-filters__group">
                        <span className="np-quick-filters__title">Прокси</span>
                        <button
                            type="button"
                            className={`np-quick-filters__chip${!tableFilters.__quick_proxy_state || tableFilters.__quick_proxy_state.length === 0 ? ' np-quick-filters__chip--active' : ''}`}
                            onClick={() => handleQuickFilter('__quick_proxy_state', null)}
                            aria-label={`Прокси: все ${npQuickStats.totalPorts}`}
                            tabIndex={0}
                        >
                            <span className="np-quick-filters__chip-label">Все</span>
                            <span className="np-quick-filters__chip-value">{npQuickStats.totalPorts}</span>
                        </button>
                        <button
                            type="button"
                            className={`np-quick-filters__chip${Array.isArray(tableFilters.__quick_proxy_state) && tableFilters.__quick_proxy_state[0] === 'available' ? ' np-quick-filters__chip--active' : ''}`}
                            onClick={() => handleQuickFilter('__quick_proxy_state', 'available')}
                            aria-label={`Прокси: с доступными ${npQuickStats.portsWithAvailableProxies}`}
                            tabIndex={0}
                        >
                            <span className="np-quick-filters__chip-label">Доступно</span>
                            <span className="np-quick-filters__chip-value np-quick-filters__chip-value--green">{npQuickStats.portsWithAvailableProxies}</span>
                        </button>
                        <button
                            type="button"
                            className={`np-quick-filters__chip${Array.isArray(tableFilters.__quick_proxy_state) && tableFilters.__quick_proxy_state[0] === 'unavailable' ? ' np-quick-filters__chip--active' : ''}`}
                            onClick={() => handleQuickFilter('__quick_proxy_state', 'unavailable')}
                            aria-label={`Прокси: с недоступными ${npQuickStats.portsWithUnavailableProxies}`}
                            tabIndex={0}
                        >
                            <span className="np-quick-filters__chip-label">Недоступно</span>
                            <span className="np-quick-filters__chip-value np-quick-filters__chip-value--red">{npQuickStats.portsWithUnavailableProxies}</span>
                        </button>
                    </div>
                    )}

                    <div className="np-quick-filters__group">
                        <span className="np-quick-filters__title">Модемы</span>
                        <button
                            type="button"
                            className={`np-quick-filters__chip${Array.isArray(tableFilters.__quick_modem_status) && tableFilters.__quick_modem_status[0] === 'available' ? ' np-quick-filters__chip--active' : ''}`}
                            onClick={() => handleQuickFilter('__quick_modem_status', 'available')}
                            aria-label={`Модемы доступно ${npQuickStats.modemAvailable}`}
                            tabIndex={0}
                        >
                            <span className="np-quick-filters__chip-label">Доступно</span>
                            <span className="np-quick-filters__chip-value np-quick-filters__chip-value--green">{npQuickStats.modemAvailable}</span>
                        </button>
                        <button
                            type="button"
                            className={`np-quick-filters__chip${Array.isArray(tableFilters.__quick_modem_status) && tableFilters.__quick_modem_status[0] === 'unavailable' ? ' np-quick-filters__chip--active' : ''}`}
                            onClick={() => handleQuickFilter('__quick_modem_status', 'unavailable')}
                            aria-label={`Модемы недоступно ${npQuickStats.modemUnavailable}`}
                            tabIndex={0}
                        >
                            <span className="np-quick-filters__chip-label">Недоступно</span>
                            <span className="np-quick-filters__chip-value np-quick-filters__chip-value--red">{npQuickStats.modemUnavailable}</span>
                        </button>
                    </div>

                    {!isMobile && npQuickStats.cities.length > 0 && (
                        <div className="np-quick-filters__group np-quick-filters__group--cities">
                            <span className="np-quick-filters__title">Города</span>
                            {npQuickStats.cities.map((city) => (
                                <button
                                    key={city.name}
                                    type="button"
                                    className={`np-quick-filters__chip${Array.isArray(tableFilters.city) && tableFilters.city[0] === city.name ? ' np-quick-filters__chip--active' : ''}`}
                                    onClick={() => handleQuickFilter('city', city.name)}
                                    aria-label={`${city.name} ${city.count}`}
                                    tabIndex={0}
                                >
                                    <span className="np-quick-filters__chip-label">{city.name}</span>
                                    <span className="np-quick-filters__chip-value">{city.count}</span>
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                <div className="np-header__spacer" />
                <div className="np-header__actions">
                    <button
                        type="button"
                        className={`np-header__action-btn np-header__action-btn--imei-check${
                            Array.isArray(tableFilters.__imei_dup_check)
                            && tableFilters.__imei_dup_check.length > 0
                                ? ' np-header__action-btn--active'
                                : ''
                        }`}
                        onClick={handleCheckDuplicateImei}
                        title={
                            Array.isArray(tableFilters.__imei_dup_check)
                            && tableFilters.__imei_dup_check.length > 0
                                ? 'Сбросить фильтр дублей IMEI'
                                : 'Проверить IMEI на дубли'
                        }
                        aria-label={
                            Array.isArray(tableFilters.__imei_dup_check)
                            && tableFilters.__imei_dup_check.length > 0
                                ? 'Сбросить фильтр дублей IMEI'
                                : 'Проверить IMEI'
                        }
                        tabIndex={0}
                    >
                        <NpImeiCheckIcon />
                    </button>
                    {/* Удалить выбранные (показывается когда есть выделение) */}
                    {selectedRows.length >= 1 && (
                        <button
                            className="np-header__action-btn"
                            onClick={handleBulkRefreshModemIdentity}
                            title={`Обновить IMEI и MAC (${selectedRows.length})`}
                            aria-label="Обновить IMEI и MAC выбранных модемов"
                            tabIndex={0}
                            disabled={selectedRows.some((row) => {
                                const modemId = getModemCheckId(row);
                                return modemId ? refreshingIdentityModemIds.has(String(modemId)) : false;
                            })}
                        >
                            <IdcardOutlined />
                        </button>
                    )}
                    {selectedRows.length >= 1 && (
                        <button
                            className="np-header__action-btn"
                            onClick={handleBulkRebootModems}
                            title={`Перезагрузка модемов (${selectedRows.length})`}
                            aria-label="Перезагрузка выбранных модемов"
                            tabIndex={0}
                            disabled={
                                selectedRows.every((row) => Boolean(getRebootDisabledReason(row)))
                                || selectedRows.some((row) => {
                                    const modemId = getModemCheckId(row);
                                    return modemId ? rebootingModemIds.has(String(modemId)) : false;
                                })
                            }
                        >
                            <ReloadOutlined />
                        </button>
                    )}
                    {selectedRows.length >= 1 && (
                        <button
                            className={`np-header__action-btn np-header__action-btn--check-bug${selectedRows.some((row) => {
                                const modemId = getModemCheckId(row);
                                return modemId ? checkingModemIds.has(modemId) : false;
                            }) ? ' np-header__action-btn--bug-checking' : ''}`}
                            onClick={handleBulkCheckUnavailableProxies}
                            title={`Проверить нерабочие прокси (${selectedRows.length})`}
                            aria-label="Проверить нерабочие прокси"
                            tabIndex={0}
                            disabled={selectedRows.some((row) => {
                                const modemId = getModemCheckId(row);
                                return modemId ? checkingModemIds.has(modemId) : false;
                            })}
                        >
                            <NpBugIcon />
                        </button>
                    )}
                    {selectedRows.length >= 1 && (
                        <button
                            className="np-header__action-btn np-header__action-btn--danger"
                            onClick={() => {
                                Modal.confirm({
                                    title: 'Удалить выбранные порты',
                                    content: `Вы уверены, что хотите удалить ${selectedRows.length} портов? Это действие нельзя отменить.`,
                                    okText: 'Удалить',
                                    okType: 'danger',
                                    cancelText: 'Отмена',
                                    onOk: () => handleBulkDeletePorts()
                                });
                            }}
                            title={`Удалить (${selectedRows.length})`}
                            aria-label="Удалить выбранные"
                            tabIndex={0}
                        >
                            <svg viewBox="0 0 12 12" fill="none"><path d="M1.5 3h9M4.5 3V2a1 1 0 0 1 1-1h1a1 1 0 0 1 1 1v1M9.5 3v7a1 1 0 0 1-1 1h-5a1 1 0 0 1-1-1V3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/><path d="M5 5.5v3M7 5.5v3" stroke="currentColor" strokeWidth="1" strokeLinecap="round"/></svg>
                        </button>
                    )}
                    {/* Массовое редактирование (при выделении >= 2) */}
                    {selectedRows.length >= 2 && (
                        <button
                            className="np-header__action-btn np-header__action-btn--bulk-edit"
                            onClick={() => {
                                setBulkEditModalVisible(true);
                                fetchModemTypes().catch(() => {});
                            }}
                            title={`Массовое редактирование (${selectedRows.length})`}
                            aria-label="Массовое редактирование"
                            tabIndex={0}
                        >
                            <svg viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg"><g clipPath="url(#clip0_np_bulkedit)"><path d="M9.32801 0.465036L3.23201 6.56104C2.99918 6.79261 2.8146 7.06806 2.68895 7.37146C2.5633 7.67485 2.49908 8.00015 2.50001 8.32854V9.00004C2.50001 9.13264 2.55269 9.25982 2.64646 9.35359C2.74022 9.44736 2.8674 9.50004 3.00001 9.50004H3.67151C3.99989 9.50096 4.32519 9.43674 4.62859 9.3111C4.93198 9.18545 5.20744 9.00086 5.43901 8.76804L11.535 2.67204C11.8272 2.37912 11.9913 1.98227 11.9913 1.56854C11.9913 1.1548 11.8272 0.757951 11.535 0.465036C11.2378 0.180983 10.8426 0.0224609 10.4315 0.0224609C10.0204 0.0224609 9.62517 0.180983 9.32801 0.465036ZM10.828 1.96504L4.73201 8.06104C4.45007 8.34125 4.06901 8.49899 3.67151 8.50004H3.50001V8.32854C3.50105 7.93103 3.65879 7.54997 3.93901 7.26804L10.035 1.17204C10.1418 1.07001 10.2838 1.01308 10.4315 1.01308C10.5792 1.01308 10.7212 1.07001 10.828 1.17204C10.933 1.27729 10.9919 1.41988 10.9919 1.56854C10.9919 1.71719 10.933 1.85978 10.828 1.96504Z" fill="currentColor"/><path d="M11.5 4.4895C11.3674 4.4895 11.2402 4.54218 11.1464 4.63595C11.0527 4.72972 11 4.85689 11 4.9895V7.5H9C8.60218 7.5 8.22064 7.65804 7.93934 7.93934C7.65804 8.22064 7.5 8.60218 7.5 9V11H2.5C2.10218 11 1.72064 10.842 1.43934 10.5607C1.15804 10.2794 1 9.89783 1 9.5V2.5C1 2.10218 1.15804 1.72064 1.43934 1.43934C1.72064 1.15804 2.10218 1 2.5 1H7.021C7.15361 1 7.28079 0.947322 7.37455 0.853554C7.46832 0.759785 7.521 0.632608 7.521 0.5C7.521 0.367392 7.46832 0.240215 7.37455 0.146447C7.28079 0.0526784 7.15361 0 7.021 0L2.5 0C1.8372 0.000793929 1.20178 0.264441 0.73311 0.73311C0.264441 1.20178 0.000793929 1.8372 0 2.5L0 9.5C0.000793929 10.1628 0.264441 10.7982 0.73311 11.2669C1.20178 11.7356 1.8372 11.9992 2.5 12H8.1715C8.49996 12.0009 8.82534 11.9367 9.12882 11.8111C9.43229 11.6854 9.70784 11.5008 9.9395 11.268L11.2675 9.939C11.5004 9.70744 11.685 9.43199 11.8108 9.1286C11.9365 8.82522 12.0008 8.49991 12 8.1715V4.9895C12 4.85689 11.9473 4.72972 11.8536 4.63595C11.7598 4.54218 11.6326 4.4895 11.5 4.4895ZM9.2325 10.561C9.03151 10.7615 8.77734 10.9003 8.5 10.961V9C8.5 8.86739 8.55268 8.74022 8.64645 8.64645C8.74022 8.55268 8.86739 8.5 9 8.5H10.9625C10.9006 8.77676 10.762 9.03047 10.5625 9.232L9.2325 10.561Z" fill="currentColor"/></g><defs><clipPath id="clip0_np_bulkedit"><rect width="12" height="12" fill="white"/></clipPath></defs></svg>
                        </button>
                    )}
                    {/* Редактировать (один выбранный) */}
                    {selectedRows.length === 1 && (
                        <button
                            className="np-header__action-btn"
                            onClick={() => {
                                const record = selectedRows[0];
                                if (!record.connection_id && !record.server_id) {
                                    message.error('Ошибка: невозможно определить модем для редактирования.');
                                    return;
                                }
                                setSelectedPort(record);
                                setEditModalVisible(true);
                                fetchModemTypes().catch(() => {});
                            }}
                            title="Редактировать"
                            aria-label="Редактировать"
                            tabIndex={0}
                        >
                            <svg viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg"><g clipPath="url(#clip0_np_edit)"><path d="M9.32801 0.465036L3.23201 6.56104C2.99918 6.79261 2.8146 7.06806 2.68895 7.37146C2.5633 7.67485 2.49908 8.00015 2.50001 8.32854V9.00004C2.50001 9.13264 2.55269 9.25982 2.64646 9.35359C2.74022 9.44736 2.8674 9.50004 3.00001 9.50004H3.67151C3.99989 9.50096 4.32519 9.43674 4.62859 9.3111C4.93198 9.18545 5.20744 9.00086 5.43901 8.76804L11.535 2.67204C11.8272 2.37912 11.9913 1.98227 11.9913 1.56854C11.9913 1.1548 11.8272 0.757951 11.535 0.465036C11.2378 0.180983 10.8426 0.0224609 10.4315 0.0224609C10.0204 0.0224609 9.62517 0.180983 9.32801 0.465036ZM10.828 1.96504L4.73201 8.06104C4.45007 8.34125 4.06901 8.49899 3.67151 8.50004H3.50001V8.32854C3.50105 7.93103 3.65879 7.54997 3.93901 7.26804L10.035 1.17204C10.1418 1.07001 10.2838 1.01308 10.4315 1.01308C10.5792 1.01308 10.7212 1.07001 10.828 1.17204C10.933 1.27729 10.9919 1.41988 10.9919 1.56854C10.9919 1.71719 10.933 1.85978 10.828 1.96504Z" fill="currentColor"/><path d="M11.5 4.4895C11.3674 4.4895 11.2402 4.54218 11.1464 4.63595C11.0527 4.72972 11 4.85689 11 4.9895V7.5H9C8.60218 7.5 8.22064 7.65804 7.93934 7.93934C7.65804 8.22064 7.5 8.60218 7.5 9V11H2.5C2.10218 11 1.72064 10.842 1.43934 10.5607C1.15804 10.2794 1 9.89783 1 9.5V2.5C1 2.10218 1.15804 1.72064 1.43934 1.43934C1.72064 1.15804 2.10218 1 2.5 1H7.021C7.15361 1 7.28079 0.947322 7.37455 0.853554C7.46832 0.759785 7.521 0.632608 7.521 0.5C7.521 0.367392 7.46832 0.240215 7.37455 0.146447C7.28079 0.0526784 7.15361 0 7.021 0L2.5 0C1.8372 0.000793929 1.20178 0.264441 0.73311 0.73311C0.264441 1.20178 0.000793929 1.8372 0 2.5L0 9.5C0.000793929 10.1628 0.264441 10.7982 0.73311 11.2669C1.20178 11.7356 1.8372 11.9992 2.5 12H8.1715C8.49996 12.0009 8.82534 11.9367 9.12882 11.8111C9.43229 11.6854 9.70784 11.5008 9.9395 11.268L11.2675 9.939C11.5004 9.70744 11.685 9.43199 11.8108 9.1286C11.9365 8.82522 12.0008 8.49991 12 8.1715V4.9895C12 4.85689 11.9473 4.72972 11.8536 4.63595C11.7598 4.54218 11.6326 4.4895 11.5 4.4895ZM9.2325 10.561C9.03151 10.7615 8.77734 10.9003 8.5 10.961V9C8.5 8.86739 8.55268 8.74022 8.64645 8.64645C8.74022 8.55268 8.86739 8.5 9 8.5H10.9625C10.9006 8.77676 10.762 9.03047 10.5625 9.232L9.2325 10.561Z" fill="currentColor"/></g><defs><clipPath id="clip0_np_edit"><rect width="12" height="12" fill="white"/></clipPath></defs></svg>
                        </button>
                    )}
                    {/* Синхронизировать */}
                    <button
                        type="button"
                        className={`np-header__action-btn${syncing ? ' pm-sync-btn--active' : ''}`}
                        onClick={handleSyncPorts}
                        disabled={syncing}
                        title="Синхронизировать"
                        aria-label="Синхронизировать"
                        tabIndex={0}
                    >
                        <svg viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M5.1784 1.46003L4.4704 2.61C4.3975 2.7151 4.2871 2.7883 4.1618 2.8145C4.0366 2.8406 3.9061 2.8178 3.7972 2.7506C3.6883 2.6835 3.6093 2.5772 3.5764 2.4536C3.5435 2.3299 3.5593 2.1984 3.6204 2.086L4.3284 0.93603C4.506 0.65254 4.7526 0.41881 5.0453 0.25679C5.3379 0.09477 5.6669 0.00977 6.0014 0.00977C6.3359 0.00977 6.6649 0.09477 6.9576 0.25679C7.2502 0.41881 7.4969 0.65254 7.6744 0.93603L9.7744 4.353L9.9949 3.1705C10.0192 3.0401 10.0943 2.9247 10.2037 2.8496C10.313 2.7746 10.4478 2.746 10.5782 2.7703C10.7086 2.7945 10.824 2.8696 10.8991 2.979C10.9742 3.0884 11.0027 3.2231 10.9784 3.3535L10.7284 4.6885C10.6645 5.0322 10.4825 5.3427 10.2139 5.5665C9.9453 5.7903 9.607 5.9132 9.2574 5.914C9.1599 5.914 7.6489 5.626 7.6489 5.626C7.521 5.5982 7.4091 5.5214 7.3372 5.412C7.2654 5.3026 7.2393 5.1694 7.2645 5.0409C7.2898 4.9125 7.3644 4.7991 7.4724 4.7251C7.5803 4.651 7.713 4.6223 7.8419 4.645L8.9084 4.8555L6.8214 1.46003C6.7347 1.3203 6.6137 1.205 6.4699 1.12506C6.3262 1.04511 6.1644 1.00316 5.9999 1.00316C5.8354 1.00316 5.6737 1.04511 5.5299 1.12506C5.3862 1.205 5.2652 1.3203 5.1784 1.46003ZM11.7044 7.4895L11.2204 6.7035C11.188 6.6439 11.1437 6.5914 11.0903 6.5494C11.0368 6.5075 10.9754 6.4768 10.9098 6.4593C10.8441 6.4419 10.7756 6.438 10.7084 6.4479C10.6412 6.4578 10.5767 6.4812 10.5189 6.5169C10.461 6.5525 10.4111 6.5996 10.372 6.6552C10.333 6.7108 10.3057 6.7738 10.2918 6.8403C10.2779 6.9068 10.2777 6.9754 10.2912 7.042C10.3048 7.1086 10.3317 7.1717 10.3704 7.2275L10.8544 8.0135C10.9468 8.1614 10.9974 8.3314 11.001 8.5057C11.0046 8.68 10.9611 8.852 10.8749 9.0035C10.7921 9.1547 10.6699 9.2806 10.5213 9.3679C10.3727 9.4552 10.2033 9.5007 10.0309 9.4995H6.3379L6.9734 8.865C7.0646 8.7709 7.1152 8.6446 7.1143 8.5135C7.1133 8.3824 7.0609 8.2569 6.9683 8.1641C6.8758 8.0713 6.7504 8.0185 6.6193 8.0172C6.4882 8.0159 6.3619 8.0661 6.2674 8.157L5.4214 8.9995C5.1574 9.2621 5.0062 9.6172 4.9999 9.9895C4.9999 9.9895 4.9999 9.9945 4.9999 9.9965C4.9999 9.9985 4.9999 10.003 4.9999 10.0065C4.9985 10.1989 5.0356 10.3897 5.109 10.5675C5.1824 10.7453 5.2907 10.9067 5.4274 11.042L6.2684 11.868C6.3148 11.9163 6.3703 11.9548 6.4318 11.9813C6.4933 12.0077 6.5595 12.0215 6.6264 12.0219C6.6934 12.0223 6.7597 12.0092 6.8215 11.9835C6.8833 11.9577 6.9393 11.9198 6.9862 11.8721C7.033 11.8243 7.0698 11.7676 7.0944 11.7053C7.1189 11.643 7.1307 11.5765 7.1291 11.5095C7.1274 11.4426 7.1124 11.3767 7.0848 11.3157C7.0572 11.2548 7.0176 11.1999 6.9684 11.1545L6.3044 10.4995H10.0309C10.3817 10.4983 10.7258 10.4037 11.0279 10.2253C11.3299 10.047 11.579 9.7913 11.7494 9.4848C11.9199 9.1782 12.0055 8.8317 11.9976 8.481C11.9897 8.1304 11.8885 7.7881 11.7044 7.4895ZM3.3899 3.606C3.2261 3.4944 3.0416 3.4166 2.8473 3.3771C2.653 3.3376 2.4529 3.3373 2.2584 3.376L0.94393 3.635C0.87844 3.6466 0.81592 3.6711 0.76002 3.7071C0.70412 3.7431 0.65596 3.7899 0.61836 3.8447C0.58076 3.8995 0.55447 3.9613 0.54104 4.0264C0.52761 4.0916 0.52731 4.1587 0.54014 4.224C0.55298 4.2892 0.5787 4.3512 0.61579 4.4064C0.65289 4.4616 0.70062 4.5088 0.7562 4.5453C0.81177 4.5818 0.87406 4.6069 0.93944 4.619C1.00481 4.6312 1.07195 4.6302 1.13693 4.616L2.1909 4.4085L0.29543 7.4895C0.11135 7.7881 0.01014 8.1303 0.00221 8.4809C-0.00572 8.8316 0.07992 9.178 0.25031 9.4845C0.4207 9.7911 0.6697 10.0467 0.97167 10.2251C1.27364 10.4035 1.6177 10.4982 1.96843 10.4995H3.5399C3.6725 10.4995 3.7997 10.4469 3.8935 10.3531C3.9872 10.2593 4.0399 10.1321 4.0399 9.9995C4.0399 9.8669 3.9872 9.7397 3.8935 9.646C3.7997 9.5522 3.6725 9.4995 3.5399 9.4995H1.96843C1.79642 9.5003 1.62738 9.4547 1.47918 9.3674C1.33097 9.2801 1.2091 9.1544 1.12643 9.0035C1.0403 8.852 0.99673 8.68 1.00034 8.5057C1.00395 8.3314 1.0546 8.1614 1.14693 8.0135L3.0659 4.8945L3.2889 6.0915C3.3133 6.222 3.3885 6.3373 3.498 6.4123C3.6075 6.4873 3.7423 6.5157 3.8727 6.4913C4.0031 6.4669 4.1185 6.3917 4.1934 6.2822C4.2684 6.1727 4.2968 6.038 4.2724 5.9075L4.0224 4.5725C3.9865 4.3775 3.9121 4.1916 3.8035 4.0256C3.6949 3.8597 3.5543 3.717 3.3899 3.606Z" fill="currentColor"/></svg>
                    </button>
                    <input
                        ref={portsImportFileInputRef}
                        type="file"
                        accept=".xlsx,.xls"
                        onChange={handlePortsImportFileChange}
                        style={{ display: 'none' }}
                        aria-hidden="true"
                        tabIndex={-1}
                    />
                    <Dropdown
                        menu={{ items: portsExcelMenuItems, onClick: handlePortsExcelMenuClick }}
                        trigger={['click']}
                    >
                        <button
                            type="button"
                            className={`np-header__action-btn${exportingPorts ? ' np-header__action-btn--spinning' : ''}`}
                            title="Экспорт и импорт Excel"
                            aria-label="Экспорт и импорт Excel"
                            aria-busy={exportingPorts}
                            tabIndex={0}
                        >
                            <svg viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                <path d="M9.6666 7.3336C9.2885 7.3339 8.9163 7.4262 8.5818 7.6025C8.2474 7.7788 7.9609 8.0339 7.7471 8.3456L4.4926 6.8761C4.7237 6.3179 4.7246 5.691 4.4951 5.1321L7.7451 3.6551C8.062 4.1135 8.5328 4.4431 9.0719 4.5841C9.611 4.7252 10.1828 4.6683 10.6836 4.4239C11.1845 4.1794 11.581 3.7636 11.8015 3.2518C12.022 2.7399 12.0517 2.1661 11.8853 1.6342C11.7189 1.1024 11.3673 0.647774 10.8945 0.352904C10.4216 0.0580344 9.8587 -0.057586 9.3079 0.0270138C8.757 0.111614 8.2548 0.390834 7.8922 0.814034C7.5296 1.23722 7.3307 1.77636 7.3316 2.33362C7.3337 2.46552 7.3471 2.59702 7.3716 2.72662L3.9166 4.29662C3.5848 3.98572 3.1693 3.77852 2.7214 3.70052C2.2734 3.62252 1.8124 3.67702 1.395 3.85742C0.977601 4.03772 0.622001 4.33612 0.371801 4.71582C0.121601 5.09552 -0.012199 5.54002 -0.013199 5.99472C-0.014199 6.44952 0.117701 6.89462 0.366201 7.27542C0.614701 7.65622 0.969001 7.95612 1.3856 8.13832C1.8022 8.32052 2.263 8.37712 2.7113 8.30102C3.1596 8.22492 3.5759 8.01962 3.9091 7.71012L7.3731 9.27412C7.349 9.40362 7.3358 9.53492 7.3336 9.66662C7.3335 10.1282 7.4703 10.5794 7.7266 10.9632C7.983 11.3471 8.3475 11.6462 8.7739 11.8229C9.2003 11.9996 9.6695 12.0458 10.1222 11.9558C10.5749 11.8658 10.9907 11.6435 11.3171 11.3172C11.6435 10.9908 11.8658 10.5749 11.9558 10.1222C12.0458 9.6695 11.9995 9.2003 11.8229 8.7739C11.6462 8.3475 11.347 7.9831 10.9632 7.7267C10.5794 7.4703 10.1282 7.3335 9.6666 7.3336ZM9.6666 1.00012C9.9304 1.00002 10.1882 1.07815 10.4076 1.22462C10.6269 1.37108 10.7979 1.57931 10.8989 1.82297C10.9999 2.06662 11.0264 2.33482 10.975 2.59352C10.9236 2.85222 10.7967 3.08982 10.6102 3.27642C10.4237 3.46292 10.1861 3.59002 9.9274 3.64152C9.6687 3.69302 9.4006 3.66662 9.1569 3.56572C8.9132 3.46482 8.7049 3.29382 8.5584 3.07452C8.4118 2.85522 8.3336 2.59742 8.3336 2.33362C8.3339 1.98012 8.4744 1.64117 8.7243 1.39117C8.9742 1.14116 9.3131 1.00052 9.6666 1.00012ZM2.3336 7.3336C2.0698 7.3337 1.812 7.2556 1.5926 7.1091C1.3732 6.9627 1.2022 6.7544 1.1012 6.5108C1.0002 6.2671 0.973801 5.999 1.0252 5.7403C1.0765 5.4816 1.2035 5.2439 1.39 5.0574C1.5765 4.8708 1.8141 4.7438 2.0728 4.6923C2.3314 4.6408 2.5996 4.6672 2.8433 4.7681C3.087 4.869 3.2953 5.0399 3.4418 5.2592C3.5884 5.4785 3.6666 5.7364 3.6666 6.0001C3.6662 6.3536 3.5256 6.6925 3.2758 6.9424C3.0259 7.1924 2.687 7.3331 2.3336 7.3336ZM9.6666 11.0001C9.4028 11.0001 9.145 10.9219 8.9257 10.7754C8.7064 10.6289 8.5355 10.4206 8.4346 10.1769C8.3337 9.9333 8.3073 9.6651 8.3587 9.4065C8.4102 9.1478 8.5372 8.9102 8.7237 8.7237C8.9102 8.5372 9.1478 8.4102 9.4064 8.3587C9.6651 8.3073 9.9332 8.3337 10.1769 8.4346C10.4206 8.5356 10.6288 8.7065 10.7754 8.9258C10.9219 9.1451 11.0001 9.4029 11.0001 9.6666C10.9998 10.0202 10.8592 10.3592 10.6092 10.6093C10.3592 10.8593 10.0202 10.9999 9.6666 11.0001Z" fill="currentColor" />
                            </svg>
                        </button>
                    </Dropdown>
                    {/* Обновить */}
                    <button
                        className={`np-header__action-btn${manualRefreshing ? ' np-header__action-btn--spinning' : ''}`}
                        onClick={handleManualRefresh}
                        title="Обновить"
                        aria-label="Обновить"
                        tabIndex={0}
                    >
                        <svg viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg"><g clipPath="url(#clip0_np_refresh)"><path d="M6.0001 0.999894C6.66336 1.00207 7.31959 1.13576 7.93084 1.39324C8.54208 1.65071 9.09619 2.02685 9.5611 2.49989H8.0001C7.86749 2.49989 7.74032 2.55257 7.64655 2.64634C7.55278 2.74011 7.5001 2.86729 7.5001 2.99989C7.5001 3.1325 7.55278 3.25968 7.64655 3.35345C7.74032 3.44722 7.86749 3.49989 8.0001 3.49989H10.0716C10.3178 3.49976 10.5539 3.4019 10.728 3.2278C10.9021 3.0537 11 2.81761 11.0001 2.57139V0.499894C11.0001 0.367286 10.9474 0.240109 10.8537 0.14634C10.7599 0.0525723 10.6327 -0.000106158 10.5001 -0.000106158C10.3675 -0.000106158 10.2403 0.0525723 10.1465 0.14634C10.0528 0.240109 10.0001 0.367286 10.0001 0.499894V1.53889C9.17369 0.79462 8.15639 0.295099 7.06215 0.0962684C5.96791 -0.102562 4.83991 0.00714982 3.80449 0.413118C2.76907 0.819085 1.86711 1.50528 1.19961 2.39486C0.532105 3.28443 0.125422 4.34227 0.0251013 5.44989C0.0186429 5.51952 0.0267591 5.58973 0.0489327 5.65605C0.0711063 5.72237 0.106851 5.78334 0.153889 5.83509C0.200928 5.88683 0.258228 5.92821 0.32214 5.95658C0.386052 5.98496 0.455174 5.99971 0.525101 5.99989C0.647395 6.00145 0.765868 5.95731 0.857331 5.87612C0.948793 5.79492 1.00666 5.68251 1.0196 5.56089C1.13091 4.31627 1.70359 3.15825 2.62514 2.31431C3.54669 1.47038 4.75051 1.00154 6.0001 0.999894Z" fill="currentColor"/><path d="M11.4756 6.00004C11.3533 5.99848 11.2349 6.04262 11.1434 6.12382C11.0519 6.20501 10.9941 6.31742 10.9811 6.43904C10.8985 7.39066 10.5446 8.2986 9.96145 9.05515C9.37831 9.8117 8.5904 10.3851 7.6912 10.7073C6.79199 11.0296 5.81922 11.0871 4.8883 10.8731C3.95738 10.6591 3.10737 10.1826 2.43912 9.50005H4.00012C4.13273 9.50005 4.25991 9.44737 4.35368 9.3536C4.44744 9.25983 4.50012 9.13266 4.50012 9.00005C4.50012 8.86744 4.44744 8.74026 4.35368 8.64649C4.25991 8.55272 4.13273 8.50005 4.00012 8.50005H1.92862C1.80667 8.49998 1.6859 8.52395 1.57322 8.57059C1.46054 8.61723 1.35816 8.68562 1.27193 8.77185C1.18569 8.85808 1.1173 8.96047 1.07067 9.07315C1.02403 9.18583 1.00006 9.3066 1.00012 9.42855V11.5001C1.00012 11.6327 1.0528 11.7598 1.14657 11.8536C1.24034 11.9474 1.36751 12.0001 1.50012 12.0001C1.63273 12.0001 1.75991 11.9474 1.85368 11.8536C1.94744 11.7598 2.00012 11.6327 2.00012 11.5001V10.4611C2.82654 11.2053 3.84383 11.7048 4.93807 11.9037C6.03232 12.1025 7.16031 11.9928 8.19573 11.5868C9.23115 11.1809 10.1331 10.4947 10.8006 9.60509C11.4681 8.71551 11.8748 7.65767 11.9751 6.55004C11.9816 6.48041 11.9735 6.4102 11.9513 6.34388C11.9291 6.27756 11.8934 6.21659 11.8463 6.16485C11.7993 6.11311 11.742 6.07173 11.6781 6.04335C11.6142 6.01498 11.545 6.00023 11.4751 6.00004H11.4756Z" fill="currentColor"/></g><defs><clipPath id="clip0_np_refresh"><rect width="12" height="12" fill="white"/></clipPath></defs></svg>
                    </button>
                    
                    {/* Сброс фильтров */}
                    {activeFilterCount > 0 && (
                        <button
                            className="np-header__action-btn np-header__action-btn--clear-filters"
                            onClick={handleClearAllFilters}
                            title={`Сбросить фильтры (${activeFilterCount})`}
                            aria-label={`Сбросить фильтры (${activeFilterCount})`}
                            tabIndex={0}
                        >
                            <NpFilterOffIcon />
                        </button>
                    )}
                    {/* Фильтры */}
                    <button
                        className={`np-header__action-btn${npFilterPanelVisible ? ' np-header__action-btn--active' : ''}`}
                        onClick={() => {
                            setNpFilterPanelVisible((prev) => {
                                if (!prev) {
                                    setNpStatsVisible(false);
                                    setInfoPortKey(null);
                                }
                                return !prev;
                            });
                        }}
                        title="Фильтры"
                        aria-label="Фильтры"
                        tabIndex={0}
                    >
                        <svg viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M7 12C6.8918 12 6.7865 11.9649 6.7 11.9L4.7 10.4C4.6379 10.3534 4.5875 10.293 4.5528 10.2236C4.5181 10.1542 4.5 10.0776 4.5 10V7.19L0.992 3.2435C0.7429 2.9624 0.5802 2.6154 0.5235 2.2441C0.4669 1.87283 0.5187 1.49308 0.6727 1.15051C0.8266 0.80794 1.0763 0.51713 1.3916 0.31303C1.7069 0.10894 2.0744 0.00024 2.45 0H9.55C9.9255 0.00044 10.293 0.10932 10.6081 0.31355C10.9233 0.51779 11.1728 0.80869 11.3266 1.15129C11.4805 1.4939 11.5321 1.87364 11.4753 2.2449C11.4185 2.6161 11.2557 2.963 11.0065 3.244L7.5 7.19V11.5C7.5 11.6326 7.4473 11.7598 7.3535 11.8536C7.2598 11.9473 7.1326 12 7 12ZM5.5 9.75L6.5 10.5V7C6.5001 6.8776 6.5451 6.7594 6.6265 6.668L10.2605 2.5795C10.3817 2.4425 10.4607 2.2735 10.4882 2.0927C10.5157 1.91193 10.4904 1.72705 10.4154 1.56028C10.3404 1.39351 10.2188 1.25193 10.0653 1.15254C9.9118 1.05316 9.7329 1.00019 9.55 1H2.45C2.2672 1.00028 2.0884 1.05327 1.935 1.15263C1.7816 1.25199 1.6601 1.3935 1.5851 1.56018C1.5101 1.72685 1.4848 1.91163 1.5122 2.0923C1.5395 2.2731 1.6185 2.442 1.7395 2.579L5.374 6.668C5.4552 6.7595 5.5 6.8776 5.5 7V9.75Z" fill="currentColor"/></svg>
                    </button>
                    {/* Статистика */}
                    <button
                        className={`np-header__action-btn${npStatsVisible ? ' np-header__action-btn--active' : ''}`}
                        onClick={() => {
                            setNpStatsVisible((prev) => {
                                if (!prev) {
                                    setNpFilterPanelVisible(false);
                                    setInfoPortKey(null);
                                }
                                return !prev;
                            });
                        }}
                        title="Статистика"
                        aria-label="Статистика"
                        tabIndex={0}
                    >
                        <svg viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3.5 0H2C1.46957 0 0.960859 0.210714 0.585786 0.585786C0.210714 0.960859 0 1.46957 0 2V3.5C0 4.03043 0.210714 4.53914 0.585786 4.91421C0.960859 5.28929 1.46957 5.5 2 5.5H3.5C4.03043 5.5 4.53914 5.28929 4.91421 4.91421C5.28929 4.53914 5.5 4.03043 5.5 3.5V2C5.5 1.46957 5.28929 0.960859 4.91421 0.585786C4.53914 0.210714 4.03043 0 3.5 0ZM4.5 3.5C4.5 3.76522 4.39464 4.01957 4.20711 4.20711C4.01957 4.39464 3.76522 4.5 3.5 4.5H2C1.73478 4.5 1.48043 4.39464 1.29289 4.20711C1.10536 4.01957 1 3.76522 1 3.5V2C1 1.73478 1.10536 1.48043 1.29289 1.29289C1.48043 1.10536 1.73478 1 2 1H3.5C3.76522 1 4.01957 1.10536 4.20711 1.29289C4.39464 1.48043 4.5 1.73478 4.5 2V3.5Z" fill="currentColor"/><path d="M10 0H8.5C7.96957 0 7.46086 0.210714 7.08579 0.585786C6.71071 0.960859 6.5 1.46957 6.5 2V3.5C6.5 4.03043 6.71071 4.53914 7.08579 4.91421C7.46086 5.28929 7.96957 5.5 8.5 5.5H10C10.5304 5.5 11.0391 5.28929 11.4142 4.91421C11.7893 4.53914 12 4.03043 12 3.5V2C12 1.46957 11.7893 0.960859 11.4142 0.585786C11.0391 0.210714 10.5304 0 10 0ZM11 3.5C11 3.76522 10.8946 4.01957 10.7071 4.20711C10.5196 4.39464 10.2652 4.5 10 4.5H8.5C8.23478 4.5 7.98043 4.39464 7.79289 4.20711C7.60536 4.01957 7.5 3.76522 7.5 3.5V2C7.5 1.73478 7.60536 1.48043 7.79289 1.29289C7.98043 1.10536 8.23478 1 8.5 1H10C10.2652 1 10.5196 1.10536 10.7071 1.29289C10.8946 1.48043 11 1.73478 11 2V3.5Z" fill="currentColor"/><path d="M3.5 6.5H2C1.46957 6.5 0.960859 6.71071 0.585786 7.08579C0.210714 7.46086 0 7.96957 0 8.5V10C0 10.5304 0.210714 11.0391 0.585786 11.4142C0.960859 11.7893 1.46957 12 2 12H3.5C4.03043 12 4.53914 11.7893 4.91421 11.4142C5.28929 11.0391 5.5 10.5304 5.5 10V8.5C5.5 7.96957 5.28929 7.46086 4.91421 7.08579C4.53914 6.71071 4.03043 6.5 3.5 6.5ZM4.5 10C4.5 10.2652 4.39464 10.5196 4.20711 10.7071C4.01957 10.8946 3.76522 11 3.5 11H2C1.73478 11 1.48043 10.8946 1.29289 10.7071C1.10536 10.5196 1 10.2652 1 10V8.5C1 8.23478 1.10536 7.98043 1.29289 7.79289C1.48043 7.60536 1.73478 7.5 2 7.5H3.5C3.76522 7.5 4.01957 7.60536 4.20711 7.79289C4.39464 7.98043 4.5 8.23478 4.5 8.5V10Z" fill="currentColor"/><path d="M10 6.5H8.5C7.96957 6.5 7.46086 6.71071 7.08579 7.08579C6.71071 7.46086 6.5 7.96957 6.5 8.5V10C6.5 10.5304 6.71071 11.0391 7.08579 11.4142C7.46086 11.7893 7.96957 12 8.5 12H10C10.5304 12 11.0391 11.7893 11.4142 11.4142C11.7893 11.0391 12 10.5304 12 10V8.5C12 7.96957 11.7893 7.46086 11.4142 7.08579C11.0391 6.71071 10.5304 6.5 10 6.5ZM11 10C11 10.2652 10.8946 10.5196 10.7071 10.7071C10.5196 10.8946 10.2652 11 10 11H8.5C8.23478 11 7.98043 10.8946 7.79289 10.7071C7.60536 10.5196 7.5 10.2652 7.5 10V8.5C7.5 8.23478 7.60536 7.98043 7.79289 7.79289C7.98043 7.60536 8.23478 7.5 8.5 7.5H10C10.2652 7.5 10.5196 7.60536 10.7071 7.79289C10.8946 7.98043 11 8.23478 11 8.5V10Z" fill="currentColor"/></svg>
                    </button>
                    {/* Создать порт */}
                    <button
                        className="np-header__action-btn np-header__action-btn--primary"
                        onClick={() => setCreateModalVisible(true)}
                        title="Создать порт"
                        aria-label="Создать порт"
                        tabIndex={0}
                    >
                        <svg viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M11.5 5.5H6.5V0.5C6.5 0.367392 6.44732 0.240215 6.35355 0.146447C6.25979 0.0526784 6.13261 0 6 0C5.86739 0 5.74021 0.0526784 5.64645 0.146447C5.55268 0.240215 5.5 0.367392 5.5 0.5V5.5H0.5C0.367392 5.5 0.240215 5.55268 0.146447 5.64645C0.0526784 5.74021 0 5.86739 0 6C0 6.13261 0.0526784 6.25979 0.146447 6.35355C0.240215 6.44732 0.367392 6.5 0.5 6.5H5.5V11.5C5.5 11.6326 5.55268 11.7598 5.64645 11.8536C5.74021 11.9473 5.86739 12 6 12C6.13261 12 6.25979 11.9473 6.35355 11.8536C6.44732 11.7598 6.5 11.6326 6.5 11.5V6.5H11.5C11.6326 6.5 11.7598 6.44732 11.8536 6.35355C11.9473 6.25979 12 6.13261 12 6C12 5.86739 11.9473 5.74021 11.8536 5.64645C11.7598 5.55268 11.6326 5.5 11.5 5.5Z" fill="currentColor"/></svg>
                    </button>
                </div>
            </div>

            {/* Table — Figma; info panel как pxt-content--with-info у прокси */}
            <div className={`np-table-wrap${infoPort ? ' np-table-wrap--with-info' : ''}${npStatsVisible ? ' np-table-wrap--with-stats' : ''}${npFilterPanelVisible ? ' np-table-wrap--with-stats' : ''}`}>
            <div className="np-table">
                <Card>
                    <Table
                        columns={columns}
                        dataSource={portDataSource}
                        rowKey={getPortRowKey}
                        rowClassName={(record) => {
                            const classes = [];
                            const modemStatus = record?.modem_status || 'unknown';
                            if (modemStatus === 'not_working' || modemStatus === 'unavailable') {
                                classes.push('np-row--modem-unavailable');
                            }
                            if (infoPortKey && getPortRowKey(record) === infoPortKey) {
                                classes.push('np-row--info-active');
                            }
                            return classes.join(' ');
                        }}
                        onRow={(record) => ({
                            onClick: (event) => {
                                const target = event?.target;
                                if (!(target instanceof Element)) return;
                                if (
                                    target.closest('a')
                                    || target.closest('button')
                                    || target.closest('input')
                                    || target.closest('.ant-checkbox-wrapper')
                                    || target.closest('.ant-dropdown')
                                    || target.closest('.pm-action-dots')
                                ) {
                                    return;
                                }
                                handleOpenPortInfo(record);
                            },
                        })}
                        loading={false}
                        pagination={npPagination}
                        rowSelection={{
                            selectedRowKeys,
                            onChange: (keys, rows) => {
                                setSelectedRowKeys(keys);
                                setSelectedRows(rows);
                            },
                            onSelect: handlePortShiftSelect,
                            selections: [
                                Table.SELECTION_ALL,
                                Table.SELECTION_INVERT,
                                Table.SELECTION_NONE,
                            ],
                        }}
                        onChange={(paginationInfo, filters, _sorter, extra) => {
                            if (paginationInfo) {
                                setNpCurrentPage(paginationInfo.current);
                                setNpPerPage(paginationInfo.pageSize);
                            }
                            if (
                                (extra?.action === 'filter' || extra?.action === 'sort') &&
                                Array.isArray(extra?.currentDataSource)
                            ) {
                                setNpFilteredTotal(extra.currentDataSource.length);
                            }
                            setTableFilters((prev) => {
                                const next = filters || {};
                                if (Array.isArray(prev.__quick_modem_status) && prev.__quick_modem_status.length > 0) {
                                    next.__quick_modem_status = prev.__quick_modem_status;
                                }
                                if (Array.isArray(prev.__quick_proxy_state) && prev.__quick_proxy_state.length > 0) {
                                    next.__quick_proxy_state = prev.__quick_proxy_state;
                                }
                                if (Array.isArray(prev.__imei_dup_check) && prev.__imei_dup_check.length > 0) {
                                    next.__imei_dup_check = prev.__imei_dup_check;
                                }
                                return next;
                            });
                        }}
                        scroll={{ x: 2128 }}
                    />
                    <CustomPagination
                        currentPage={npCurrentPage}
                        totalPages={Math.max(1, Math.ceil(npPaginationTotalItems / npPerPage))}
                        totalItems={npPaginationTotalItems}
                        perPage={npPerPage}
                        perPageOptions={npPerPageOptions}
                        onPageChange={(p) => setNpCurrentPage(p)}
                        onPerPageChange={(v) => { setNpPerPage(v); setNpCurrentPage(1); }}
                    />
                </Card>
            </div>

            {/* Панель статистики */}
            {npStatsVisible && (
                <ProxyStatsPanel
                    statusData={npStats}
                    onClose={() => setNpStatsVisible(false)}
                />
            )}

            {/* Панель фильтров */}
            {npFilterPanelVisible && (
                (typeof document !== 'undefined' && window.innerWidth <= 768)
                    ? createPortal(
                        <>
                            <div className="psp-backdrop" onClick={() => setNpFilterPanelVisible(false)} aria-hidden="true" />
                            <div className="psp">
                                <div className="psp__title-bar">
                                    <h2 className="psp__title-text">Фильтры</h2>
                                    <button type="button" className="psp__title-close" onClick={() => setNpFilterPanelVisible(false)} aria-label="Закрыть фильтры" tabIndex={0}>
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41L17.59 5Z" fill="#FFFFFF"/></svg>
                                    </button>
                                </div>
                                <div className="psp__body">
                                    {/* Dropdown: Сервер */}
                                    {(() => {
                                        const field = 'connection_name';
                                        const label = 'Сервер';
                                        const options = getUniqueServerNames();
                                        const selected = npLocalFilters[field] || [];
                                        const isOpen = npOpenDropdown === field;
                                        const searchVal = npDropdownSearch[field] || '';
                                        const displayText = selected.length === 0 ? label : selected.length === 1 ? selected[0] : `${label} (${selected.length})`;
                                        const filtered = searchVal ? options.filter(o => o.toLowerCase().includes(searchVal.toLowerCase())) : options;
                                        return (
                                            <div className="psp__dropdown-card" ref={(el) => { npDropdownRefs.current[field] = el; }}>
                                                <button type="button" className={`psp__select-trigger ${selected.length > 0 ? 'psp__select-trigger--active' : ''}`}
                                                    onClick={() => { const next = isOpen ? null : field; setNpOpenDropdown(next); if (next) { setNpDropdownSearch(prev => ({ ...prev, [field]: '' })); requestAnimationFrame(() => { npSearchInputRefs.current[field]?.focus(); }); } }}
                                                    aria-label={label} aria-expanded={isOpen} tabIndex={0}>
                                                    <span className="psp__select-text">{displayText}</span>
                                                    {selected.length > 0 && <span className="psp__select-clear" onClick={(e) => { e.stopPropagation(); npDropdownClear(field); }} role="button" tabIndex={0}>×</span>}
                                                    <span className={`psp__select-arrow ${isOpen ? 'psp__select-arrow--open' : ''}`}><svg width="12" height="8" viewBox="0 0 12 8" fill="none"><path d="M1.41 0.59L6 5.17L10.59 0.59L12 2L6 8L0 2L1.41 0.59Z" fill="#FFFFFF"/></svg></span>
                                                </button>
                                                {isOpen && (
                                                    <div className="psp__dropdown-list">
                                                        <div className="psp__dropdown-search-wrap">
                                                            <input ref={(el) => { npSearchInputRefs.current[field] = el; }} type="text" className="psp__dropdown-search" placeholder="Поиск…" value={searchVal}
                                                                onChange={(e) => setNpDropdownSearch(prev => ({ ...prev, [field]: e.target.value }))} onClick={(e) => e.stopPropagation()} />
                                                        </div>
                                                        {filtered.length === 0 ? <div className="psp__dropdown-empty">Ничего не найдено</div> : filtered.map(opt => {
                                                            const checked = selected.includes(opt);
                                                            return (
                                                                <label key={opt} className={`psp__dropdown-item ${checked ? 'psp__dropdown-item--checked' : ''}`}>
                                                                    <input type="checkbox" className="psp__dropdown-checkbox" checked={checked} onChange={() => npDropdownToggle(field, opt)} />
                                                                    <span className="psp__dropdown-checkmark">{checked && <svg width="10" height="8" viewBox="0 0 10 8" fill="none"><path d="M1 4L3.5 6.5L9 1" stroke="#5283F4" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>}</span>
                                                                    <span className="psp__dropdown-label">{opt}</span>
                                                                </label>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })()}

                                    {/* Dropdown: Модем */}
                                    {(() => {
                                        const field = 'name';
                                        const label = 'Модем';
                                        const options = getUniqueModemNamesWithNumber();
                                        const selected = npLocalFilters[field] || [];
                                        const isOpen = npOpenDropdown === field;
                                        const searchVal = npDropdownSearch[field] || '';
                                        const displayText = selected.length === 0 ? label : selected.length === 1 ? selected[0] : `${label} (${selected.length})`;
                                        const filtered = searchVal ? options.filter(o => o.toLowerCase().includes(searchVal.toLowerCase())) : options;
                                        return (
                                            <div className="psp__dropdown-card" ref={(el) => { npDropdownRefs.current[field] = el; }}>
                                                <button type="button" className={`psp__select-trigger ${selected.length > 0 ? 'psp__select-trigger--active' : ''}`}
                                                    onClick={() => { const next = isOpen ? null : field; setNpOpenDropdown(next); if (next) { setNpDropdownSearch(prev => ({ ...prev, [field]: '' })); requestAnimationFrame(() => { npSearchInputRefs.current[field]?.focus(); }); } }}
                                                    aria-label={label} aria-expanded={isOpen} tabIndex={0}>
                                                    <span className="psp__select-text">{displayText}</span>
                                                    {selected.length > 0 && <span className="psp__select-clear" onClick={(e) => { e.stopPropagation(); npDropdownClear(field); }} role="button" tabIndex={0}>×</span>}
                                                    <span className={`psp__select-arrow ${isOpen ? 'psp__select-arrow--open' : ''}`}><svg width="12" height="8" viewBox="0 0 12 8" fill="none"><path d="M1.41 0.59L6 5.17L10.59 0.59L12 2L6 8L0 2L1.41 0.59Z" fill="#FFFFFF"/></svg></span>
                                                </button>
                                                {isOpen && (
                                                    <div className="psp__dropdown-list">
                                                        <div className="psp__dropdown-search-wrap">
                                                            <input ref={(el) => { npSearchInputRefs.current[field] = el; }} type="text" className="psp__dropdown-search" placeholder="Поиск…" value={searchVal}
                                                                onChange={(e) => setNpDropdownSearch(prev => ({ ...prev, [field]: e.target.value }))} onClick={(e) => e.stopPropagation()} />
                                                        </div>
                                                        {filtered.length === 0 ? <div className="psp__dropdown-empty">Ничего не найдено</div> : filtered.map(opt => {
                                                            const checked = selected.includes(opt);
                                                            return (
                                                                <label key={opt} className={`psp__dropdown-item ${checked ? 'psp__dropdown-item--checked' : ''}`}>
                                                                    <input type="checkbox" className="psp__dropdown-checkbox" checked={checked} onChange={() => npDropdownToggle(field, opt)} />
                                                                    <span className="psp__dropdown-checkmark">{checked && <svg width="10" height="8" viewBox="0 0 10 8" fill="none"><path d="M1 4L3.5 6.5L9 1" stroke="#5283F4" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>}</span>
                                                                    <span className="psp__dropdown-label">{opt}</span>
                                                                </label>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })()}

                                    {/* Chips: Статус */}
                                    <div className="psp__section">
                                        <h3 className="psp__section-title">Статус</h3>
                                        <div className="psp__chips">
                                            {npFilterStatusStats.map(item => {
                                                const fv = item.filterValue || item.value;
                                                const active = (npLocalFilters.active || []).includes(fv);
                                                return (
                                                    <button key={fv} type="button" className={`psp__chip ${active ? 'psp__chip--active' : ''}`}
                                                        onClick={() => npToggleFilterChip('active', fv)} tabIndex={0} aria-label={`${item.value} ${item.count}`} aria-pressed={active}>
                                                        <span className="psp__chip-label">{item.value}</span>
                                                        <span className={`psp__chip-count psp__chip-count--${item.color}`}>{item.count}</span>
                                                        <span className="psp__chip-close"><svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41L17.59 5Z" fill="#FFFFFF"/></svg></span>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    {/* Chips: ГЕО */}
                                    <div className="psp__section">
                                        <h3 className="psp__section-title">ГЕО</h3>
                                        <div className="psp__chips">
                                            {npFilterCityStats.map(item => {
                                                const active = (npLocalFilters.city || []).includes(item.value);
                                                return (
                                                    <button key={item.value} type="button" className={`psp__chip ${active ? 'psp__chip--active' : ''}`}
                                                        onClick={() => npToggleFilterChip('city', item.value)} tabIndex={0} aria-label={`${item.value} ${item.count}`} aria-pressed={active}>
                                                        <span className="psp__chip-label">{item.value}</span>
                                                        <span className="psp__chip-count psp__chip-count--gray">{item.count}</span>
                                                        <span className="psp__chip-close"><svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41L17.59 5Z" fill="#FFFFFF"/></svg></span>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    {/* Chips: Оператор */}
                                    <div className="psp__section">
                                        <h3 className="psp__section-title">Оператор</h3>
                                        <div className="psp__chips">
                                            {npFilterOperatorStats.map(item => {
                                                const active = (npLocalFilters.operator || []).includes(item.value);
                                                return (
                                                    <button key={item.value} type="button" className={`psp__chip ${active ? 'psp__chip--active' : ''}`}
                                                        onClick={() => npToggleFilterChip('operator', item.value)} tabIndex={0} aria-label={`${item.value} ${item.count}`} aria-pressed={active}>
                                                        <span className="psp__chip-label">{item.value}</span>
                                                        <span className="psp__chip-count psp__chip-count--gray">{item.count}</span>
                                                        <span className="psp__chip-close"><svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41L17.59 5Z" fill="#FFFFFF"/></svg></span>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    {/* Chips: Ротация */}
                                    <div className="psp__section">
                                        <h3 className="psp__section-title">Ротация</h3>
                                        <div className="psp__chips">
                                            {npFilterRotationStats.map(item => {
                                                const active = (npLocalFilters.reconnect || []).includes(item.value);
                                                return (
                                                    <button key={item.value} type="button" className={`psp__chip ${active ? 'psp__chip--active' : ''}`}
                                                        onClick={() => npToggleFilterChip('reconnect', item.value)} tabIndex={0} aria-label={`${item.label} ${item.count}`} aria-pressed={active}>
                                                        <span className="psp__chip-label">{item.label}</span>
                                                        <span className="psp__chip-count psp__chip-count--gray">{item.count}</span>
                                                        <span className="psp__chip-close"><svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41L17.59 5Z" fill="#FFFFFF"/></svg></span>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    {/* Chips: TCP отпечаток */}
                                    <div className="psp__section">
                                        <h3 className="psp__section-title">TCP отпечаток</h3>
                                        <div className="psp__chips">
                                            {npFilterOsfpStats.map(item => {
                                                const active = (npLocalFilters.os_fingerprint || []).includes(item.value);
                                                return (
                                                    <button key={String(item.value)} type="button" className={`psp__chip ${active ? 'psp__chip--active' : ''}`}
                                                        onClick={() => npToggleFilterChip('os_fingerprint', item.value)} tabIndex={0} aria-label={`${item.label} ${item.count}`} aria-pressed={active}>
                                                        <span className="psp__chip-label">{item.label}</span>
                                                        <span className="psp__chip-count psp__chip-count--gray">{item.count}</span>
                                                        <span className="psp__chip-close"><svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41L17.59 5Z" fill="#FFFFFF"/></svg></span>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                                <div className="psp__footer">
                                    <button type="button" className="psp__footer-btn psp__footer-btn--reset" onClick={handleNpFilterReset} tabIndex={0}>Сбросить</button>
                                    <button type="button" className="psp__footer-btn psp__footer-btn--apply" onClick={handleNpFilterApply} tabIndex={0}>Применить</button>
                                </div>
                            </div>
                        </>,
                        document.body
                    )
                    : (
                        <>
                            <div className="psp-backdrop" onClick={() => setNpFilterPanelVisible(false)} aria-hidden="true" />
                            <div className="psp">
                        <div className="psp__title-bar">
                            <h2 className="psp__title-text">Фильтры</h2>
                            <button type="button" className="psp__title-close" onClick={() => setNpFilterPanelVisible(false)} aria-label="Закрыть фильтры" tabIndex={0}>
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41L17.59 5Z" fill="#FFFFFF"/></svg>
                            </button>
                        </div>
                        <div className="psp__body">
                            {/* Dropdown: Сервер */}
                            {(() => {
                                const field = 'connection_name';
                                const label = 'Сервер';
                                const options = getUniqueServerNames();
                                const selected = npLocalFilters[field] || [];
                                const isOpen = npOpenDropdown === field;
                                const searchVal = npDropdownSearch[field] || '';
                                const displayText = selected.length === 0 ? label : selected.length === 1 ? selected[0] : `${label} (${selected.length})`;
                                const filtered = searchVal ? options.filter(o => o.toLowerCase().includes(searchVal.toLowerCase())) : options;
                                return (
                                    <div className="psp__dropdown-card" ref={(el) => { npDropdownRefs.current[field] = el; }}>
                                        <button type="button" className={`psp__select-trigger ${selected.length > 0 ? 'psp__select-trigger--active' : ''}`}
                                            onClick={() => { const next = isOpen ? null : field; setNpOpenDropdown(next); if (next) { setNpDropdownSearch(prev => ({ ...prev, [field]: '' })); requestAnimationFrame(() => { npSearchInputRefs.current[field]?.focus(); }); } }}
                                            aria-label={label} aria-expanded={isOpen} tabIndex={0}>
                                            <span className="psp__select-text">{displayText}</span>
                                            {selected.length > 0 && <span className="psp__select-clear" onClick={(e) => { e.stopPropagation(); npDropdownClear(field); }} role="button" tabIndex={0}>×</span>}
                                            <span className={`psp__select-arrow ${isOpen ? 'psp__select-arrow--open' : ''}`}><svg width="12" height="8" viewBox="0 0 12 8" fill="none"><path d="M1.41 0.59L6 5.17L10.59 0.59L12 2L6 8L0 2L1.41 0.59Z" fill="#FFFFFF"/></svg></span>
                                        </button>
                                        {isOpen && (
                                            <div className="psp__dropdown-list">
                                                <div className="psp__dropdown-search-wrap">
                                                    <input ref={(el) => { npSearchInputRefs.current[field] = el; }} type="text" className="psp__dropdown-search" placeholder="Поиск…" value={searchVal}
                                                        onChange={(e) => setNpDropdownSearch(prev => ({ ...prev, [field]: e.target.value }))} onClick={(e) => e.stopPropagation()} />
                                                </div>
                                                {filtered.length === 0 ? <div className="psp__dropdown-empty">Ничего не найдено</div> : filtered.map(opt => {
                                                    const checked = selected.includes(opt);
                                                    return (
                                                        <label key={opt} className={`psp__dropdown-item ${checked ? 'psp__dropdown-item--checked' : ''}`}>
                                                            <input type="checkbox" className="psp__dropdown-checkbox" checked={checked} onChange={() => npDropdownToggle(field, opt)} />
                                                            <span className="psp__dropdown-checkmark">{checked && <svg width="10" height="8" viewBox="0 0 10 8" fill="none"><path d="M1 4L3.5 6.5L9 1" stroke="#5283F4" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>}</span>
                                                            <span className="psp__dropdown-label">{opt}</span>
                                                        </label>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                );
                            })()}

                            {/* Dropdown: Модем */}
                            {(() => {
                                const field = 'name';
                                const label = 'Модем';
                                const options = getUniqueModemNamesWithNumber();
                                const selected = npLocalFilters[field] || [];
                                const isOpen = npOpenDropdown === field;
                                const searchVal = npDropdownSearch[field] || '';
                                const displayText = selected.length === 0 ? label : selected.length === 1 ? selected[0] : `${label} (${selected.length})`;
                                const filtered = searchVal ? options.filter(o => o.toLowerCase().includes(searchVal.toLowerCase())) : options;
                                return (
                                    <div className="psp__dropdown-card" ref={(el) => { npDropdownRefs.current[field] = el; }}>
                                        <button type="button" className={`psp__select-trigger ${selected.length > 0 ? 'psp__select-trigger--active' : ''}`}
                                            onClick={() => { const next = isOpen ? null : field; setNpOpenDropdown(next); if (next) { setNpDropdownSearch(prev => ({ ...prev, [field]: '' })); requestAnimationFrame(() => { npSearchInputRefs.current[field]?.focus(); }); } }}
                                            aria-label={label} aria-expanded={isOpen} tabIndex={0}>
                                            <span className="psp__select-text">{displayText}</span>
                                            {selected.length > 0 && <span className="psp__select-clear" onClick={(e) => { e.stopPropagation(); npDropdownClear(field); }} role="button" tabIndex={0}>×</span>}
                                            <span className={`psp__select-arrow ${isOpen ? 'psp__select-arrow--open' : ''}`}><svg width="12" height="8" viewBox="0 0 12 8" fill="none"><path d="M1.41 0.59L6 5.17L10.59 0.59L12 2L6 8L0 2L1.41 0.59Z" fill="#FFFFFF"/></svg></span>
                                        </button>
                                        {isOpen && (
                                            <div className="psp__dropdown-list">
                                                <div className="psp__dropdown-search-wrap">
                                                    <input ref={(el) => { npSearchInputRefs.current[field] = el; }} type="text" className="psp__dropdown-search" placeholder="Поиск…" value={searchVal}
                                                        onChange={(e) => setNpDropdownSearch(prev => ({ ...prev, [field]: e.target.value }))} onClick={(e) => e.stopPropagation()} />
                                                </div>
                                                {filtered.length === 0 ? <div className="psp__dropdown-empty">Ничего не найдено</div> : filtered.map(opt => {
                                                    const checked = selected.includes(opt);
                                                    return (
                                                        <label key={opt} className={`psp__dropdown-item ${checked ? 'psp__dropdown-item--checked' : ''}`}>
                                                            <input type="checkbox" className="psp__dropdown-checkbox" checked={checked} onChange={() => npDropdownToggle(field, opt)} />
                                                            <span className="psp__dropdown-checkmark">{checked && <svg width="10" height="8" viewBox="0 0 10 8" fill="none"><path d="M1 4L3.5 6.5L9 1" stroke="#5283F4" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>}</span>
                                                            <span className="psp__dropdown-label">{opt}</span>
                                                        </label>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                );
                            })()}

                            {/* Chips: Статус */}
                            <div className="psp__section">
                                <h3 className="psp__section-title">Статус</h3>
                                <div className="psp__chips">
                                    {npFilterStatusStats.map(item => {
                                        const fv = item.filterValue || item.value;
                                        const active = (npLocalFilters.active || []).includes(fv);
                                        return (
                                            <button key={fv} type="button" className={`psp__chip ${active ? 'psp__chip--active' : ''}`}
                                                onClick={() => npToggleFilterChip('active', fv)} tabIndex={0} aria-label={`${item.value} ${item.count}`} aria-pressed={active}>
                                                <span className="psp__chip-label">{item.value}</span>
                                                <span className={`psp__chip-count psp__chip-count--${item.color}`}>{item.count}</span>
                                                <span className="psp__chip-close"><svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41L17.59 5Z" fill="#FFFFFF"/></svg></span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Chips: ГЕО */}
                            <div className="psp__section">
                                <h3 className="psp__section-title">ГЕО</h3>
                                <div className="psp__chips">
                                    {npFilterCityStats.map(item => {
                                        const active = (npLocalFilters.city || []).includes(item.value);
                                        return (
                                            <button key={item.value} type="button" className={`psp__chip ${active ? 'psp__chip--active' : ''}`}
                                                onClick={() => npToggleFilterChip('city', item.value)} tabIndex={0} aria-label={`${item.value} ${item.count}`} aria-pressed={active}>
                                                <span className="psp__chip-label">{item.value}</span>
                                                <span className="psp__chip-count psp__chip-count--gray">{item.count}</span>
                                                <span className="psp__chip-close"><svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41L17.59 5Z" fill="#FFFFFF"/></svg></span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Chips: Оператор */}
                            <div className="psp__section">
                                <h3 className="psp__section-title">Оператор</h3>
                                <div className="psp__chips">
                                    {npFilterOperatorStats.map(item => {
                                        const active = (npLocalFilters.operator || []).includes(item.value);
                                        return (
                                            <button key={item.value} type="button" className={`psp__chip ${active ? 'psp__chip--active' : ''}`}
                                                onClick={() => npToggleFilterChip('operator', item.value)} tabIndex={0} aria-label={`${item.value} ${item.count}`} aria-pressed={active}>
                                                <span className="psp__chip-label">{item.value}</span>
                                                <span className="psp__chip-count psp__chip-count--gray">{item.count}</span>
                                                <span className="psp__chip-close"><svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41L17.59 5Z" fill="#FFFFFF"/></svg></span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Chips: Ротация */}
                            <div className="psp__section">
                                <h3 className="psp__section-title">Ротация</h3>
                                <div className="psp__chips">
                                    {npFilterRotationStats.map(item => {
                                        const active = (npLocalFilters.reconnect || []).includes(item.value);
                                        return (
                                            <button key={item.value} type="button" className={`psp__chip ${active ? 'psp__chip--active' : ''}`}
                                                onClick={() => npToggleFilterChip('reconnect', item.value)} tabIndex={0} aria-label={`${item.label} ${item.count}`} aria-pressed={active}>
                                                <span className="psp__chip-label">{item.label}</span>
                                                <span className="psp__chip-count psp__chip-count--gray">{item.count}</span>
                                                <span className="psp__chip-close"><svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41L17.59 5Z" fill="#FFFFFF"/></svg></span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Chips: TCP отпечаток */}
                            <div className="psp__section">
                                <h3 className="psp__section-title">TCP отпечаток</h3>
                                <div className="psp__chips">
                                    {npFilterOsfpStats.map(item => {
                                        const active = (npLocalFilters.os_fingerprint || []).includes(item.value);
                                        return (
                                            <button key={String(item.value)} type="button" className={`psp__chip ${active ? 'psp__chip--active' : ''}`}
                                                onClick={() => npToggleFilterChip('os_fingerprint', item.value)} tabIndex={0} aria-label={`${item.label} ${item.count}`} aria-pressed={active}>
                                                <span className="psp__chip-label">{item.label}</span>
                                                <span className="psp__chip-count psp__chip-count--gray">{item.count}</span>
                                                <span className="psp__chip-close"><svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41L17.59 5Z" fill="#FFFFFF"/></svg></span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>

                        <div className="psp__footer">
                            <button type="button" className="psp__footer-btn psp__footer-btn--reset" onClick={handleNpFilterReset} tabIndex={0}>Сбросить</button>
                            <button type="button" className="psp__footer-btn psp__footer-btn--apply" onClick={handleNpFilterApply} tabIndex={0}>Применить</button>
                        </div>
                    </div>
                        </>
                    )
            )}
            {infoPort ? (
                <>
                    <div
                        className="pxt-info-backdrop"
                        onClick={handleClosePortInfo}
                        role="presentation"
                        aria-hidden="true"
                    />
                    <PortInfoPanel
                        port={infoPort}
                        onClose={handleClosePortInfo}
                        onEdit={handleEditFromInfoPanel}
                        resolveOperator={(raw) => resolveOperatorByTemplates(raw, operatorTemplates)}
                    />
                </>
            ) : null}
            </div>

            {/* Панель создания порта (slide-in) */}
            <PortCreatePanel
                visible={createModalVisible}
                onClose={() => setCreateModalVisible(false)}
                onSubmit={handlePortCreateSubmit}
                submitting={createSubmitting}
                krakenConnections={krakenConnections}
                loadingConnections={loadingConnections}
                networkInterfaces={networkInterfaces}
                loadingInterfaces={loadingInterfaces}
                modemTypes={modemTypes}
                ports={safePorts}
                onConnectionChange={(connectionId) => {
                    fetchNetworkInterfaces(connectionId);
                    fetchTcpFingerprintsForConnection(connectionId);
                }}
                tcpFingerprints={tcpFingerprintsForCreate}
                loadingTcpFingerprints={loadingTcpFingerprintsForCreate}
            />

            {/* Панель редактирования порта (slide-in) */}
            <PortEditPanel
                visible={editModalVisible}
                onClose={() => {
                    setEditModalVisible(false);
                    setSelectedPort(null);
                    setInitialFormValues(null);
                }}
                onSubmit={handlePortEditSubmit}
                onBandApplied={handleBandPolicyApplied}
                submitting={editSubmitting}
                initialData={selectedPort}
                krakenConnections={krakenConnections}
                modemTypes={modemTypes}
                tcpFingerprints={tcpFingerprints}
                loadingTcpFingerprints={loadingTcpFingerprints}
                serverUsers={serverUsers}
            />

            {/* Модальное окно с подробной информацией */}
            <Modal
                className="light-modal"
                title={`Подробная информация: ${selectedPort?.name || ''}`}
                open={detailsModalVisible}
                onCancel={() => {
                    setDetailsModalVisible(false);
                    setSelectedPort(null);
                }}
                footer={[
                    <Button key="close" onClick={() => setDetailsModalVisible(false)}>
                        Закрыть
                    </Button>
                ]}
                width={600}
            >
                {selectedPort && (
                    <Descriptions column={1} bordered size="small">
                        <Descriptions.Item label="ID">{selectedPort.id}</Descriptions.Item>
                        <Descriptions.Item label="Название">{selectedPort.name}</Descriptions.Item>
                        <Descriptions.Item label="Интерфейс">{selectedPort.ifname}</Descriptions.Item>
                        <Descriptions.Item label="IP адрес">
                            <Tag color={selectedPort.ip_address ? 'green' : 'default'}>
                                {selectedPort.ip_address || 'Не определен'}
                            </Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label="Тип модема">
                            <Tag color="blue">{getModemTypeName(selectedPort.type)}</Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label="Статус">
                            <Tag 
                                color={getStatusColor(selectedPort.active)} 
                                icon={selectedPort.active ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                            >
                                {selectedPort.active ? 'Активен' : 'Неактивен'}
                            </Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label="Режим реконекта">
                            <Tag color={getReconnectTypeColor(selectedPort.reconnect_type)} icon={<ReloadOutlined />}>
                                {getReconnectTypeLabel(selectedPort.reconnect_type)}
                            </Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label="Скорость (емкость)">
                            <Tag color="cyan">{selectedPort.capacity_speed_mbps || 20} Мб/с</Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label="Максимальные TCP соединения">
                            <Tag color="blue">{selectedPort.max_tcp_connections || 1000}</Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label="Буфер безопасности">
                            <Tag color="orange">{((selectedPort.safety_buffer || 1.0) * 100).toFixed(0)}%</Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label="Интервал мониторинга">{selectedPort.monitoring_interval} сек</Descriptions.Item>
                        <Descriptions.Item label="MTU">
                            <Tag color={selectedPort.mtu === 0 ? 'default' : 'green'}>
                                {selectedPort.mtu === 0 ? 'Auto' : selectedPort.mtu}
                            </Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label="Тип переподключения">
                            <Tag color={getReconnectTypeColor(selectedPort.reconnect_type)}>
                                {getReconnectTypeLabel(selectedPort.reconnect_type)}
                            </Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label="Интервал переподключения">{selectedPort.reconnect_interval} сек</Descriptions.Item>
                        <Descriptions.Item label="Минимальный шаг">{selectedPort.reconnect_min} сек</Descriptions.Item>
                        <Descriptions.Item label="OSFP">
                            <Tag color={selectedPort.is_osfp ? 'green' : 'default'}>
                                {selectedPort.is_osfp ? 'Включен' : 'Отключен'}
                            </Tag>
                            {selectedPort.is_osfp && selectedPort.osfp && (
                                <div>ID отпечатка: {typeof selectedPort.osfp === 'object' ? (selectedPort.osfp.id || selectedPort.osfp.name) : selectedPort.osfp}</div>
                            )}
                        </Descriptions.Item>
                        <Descriptions.Item label="Пользователи">
                            {selectedPort.users && Array.isArray(selectedPort.users) && selectedPort.users.length > 0 
                                ? selectedPort.users.map(user => 
                                    typeof user === 'object' ? (user.name || user.label || user.id) : user
                                ).join(', ')
                                : 'Не назначены'
                            }
                        </Descriptions.Item>
                    </Descriptions>
                )}
            </Modal>

            {/* Модальное окно массового редактирования */}
            <PortBulkEditPanel
                visible={bulkEditModalVisible && !loading}
                onClose={() => {
                    if (!loading) setBulkEditModalVisible(false);
                }}
                onSubmit={handleBulkUpdatePorts}
                onBandApplied={handleBandPolicyApplied}
                submitting={loading}
                selectedCount={selectedRows.length}
                selectedPorts={selectedRows}
                modemTypes={modemTypes}
                tcpFingerprints={tcpFingerprints}
                loadingTcpFingerprints={loadingTcpFingerprints}
            />

            {/* НОВОЕ: Модальное окно детальной статистики синхронизации */}
            <SyncStatsModal
                visible={syncStatsVisible}
                onClose={() => setSyncStatsVisible(false)}
                stats={syncStats}
                title="Статистика синхронизации модемов"
            />
            <SyncStatsModal
                visible={bulkUpdateStatsVisible}
                onClose={() => {
                    setBulkUpdateStatsVisible(false);
                    setBulkUpdateStats(null);
                }}
                stats={bulkUpdateStats}
                title="Результаты массового обновления модемов"
                zIndex={1100}
            />
        </div>
    );
};

export default NetworkPorts;
